import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getApiBase } from './config/apiBase';
import { authService } from './services/authService';
import { parseManualProbe } from './endpointProbe';
import useDictation from './hooks/useDictation';
import JiraIssuePicker from './components/JiraIssuePicker';
import ModelSelector from './ModelSelector';
import MockupBuilder from './MockupBuilder';
import LyricConfirm from './components/LyricConfirm';
import AppViewerOverlay from './components/AppViewerOverlay';

const REPO_STEPS  = ['choose', 'repo', 'validation', 'pickApp', 'stack', 'requirements', 'config', 'review', 'deployed'];
const BUILD_STEPS = ['choose', 'describe', 'architecture', 'services', 'build-review', 'github-token', 'building'];
const EDIT_STEPS  = ['choose', 'repo', 'validation', 'enhance', 'enhancing'];

// Visual Edit Studio "Add element" palette, grouped by category. Each entry is
// [elementType, label] where elementType must match a key in makeEl() in the
// server-side ?__lyricEdit=1 overlay (server/routes/deploy.js).
const VISUAL_ADD_GROUPS = [
  ['Text', [
    ['heading', '🔠 Heading'], ['subheading', '🔡 Subheading'], ['paragraph', '¶ Paragraph'],
    ['text', '𝐓 Text / span'], ['blockquote', '❝ Quote'], ['list', '• Bulleted list'],
    ['orderedList', '1. Numbered list'], ['code', '</> Code block'],
  ]],
  ['Form', [
    ['button', '🔘 Button'], ['link', '🔗 Link'], ['input', '⌨️ Text input'],
    ['textarea', '📝 Textarea'], ['select', '▼ Dropdown'], ['checkbox', '☑️ Checkbox'],
    ['radio', '🔵 Radio'], ['slider', '🎚 Slider'], ['toggle', '🔀 Toggle'],
    ['number', '#️⃣ Number'], ['date', '📅 Date'], ['time', '⏰ Time'],
    ['email', '✉️ Email'], ['password', '🔑 Password'], ['search', '🔎 Search'],
    ['color', '🎨 Color picker'], ['file', '📎 File upload'], ['label', '🏷 Label'],
    ['fieldset', '🗃 Fieldset'], ['form', '🧾 Form'],
  ]],
  ['Media', [
    ['image', '🖼 Image'], ['video', '🎬 Video'], ['audio', '🔊 Audio'],
    ['iframe', '🪟 Embed / iframe'], ['icon', '⭐ Icon'],
  ]],
  ['Layout', [
    ['div', '▭ Container'], ['section', '🗂 Section'], ['card', '🃏 Card'],
    ['row', '↔️ Row (flex)'], ['grid', '▦ Grid'], ['nav', '🧭 Nav bar'],
    ['header', '⬆️ Header'], ['footer', '⬇️ Footer'], ['details', '▸ Accordion'],
  ]],
  ['Shapes', [
    ['divider', '— Divider'], ['line', '│ Line'], ['box', '⬛ Box'],
    ['circle', '⬤ Circle'], ['spacer', '␣ Spacer'],
  ]],
  ['Components', [
    ['table', '▦ Table'], ['progress', '▰ Progress bar'], ['meter', '📊 Meter'],
    ['badge', '🏷 Badge'], ['alert', '⚠️ Alert'],
  ]],
];


const CO_ADMIN_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Keys whose values are likely secrets. Saved drafts are persisted server-side
// (file backend JSON or S3) and reloaded later, so any secret the user typed
// into a connected-service config (API keys, DB connection strings, tokens…)
// must be redacted before it leaves the browser.
const SECRET_KEY_RE = /(secret|token|password|passwd|pwd|api[-_]?key|access[-_]?key|private[-_]?key|connection[-_]?string|conn[-_]?str|dsn|credential)/i;

// Return a copy of serviceConfig with secret-looking values blanked out. Keeps
// non-secret fields (e.g. service name, baseUrl) so a resumed draft still shows
// which services were configured; the user re-enters secrets on resume.
function redactServiceConfig(serviceConfig) {
  if (!serviceConfig || typeof serviceConfig !== 'object') return serviceConfig;
  const out = {};
  for (const [svc, cfg] of Object.entries(serviceConfig)) {
    if (cfg && typeof cfg === 'object' && !Array.isArray(cfg)) {
      const redacted = {};
      for (const [k, v] of Object.entries(cfg)) {
        redacted[k] = (typeof v === 'string' && v && SECRET_KEY_RE.test(k)) ? '' : v;
      }
      out[svc] = redacted;
    } else {
      out[svc] = cfg;
    }
  }
  return out;
}

// Playful, phase-aware status lines shown while an app is being built. These
// rotate every few seconds so the wait feels alive and the user always has a
// sense of what Lyric is actually doing under the hood.
const BUILD_FUN_MESSAGES = {
  repo: [
    '📦 Reserving your corner of GitHub…',
    '🧱 Laying the first bricks of your repository…',
    '🗺️ Staking out a home for your code…',
  ],
  scaffold: [
    '🧠 Lyric is thinking through your app…',
    '✍️ Writing your very first lines of code…',
    '📐 Sketching out the architecture…',
    '🧩 Snapping components into place…',
    '🤖 Teaching your app how to app…',
    '☕ This is the brainy part — great moment for a sip of coffee…',
    '🪄 Turning your description into working code…',
  ],
  push: [
    '📤 Committing the freshly-written files…',
    '🚚 Shipping your code up to GitHub…',
    '🗂️ Filing everything neatly into your repo…',
  ],
  deploy: [
    '📦 Wrangling npm dependencies…',
    '🐳 Coaxing the container to cooperate…',
    '🔧 Tightening the last few bolts…',
    '🧪 Compiling and building your app…',
    '🛠️ Assembling the production build…',
    '🧵 Weaving everything together…',
  ],
  starting: [
    '⚡ Flipping the power switch…',
    '🌅 Waking your app up…',
    '🚀 Almost there — spinning up the server…',
  ],
  waiting: [
    '🔑 Your code is ready — add any credentials below, then hit Deploy.',
  ],
};

// Rough per-phase timing model used to animate the progress bar and set
// expectations. `range` is the % window the bar occupies for that phase; `est`
// is the typical seconds that phase takes (the bar creeps toward the ceiling
// over that window, then holds until the phase actually advances).
const BUILD_PHASE_META = {
  repo:     { range: [2, 10],   est: 6,  label: 'Setting up your repository' },
  scaffold: { range: [10, 45],  est: 35, label: 'Writing your app with AI' },
  push:     { range: [45, 55],  est: 8,  label: 'Pushing code to GitHub' },
  waiting:  { range: [55, 55],  est: 1,  label: 'Waiting on your credentials' },
  deploy:   { range: [55, 92],  est: 60, label: 'Installing & building' },
  starting: { range: [92, 98],  est: 12, label: 'Starting your app' },
  done:     { range: [100, 100], est: 1, label: 'Live' },
};

// Playful rotating status lines for the edit/enhance flow (one long AI phase).
// Kept deterministic (indexed by elapsed time) so they don't flicker between
// renders — just a long, varied loop so the wait stays entertaining.
const ENHANCE_FUN_MESSAGES = [
  '🔍 Reading through your codebase…',
  '🧠 Lyric is understanding how your app works…',
  '🕵️ Following the trail of your imports…',
  '✍️ Drafting the changes you asked for…',
  '🧩 Wiring the new logic into place…',
  '🩹 Patching files carefully (no code was harmed)…',
  '🧐 Arguing with itself about the cleanest approach…',
  '🧹 Sweeping up stray semicolons…',
  '🎯 Making sure we touch ONLY what you asked for…',
  '🧶 Untangling a particularly stubborn function…',
  '📎 Aligning the braces so your linter stays calm…',
  '🤖 Teaching the new code some manners…',
  '🛟 Keeping your existing features safe and sound…',
  '🔧 Tightening a few bolts in the logic…',
  '📐 Measuring twice so we cut once…',
  '🧪 Double-checking the edits make sense…',
  '🪄 Sprinkling a little polish on the diff…',
  '☕ The thoughtful part — great moment for a quick coffee…',
  '🧾 Writing a commit message you\'ll actually read…',
  '📤 Getting your changes ready to push…',
];

// Self-aware lines that only appear once the wait runs long, so a chunky edit
// feels like a bit of comedy instead of a hang.
const ENHANCE_LONG_WAIT_MESSAGES = [
  '🐢 Okay, this one\'s a chunky change — still on it…',
  '🫡 Taking a little longer than usual, but we don\'t ship sloppy…',
  '🧘 Deep breaths — good code can\'t be rushed…',
  '🍕 Long enough to order a pizza? Maybe. Still grinding…',
  '🏗️ Rome wasn\'t refactored in a day, but we\'re close…',
  '📚 Reading the docs so you don\'t have to…',
  '🧦 Pairing up every opening tag with its closing tag…',
];

// Pick the status line for the current elapsed time: mostly the fun loop, but
// once we pass a minute we drop in a self-aware long-wait quip every ~9s.
function enhanceStatusLine(elapsedSec) {
  const step = Math.floor((elapsedSec || 0) / 3);
  if ((elapsedSec || 0) >= 60 && step % 3 === 0) {
    return ENHANCE_LONG_WAIT_MESSAGES[Math.floor(step / 3) % ENHANCE_LONG_WAIT_MESSAGES.length];
  }
  return ENHANCE_FUN_MESSAGES[step % ENHANCE_FUN_MESSAGES.length];
}

// Preview the version a deploy will auto-assign when the engineer leaves the
// version override blank: a brand-new app starts at 1.0.0, otherwise the minor
// component bumps and patch resets (1.0.0 → 1.1.0, 2.3.4 → 2.4.0). This is a
// best-effort UI hint only; the server is authoritative and its bumpMinorVersion
// falls back to 1.1.0 for invalid/empty input, whereas this preview shows 1.0.0
// for a never-versioned app (matching the server's 'initial' path).
function nextAutoVersion(current) {
  const raw = String(current || '').trim().replace(/^v/i, '');
  if (!/^\d{1,6}(\.\d{1,6}){0,2}$/.test(raw)) return '1.0.0';
  const [maj = '0', min = '0'] = raw.split('.');
  return `${parseInt(maj, 10)}.${parseInt(min, 10) + 1}.0`;
}

// Reusable editor for the per-app co-admins list (email chips). Co-admins get
// full owner-level management parity over the deployed app.
// `bare` drops the outer card + title so the editor can be embedded inside a
// CollapsibleSection (which supplies its own card chrome and header title).
// Generic email-chip list editor: an add-by-email input plus removable chips.
// Shared by CoAdminsEditor and AllowedUsersEditor so the two look/behave the
// same; only the copy and accent color differ. `bare` drops the heading + card
// chrome for embedding inside a CollapsibleSection.
function EmailChipListEditor({
  emails,
  onChange,
  bare = false,
  label,
  description,
  placeholder = 'name@example.com',
  accent = '#ce93d8',
  chipBg = '#2a1a3e',
}) {
  const [draft, setDraft] = useState('');
  const list = Array.isArray(emails) ? emails : [];
  const draftTrimmed = draft.trim().toLowerCase();
  const draftInvalid = draftTrimmed.length > 0 && !CO_ADMIN_EMAIL_RE.test(draftTrimmed);

  const addEmail = () => {
    if (!draftTrimmed || draftInvalid) return;
    if (!list.some(e => e.toLowerCase() === draftTrimmed)) onChange([...list, draftTrimmed]);
    setDraft('');
  };
  const removeEmail = (e) => onChange(list.filter(x => x !== e));

  const inner = (
    <>
      {!bare && label && <div style={{ color: '#e0e0e0', fontSize: 14, fontWeight: 600 }}>{label}</div>}
      {description && (
        <div style={{ color: '#888', fontSize: 12, margin: bare ? '0 0 10px' : '4px 0 10px' }}>
          {description}
        </div>
      )}
      {list.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {list.map(email => (
            <span key={email} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
              borderRadius: 16, fontSize: 13, backgroundColor: chipBg, border: `1px solid ${accent}`, color: accent,
            }}>
              {email}
              <button
                type="button"
                onClick={() => removeEmail(email)}
                aria-label={`Remove ${email}`}
                style={{ background: 'none', border: 'none', color: accent, cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 0 }}
              >×</button>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addEmail(); } }}
          onBlur={addEmail}
          placeholder={placeholder}
          style={{
            flex: 1, padding: '8px 12px', borderRadius: 6, fontSize: 13,
            backgroundColor: '#222', color: '#e0e0e0',
            border: `1px solid ${draftInvalid ? '#ef5350' : '#444'}`,
          }}
        />
        <button
          type="button"
          onClick={addEmail}
          disabled={!draftTrimmed || draftInvalid}
          style={{
            padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: draftTrimmed && !draftInvalid ? 'pointer' : 'not-allowed',
            border: 'none', backgroundColor: draftTrimmed && !draftInvalid ? accent : '#555', color: '#111',
          }}
        >Add</button>
      </div>
      {draftInvalid && <p style={{ color: '#ef5350', fontSize: 11, margin: '8px 0 0' }}>Enter a valid email address.</p>}
    </>
  );

  if (bare) return inner;
  return (
    <div style={{ marginTop: 16, padding: '16px', backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: 8 }}>
      {inner}
    </div>
  );
}

// Co-admins: additional users granted FULL owner-level management parity.
function CoAdminsEditor({ emails, onChange, bare = false }) {
  return (
    <EmailChipListEditor
      emails={emails}
      onChange={onChange}
      bare={bare}
      label="👥 Co-admins (optional)"
      description="Add other users by email to co-manage this app — they get the same admin controls (Edit, Stop, Redeploy, Delete, API keys) as if they created it."
      placeholder="steve@example.com"
      accent="#ce93d8"
      chipBg="#2a1a3e"
    />
  );
}

// Allowed users: named people who may OPEN a private app — view/use only, no
// admin controls. Distinct blue accent so they're visually separate from the
// purple co-admins list.
function AllowedUsersEditor({ emails, onChange, bare = false }) {
  return (
    <EmailChipListEditor
      emails={emails}
      onChange={onChange}
      bare={bare}
      label="👤 Allowed users (optional)"
      description="Add people by email who can open this app while it's private. They can view and use it, but get no admin controls."
      placeholder="teammate@example.com"
      accent="#64b5f6"
      chipBg="#12283e"
    />
  );
}

// Card-styled section with a clickable header that expands/collapses its body.
// When collapsed, an optional `summary` node is shown on the right so the
// current state stays visible at a glance.
function CollapsibleSection({ title, summary, defaultOpen = false, borderColor = '#333', children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginTop: 16, backgroundColor: '#1a1a2e', border: `1px solid ${borderColor}`, borderRadius: 8, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 16px', background: 'none', border: 'none',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ color: '#888', fontSize: 12, flexShrink: 0, width: 10 }}>{open ? '▾' : '▸'}</span>
        <span style={{ color: '#e0e0e0', fontSize: 14, fontWeight: 600, flexShrink: 0 }}>{title}</span>
        {!open && summary != null && summary !== '' && (
          <span style={{
            marginLeft: 'auto', paddingLeft: 12, color: '#888', fontSize: 12, fontWeight: 400,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {summary}
          </span>
        )}
      </button>
      {open && <div style={{ padding: '0 16px 16px' }}>{children}</div>}
    </div>
  );
}

const STEP_LABELS = {
  choose: 'Get Started',
  repo: 'Repository',
  validation: 'Validation',
  pickApp: 'Pick App',
  stack: 'Stack Detection',
  requirements: 'App Config',
  config: 'Deploy Settings',
  review: 'Review & Deploy',
  describe: 'Describe Your App',
  architecture: 'Architecture',
  services: 'Connected Services',
  'build-review': 'Review & Build',
  'github-token': 'GitHub Access',
  enhance: 'Enhance / Debug',
  enhancing: 'Applying Changes',
  deployed: 'Status',
  building: 'Status',
};

const GITHUB_TOKEN_STORAGE_KEY = 'lyric-build-github-token';

// ── Deploy-from-local-folder helpers ─────────────────────────────────────────
// The server can't read the user's laptop, so the browser enumerates the chosen
// directory (<input webkitdirectory>) and uploads each file. We filter out the
// obvious junk client-side so we never read/upload dependencies or build output.

// Directory names that are dependency/build/tooling output — never uploaded.
const LOCAL_IGNORE_DIRS = new Set([
  'node_modules', '.git', '.hg', '.svn', 'dist', 'build', 'out', '.next', '.nuxt',
  '.venv', 'venv', 'env', '__pycache__', '.pytest_cache', '.mypy_cache', '.ruff_cache',
  '.cache', '.parcel-cache', '.turbo', 'coverage', '.nyc_output', '.gradle',
  '.idea', '.vscode', '.terraform', 'target', 'bin', 'obj', 'vendor',
]);
// Individual files to skip (OS cruft, logs) regardless of directory.
const LOCAL_IGNORE_FILES = new Set(['.DS_Store', 'Thumbs.db', 'npm-debug.log', 'yarn-error.log']);
// Skip any single file bigger than this — almost always a binary/asset, and it
// bloats the base64 upload. Users can add large assets to the repo separately.
const LOCAL_MAX_FILE_BYTES = 5 * 1024 * 1024;

// Decide whether a repo-relative path (folder prefix already stripped) should be
// skipped. Returns a short reason string, or '' to keep the file. Secret .env
// files are skipped by default so we never commit credentials to the new repo —
// *.example / *.sample / *.template are kept as they're safe placeholders.
function localSkipReason(relPath, sizeBytes) {
  const segs = relPath.split('/');
  const base = segs[segs.length - 1];
  for (const seg of segs.slice(0, -1)) {
    if (LOCAL_IGNORE_DIRS.has(seg)) return `in ${seg}/`;
  }
  if (LOCAL_IGNORE_FILES.has(base)) return 'system file';
  if (base === '.env' || (/^\.env\./.test(base) && !/\.(example|sample|template)$/i.test(base))) {
    return '.env (secrets)';
  }
  if (sizeBytes > LOCAL_MAX_FILE_BYTES) return `> ${Math.round(LOCAL_MAX_FILE_BYTES / (1024 * 1024))}MB`;
  return '';
}

// Convert an ArrayBuffer to base64 in chunks (avoids call-stack overflow that a
// naive String.fromCharCode(...bytes) hits on large files).
function arrayBufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

// Client mirror of the server's toRepoSlug — a valid GitHub repo name.
function slugifyRepoName(name) {
  return String(name || '')
    .trim().toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^[-._]+|[-._]+$/g, '')
    .slice(0, 100);
}

// True when the browser supports the File System Access API. NOTE: it is NOT a
// popup-free path — window.showDirectoryPicker() shows a native "Allow this site
// to view and copy files?" permission prompt. The ONLY way to read a local
// folder with zero native browser dialogs is drag-and-drop (the drop gesture is
// implicit consent), so that is the primary path; this API + <input
// webkitdirectory> are click-to-browse fallbacks that DO prompt.
const SUPPORTS_DIRECTORY_PICKER =
  typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';

// Recursively walk a FileSystemDirectoryHandle, returning [{ rel, file, size }].
// Ignored directories are pruned during the walk so we never even read them.
async function collectFilesFromDirHandle(dirHandle, prefix = '') {
  const out = [];
  for await (const entry of dirHandle.values()) {
    const rel = prefix ? `${prefix}/${entry.name}` : entry.name;
    if (entry.kind === 'directory') {
      if (LOCAL_IGNORE_DIRS.has(entry.name)) continue;
      const nested = await collectFilesFromDirHandle(entry, rel);
      for (const f of nested) out.push(f);
    } else if (entry.kind === 'file') {
      const file = await entry.getFile();
      out.push({ rel, file, size: file.size });
    }
  }
  return out;
}

// Read ALL immediate children of a dropped directory entry. The DataTransfer
// FileSystemDirectoryReader returns at most ~100 entries per readEntries() call,
// so we loop until it returns an empty batch.
function readAllDirEntries(dirEntry) {
  return new Promise((resolve, reject) => {
    const reader = dirEntry.createReader();
    const all = [];
    const readMore = () => reader.readEntries((batch) => {
      if (!batch.length) { resolve(all); return; }
      for (const e of batch) all.push(e);
      readMore();
    }, reject);
    readMore();
  });
}

// Recursively read a dropped FileSystemEntry (from DataTransferItem.
// webkitGetAsEntry) into [{ rel, file, size }]. Drag-and-drop reads files with
// NO native browser permission popup — unlike any click-to-browse picker.
async function collectFilesFromEntry(entry, prefix = '') {
  const rel = prefix ? `${prefix}/${entry.name}` : entry.name;
  if (entry.isFile) {
    const file = await new Promise((res, rej) => entry.file(res, rej));
    return [{ rel, file, size: file.size }];
  }
  if (entry.isDirectory) {
    if (LOCAL_IGNORE_DIRS.has(entry.name)) return [];
    const children = await readAllDirEntries(entry);
    const out = [];
    for (const child of children) {
      const nested = await collectFilesFromEntry(child, rel);
      for (const f of nested) out.push(f);
    }
    return out;
  }
  return [];
}


// Slugify a repo/branch fragment for use in a Lyric app name (URL slug).
// The server sanitizes appNameOverride with /[^a-z0-9-]/g (lowercase; it KEEPS
// existing dashes), so we do the same here, then collapse runs of dashes and
// trim edges for readability. Examples:
//   "feature/foo"  → "feature-foo"
//   "release-1.2"  → "release-1-2"
//   "-Fix_bug--"   → "fix-bug"
const slugifyName = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')  // match the server's allowed set (keeps '-')
    .replace(/-+/g, '-')          // collapse runs so ".", "_", "/" don't stack
    .replace(/^-+|-+$/g, '');     // trim leading/trailing dashes

// ── Discovered-endpoint network check (deploy 'stack' step) ─────────────────
// These mirror the admin Network Reachability tool's auth handling so a
// developer can attach a Bearer token / custom headers to a probe of an
// endpoint their own code references. The endpoints themselves come ONLY from
// the static scan in /api/deploy/detect-stack — nothing here is invented.

// Per-endpoint test config: whether it's selected for probing, optional auth,
// and whether the auth panel is expanded.
const emptyEndpointTest = () => ({
  selected: false,
  manualUrl: '',
  auth: { mode: 'none', bearer: '', custom: [{ name: '', value: '' }] },
  _authOpen: false,
});

// Convert a row's auth config into the header object the server expects.
// Returns null when there's nothing to send (so the request omits `headers`).
function buildEndpointHeaders(auth) {
  if (!auth || auth.mode === 'none') return null;
  if (auth.mode === 'bearer') {
    const tok = (auth.bearer || '').trim();
    return tok ? { Authorization: `Bearer ${tok}` } : null;
  }
  if (auth.mode === 'custom') {
    const out = {};
    for (const { name, value } of auth.custom || []) {
      const n = (name || '').trim();
      const v = (value || '').trim();
      if (n && v) out[n] = v;
    }
    return Object.keys(out).length ? out : null;
  }
  return null;
}

// Per-kind presentation: icon + short human label + whether it's auto-probeable.
const ENDPOINT_KIND_META = {
  'aws-service':   { icon: '☁️', label: 'AWS service',     probeable: true },
  'hardcoded-url': { icon: '🔗', label: 'Hardcoded URL',   probeable: true },
  'env-var-url':   { icon: '🔧', label: 'Env-var endpoint', probeable: false },
  'dynamic':       { icon: '🧩', label: 'Runtime-built URL', probeable: false },
  'cli-shellout':  { icon: '⚠️', label: 'Cloud CLI call',  probeable: false },
};


/**
 * Perfectly symmetrical SVG gear, computed mathematically so every tooth
 * is identical and the centroid is exactly at the centre — no wobble when spinning.
 */
function GearIcon({ size = 52, color = '#4fc3f7' }) {
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size * 0.42;   // tooth tips
  const innerR = size * 0.28;   // tooth roots
  const holeR  = size * 0.13;   // centre hole
  const numTeeth = 10;
  const toothHalf = (Math.PI / numTeeth) * 0.52; // fraction of half-pitch taken by each tooth

  // Build the gear outline: for each tooth, 4 points (inner approach, outer start, outer end, inner depart)
  const pts = [];
  for (let i = 0; i < numTeeth; i++) {
    const base = (i / numTeeth) * 2 * Math.PI - Math.PI / 2;
    const a1 = base - toothHalf;
    const a2 = base + toothHalf;
    pts.push([cx + innerR * Math.cos(a1), cy + innerR * Math.sin(a1)]);
    pts.push([cx + outerR * Math.cos(a1), cy + outerR * Math.sin(a1)]);
    pts.push([cx + outerR * Math.cos(a2), cy + outerR * Math.sin(a2)]);
    pts.push([cx + innerR * Math.cos(a2), cy + innerR * Math.sin(a2)]);
  }

  // Connect points: L for straight flanks/tips, A for arcs along the inner root circle
  let d = `M ${pts[0][0].toFixed(2)} ${pts[0][1].toFixed(2)}`;
  for (let i = 1; i < pts.length; i++) {
    const [x, y] = pts[i];
    if (i % 4 === 0) {
      // Transition from inner depart of previous tooth to inner approach of this tooth — arc
      d += ` A ${innerR.toFixed(2)} ${innerR.toFixed(2)} 0 0 1 ${x.toFixed(2)} ${y.toFixed(2)}`;
    } else {
      d += ` L ${x.toFixed(2)} ${y.toFixed(2)}`;
    }
  }
  // Final arc back to start
  d += ` A ${innerR.toFixed(2)} ${innerR.toFixed(2)} 0 0 1 ${pts[0][0].toFixed(2)} ${pts[0][1].toFixed(2)} Z`;

  // Append centre hole as a subpath (evenodd cuts it out)
  const hr = holeR.toFixed(2);
  d += ` M ${(cx + holeR).toFixed(2)} ${cy.toFixed(2)}`
     + ` A ${hr} ${hr} 0 1 0 ${(cx - holeR).toFixed(2)} ${cy.toFixed(2)}`
     + ` A ${hr} ${hr} 0 1 0 ${(cx + holeR).toFixed(2)} ${cy.toFixed(2)} Z`;

  return (
    <svg
      width={size} height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ display: 'inline-block', animation: 'lyricGearSpin 2s linear infinite' }}
    >
      <path d={d} fill={color} fillRule="evenodd" />
    </svg>
  );
}


function DeployIntakeForm({ onClose, selectedModel: selectedModelProp, initialEdit, initialDeploy, initialIosFromApp }) {
  const [selectedModel, setSelectedModelState] = useState(
    selectedModelProp || localStorage.getItem('selectedModel') || 'gemini-2.5-flash'
  );
  // Persist the chosen model so it stays consistent with the app-wide picker.
  const setSelectedModel = (m) => {
    setSelectedModelState(m);
    try { localStorage.setItem('selectedModel', m); } catch { /* ignore */ }
  };
  const [path, setPath] = useState(initialEdit ? 'edit' : initialDeploy ? 'repo' : initialIosFromApp ? 'build' : null);
  const [step, setStep] = useState(initialEdit ? 'validation' : initialDeploy ? 'repo' : initialIosFromApp ? 'describe' : 'choose');
  const [modelOpen, setModelOpen] = useState(false);
  const [showCreateJira, setShowCreateJira] = useState(false);
  const [showMockup, setShowMockup] = useState(false);
  // Save/resume progress for the Build-from-Scratch flow.
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftMsg, setDraftMsg] = useState('');
  const [buildDrafts, setBuildDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [showLoadDrafts, setShowLoadDrafts] = useState(false);
  // Which Connected-Services category ('integration'|'database'|'custom') has its
  // "Add Integration" picker expanded.
  const [serviceAddNotice, setServiceAddNotice] = useState(null);
  // Service id (from SERVICE_CATALOG) currently selected in that category's
  // picker dropdown, awaiting field entry + confirmation.
  const [pickingServiceId, setPickingServiceId] = useState('');
  // Draft field values for the service currently being added, keyed by field key.
  const [pendingServiceFields, setPendingServiceFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [repositories, setRepositories] = useState([]);
  const LAST_REPO_KEY = 'lyric-last-repo-url';
  const [repoSearch, setRepoSearch] = useState('');
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [repoInputMode, setRepoInputMode] = useState(() => localStorage.getItem(LAST_REPO_KEY) ? 'url' : 'search');
  // ── Deploy-from-local-folder state ──────────────────────────────────────────
  const [localFiles, setLocalFiles] = useState([]);          // [{ path, file, size }] kept after filtering
  const [localFolderName, setLocalFolderName] = useState(''); // top-level folder name the user picked
  const [localRepoName, setLocalRepoName] = useState('');     // editable new-repo name (defaults to folder slug)
  const [localSkipped, setLocalSkipped] = useState(0);        // count of files filtered out (deps/build/.env/…)
  const [localTotalBytes, setLocalTotalBytes] = useState(0);  // total bytes of kept files
  const [localBusy, setLocalBusy] = useState(false);          // true while reading/uploading
  const [localProgress, setLocalProgress] = useState('');     // human-readable progress text
  const [localError, setLocalError] = useState('');
  const [localTokenNeeded, setLocalTokenNeeded] = useState(false); // server asked for a PAT
  const [localDragOver, setLocalDragOver] = useState(false);       // drag-and-drop hover state
  const localDirInputRef = useRef(null);
  const [deployResult, setDeployResult] = useState(null);
  const [configAnalysis, setConfigAnalysis] = useState(null);
  // Which monorepo candidate the user picked on the 'pickApp' step. Stores
  // the candidate's path (e.g. 'apps/workflow-tracker'); '' means repo root.
  const [pickedCandidate, setPickedCandidate] = useState(null);
  // Search box on the monorepo picker. Helpful for repos with dozens of apps
  // (e.g. ischemaview/rapid-platform has ~50 candidate subdirs).
  const [candidateFilter, setCandidateFilter] = useState('');
  // Search box on the branch dropdown. Surfaces only when the repo has lots
  // of branches (e.g. rapid-platform has 100+) so users can find main / their
  // feature branch quickly.
  const [branchFilter, setBranchFilter] = useState('');
  const [lyricEnvMatch, setLyricEnvMatch] = useState(null); // { matched: [], unmatched: [] }
  const [matchingLyricEnv, setMatchingLyricEnv] = useState(false);
  const [githubToken, setGithubToken] = useState(() => {
    try { return localStorage.getItem(GITHUB_TOKEN_STORAGE_KEY) || ''; } catch { return ''; }
  });
  const [tokenSetup, setTokenSetup] = useState(null);
  const [rememberToken, setRememberToken] = useState(true);
  const [showBuildConfirm, setShowBuildConfirm] = useState(false);
  const [scaffoldPhase, setScaffoldPhase] = useState(null); // null | 'generating' | 'done' | 'error'
  const [scaffoldResult, setScaffoldResult] = useState(null);
  const [deployPhase, setDeployPhase] = useState(null); // null | 'deploying' | 'done' | 'error'
  const [deployAppResult, setDeployAppResult] = useState(null);
  const [buildEnvVars, setBuildEnvVars] = useState([]); // [{ key, example, comment, value }]
  const [buildEnvPhase, setBuildEnvPhase] = useState(null); // null | 'waiting' | 'deploying'
  // Elapsed-seconds counter + phase tracking that drive the live progress bar
  // and rotating status messages on the building screen.
  const [buildElapsed, setBuildElapsed] = useState(0);
  const buildPhaseKeyRef = useRef(null);
  const buildPhaseStartRef = useRef(0);
  // 'new' = create/overwrite a secret from the env vars in the form
  // 'existing' = reuse an existing tools/lyric/* secret (optionally merge missing keys into it)
  // Edit flow: if the app is already using a secret, default to 'existing' so the
  // picker shows the secret the app currently uses instead of defaulting to
  // "Create new" (which read as if it would overwrite the current secret).
  const [secretMode, setSecretMode] = useState(initialEdit?.secretName ? 'existing' : 'new');
  // Pre-selected to the app's current secret when editing (see secretMode above).
  const [selectedExistingSecret, setSelectedExistingSecret] = useState(initialEdit?.secretName || '');
  // Within 'existing' mode, the user can either pick from the dropdown (needs
  // secretsmanager:ListSecrets) or type the name directly (only needs GetSecretValue).
  // 'list' uses selectedExistingSecret; 'manual' uses manualSecretName.
  const [existingSource, setExistingSource] = useState('list'); // 'list' | 'manual'
  // Pre-filled with the app's current secret when editing, so switching to the
  // "Type name" source still shows the secret the app is already using.
  const [manualSecretName, setManualSecretName] = useState(initialEdit?.secretName || '');
  const [existingSecrets, setExistingSecrets] = useState([]);
  const [secretsLoading, setSecretsLoading] = useState(false);
  const [secretsLoadError, setSecretsLoadError] = useState('');
  // Diagnostic info returned by GET /api/app-secrets so we can explain empty dropdowns.
  // Shape: { totalCount, sampleNames: [], region, prefix }
  const [secretsDiagnostic, setSecretsDiagnostic] = useState(null);
  const [existingSecretKeys, setExistingSecretKeys] = useState([]); // [{key, masked}]
  const [existingSecretLoading, setExistingSecretLoading] = useState(false);
  const [existingSecretError, setExistingSecretError] = useState('');
  const [missingKeyValues, setMissingKeyValues] = useState({}); // { keyName: value to merge in } — used for both missing AND edited-existing keys
  // Keys of an existing secret the user has clicked "Edit" on, so we know which
  // rows should show a value input instead of just the ✓/present indicator.
  const [editingSecretKeys, setEditingSecretKeys] = useState({}); // { keyName: true }
  // Freeform key/value pairs the user adds beyond what the app declares as
  // required — lets them stash any extra credential into the same AWS secret.
  const [customSecretVars, setCustomSecretVars] = useState([]); // [{ key, value }]
  // Compliance Check — approved fixes are committed to a temporary GitHub
  // branch before deployment. The source branch remains untouched.
  const [approvedFixes, setApprovedFixes] = useState({}); // { fixId: true }
  // When the user explicitly chooses to deploy despite unfixed blocker-level findings.
  // Reset to false whenever stack detection runs (so new findings re-gate the wizard).
  const [overrideBlockers, setOverrideBlockers] = useState(false);
  // "Deploy anyway" compliance-override confirmation (Lyric popup, not window.confirm).
  const [deployAnywayOpen, setDeployAnywayOpen] = useState(false);
  // Discovered-endpoint network check (deploy 'stack' step). Keyed by a stable
  // endpoint id (`${kind}:${value}`) so per-row test/auth state survives
  // re-renders. Populated lazily as the user expands the panel.
  const [endpointTests, setEndpointTests] = useState({}); // { [endpointId]: emptyEndpointTest() }
  const [endpointResults, setEndpointResults] = useState(null); // server `results` array, or null
  const [endpointMeta, setEndpointMeta] = useState(null);       // server `meta`, or null
  const [endpointChecking, setEndpointChecking] = useState(false);
  const [endpointError, setEndpointError] = useState('');
  const [enhanceEnvVars, setEnhanceEnvVars] = useState([]); // [{ key, value }]
  const [showEnhanceEnvVars, setShowEnhanceEnvVars] = useState(false);
  // Edit flow: hide the AWS Secrets Manager picker by default. The user must
  // explicitly opt in by clicking "Add/Change AWS Secret" — otherwise the
  // existing secretName (if any) is preserved on redeploy.
  const [showSecretsEditor, setShowSecretsEditor] = useState(false);
  const [loadingEnvExample, setLoadingEnvExample] = useState(false);
  const [enhanceDescription, setEnhanceDescription] = useState('');
  // Enhance edits ALWAYS open a pull request now — they never commit straight to
  // the source branch. Kept as state (locked to 'pr') so the existing
  // mode-threading through handleEnhanceSubmit keeps working.
  const [enhanceMode] = useState('pr');
  const [enhancePhase, setEnhancePhase] = useState(null); // null | 'working' | 'clarify' | 'done' | 'error'
  const [enhanceResult, setEnhanceResult] = useState(null);
  // Pending clarifying questions Lyric asked before editing (null when none).
  // { questions:[{id,question,why,suggestions}], assumptions:[], answers:{id:text}, baseOverrides }
  const [enhanceClarify, setEnhanceClarify] = useState(null);
  const [enhanceDeployPhase, setEnhanceDeployPhase] = useState(null); // null | 'deploying' | 'starting' | 'done' | 'error'
  const [enhanceDeployResult, setEnhanceDeployResult] = useState(null);
  // Edit flow: optionally base the change request on a Jira ticket. Pick a
  // project + ticket; Lyric reads the ticket (GET /api/jira/issues/:key) and
  // loads its summary/description into the change-request box below so the same
  // /api/deploy/enhance pipeline applies the changes described in the ticket.
  const [enhanceUseJira, setEnhanceUseJira] = useState(false);
  const [enhanceJira, setEnhanceJira] = useState({ projectKey: '', issueType: 'Any', issueKey: '', issueSummary: '' });
  const [enhanceJiraLoading, setEnhanceJiraLoading] = useState(false);
  const [enhanceJiraError, setEnhanceJiraError] = useState('');
  const [enhanceJiraTicket, setEnhanceJiraTicket] = useState(null);
  const enhanceJiraBlockRef = useRef('');
  // Result of stamping the Jira ticket after a ticket-driven change is applied:
  // null | { status: 'posting'|'done'|'error', key, error? }
  const [enhanceJiraStamp, setEnhanceJiraStamp] = useState(null);
  const [fixConfirmOpen, setFixConfirmOpen] = useState(false); // "are you sure" before fixing a flagged issue
  // ── Visual edit (Phase 1): click an element in the live app (iframed via the
  // /apps proxy in ?__lyricEdit=1 mode), describe a change for that element, and
  // route it through the enhance pipeline (PR by default). Only meaningful when
  // editing a deployed app (initialEdit.appName present).
  const [visualStudioOpen, setVisualStudioOpen] = useState(false); // full-screen editor
  const [visualSelected, setVisualSelected] = useState(null); // element anchor from the iframe
  const [visualInstruction, setVisualInstruction] = useState('');
  const [visualChanges, setVisualChanges] = useState([]); // queued edits: AI instructions AND manual ops
  const [visualIframeKey, setVisualIframeKey] = useState(0); // bump to reload the iframe
  const [visualMode, setVisualMode] = useState('edit'); // 'edit' | 'navigate' | 'draw'
  const [visualNavLinks, setVisualNavLinks] = useState(true); // show red nav-to-edit circles
  const visualNavLinksRef = useRef(true); // latest value for the message handler
  const [visualAddOpen, setVisualAddOpen] = useState(false); // Add-element dropdown
  // Draw/sketch mode: annotate the live app to explain a change in pictures.
  const [visualDrawTool, setVisualDrawTool] = useState('pen'); // 'pen' | 'arrow' | 'box'
  const [visualDrawColor, setVisualDrawColor] = useState('#ef4444');
  const [visualSketchCount, setVisualSketchCount] = useState(0); // marks drawn (each is its own change)
  const visualIframeRef = useRef(null); // for postMessage commands into the overlay
  // Origin of the deployed app shown in the visual-editor iframe (the API origin
  // in dev, same-origin in prod). Used as the postMessage targetOrigin so overlay
  // commands are delivered ONLY to that frame instead of being broadcast with '*'.
  const visualAppOrigin = (() => {
    try { return new URL(getApiBase()).origin; }
    catch { return window.location.origin; }
  })();
  const [repoEnvVars, setRepoEnvVars] = useState([]); // [{ key, value }] for the repo deploy flow
  const [availableGroups, setAvailableGroups] = useState([]);
  const [groupsDemoMode, setGroupsDemoMode] = useState(false);
  const [deployLiveStatus, setDeployLiveStatus] = useState('deploying');
  const [deployLiveStage, setDeployLiveStage] = useState('cloning');
  const [deployLiveError, setDeployLiveError] = useState(null);
  const [deployElapsed, setDeployElapsed] = useState(0);
  const deployPollRef = useRef(null);
  const deployTimerRef = useRef(null);
  // When set, a deployed app is shown contained in a full-screen in-window
  // viewer (same overlay the dashboard uses) instead of a new browser tab.
  const [viewerApp, setViewerApp] = useState(null);

  // ── Branch-aware deploy: replace an existing app or create a new one ──
  // When deploying a repo the user already has a deployed app for, we let them
  // choose to REPLACE one of those apps (redeploy in place, same URL) or CREATE
  // a NEW app (branch-suffixed name) so two branches of the same repo can run
  // side by side. Populated from /api/deploy/list on the review step.
  const [existingRepoApps, setExistingRepoApps] = useState([]); // [{appName, branch, displayName, status, url}]
  const [existingRepoAppsLoaded, setExistingRepoAppsLoaded] = useState(false); // false until /api/deploy/list resolves
  const [existingRepoAppsError, setExistingRepoAppsError] = useState(''); // non-empty when the list couldn't be loaded
  const [existingReloadNonce, setExistingReloadNonce] = useState(0); // bump to force a re-fetch (Retry button)
  const [deployMode, setDeployMode] = useState('new'); // 'replace' | 'new'
  const [replaceTargetApp, setReplaceTargetApp] = useState(''); // appName to replace when mode==='replace'
  const [newAppDisplayName, setNewAppDisplayName] = useState(''); // editable display name for a new branch app

  // Repo .env files offered in the config step's environment picker. Fetched
  // from /api/deploy/env-files (subdir-aware, excludes .env.example). Empty list
  // → the dropdown shows a disabled "no .env files available" placeholder.
  const [envFiles, setEnvFiles] = useState([]);
  const [envFilesLoading, setEnvFilesLoading] = useState(false);
  const [envFilesError, setEnvFilesError] = useState('');

  const [formData, setFormData] = useState({
    // Repo path fields
    repoUrl: initialEdit
      ? `https://github.com/${initialEdit.owner}/${initialEdit.repo}`
      : initialDeploy
      ? `https://github.com/${initialDeploy.owner}/${initialDeploy.repo}`
      : (localStorage.getItem(LAST_REPO_KEY) || ''),
    owner: initialEdit?.owner || initialDeploy?.owner || '',
    repo: initialEdit?.repo || initialDeploy?.repo || '',
    branch: initialEdit?.branch || initialDeploy?.branch || 'main',
    branches: [],
    repoInfo: null,
    stackInfo: null,
    envVars: [],
    appDisplayName: initialEdit?.displayName || '',
    secretName: initialEdit?.secretName || '',
    // Prefill visibility from the existing deployment when editing, so the
    // user doesn't accidentally widen access on a redeploy.
    visibilityMode: initialEdit?.allowedGroups?.length > 0 ? 'specific' : 'everyone',
    allowedGroups: Array.isArray(initialEdit?.allowedGroups) ? initialEdit.allowedGroups : [],
    // Per-app co-admins (emails) — additional users granted full owner-level
    // management parity. Prefilled when editing so a redeploy doesn't drop them.
    coAdmins: Array.isArray(initialEdit?.coAdmins) ? initialEdit.coAdmins : [],
    // View-only allowed users (emails) — named people who may open this app
    // while it's private/group-restricted, with no admin controls. Prefilled
    // when editing so a redeploy doesn't drop them.
    allowedUsers: Array.isArray(initialEdit?.allowedUsers) ? initialEdit.allowedUsers : [],
    // Opt-in: forward the logged-in user's Okta token into the app so its
    // frontend can call its own backend as the user. Off by default.
    forwardOktaToken: !!initialEdit?.forwardOktaToken,
    // Owner-only pre-release gate. When true, only the owner (+ co-admins) can
    // see/open the app — used to production-test before releasing to teams.
    // Prefilled when editing so a redeploy preserves the owner's choice.
    ownerOnly: !!initialEdit?.ownerOnly,
    buildCommand: '',
    startCommand: '',
    deployTarget: 'local',
    // Repo .env file chosen as the app's environment (config step). Prefilled
    // when editing/redeploying so the previously-selected file stays selected.
    selectedEnvFile: initialEdit?.selectedEnvFile || initialDeploy?.selectedEnvFile || '',
    // The app's current deploy version (read-only context) and an OPTIONAL
    // override the engineer can type to set the next version. Blank override =
    // auto-increment the minor version on deploy (1.0.0 → 1.1.0).
    currentVersion: initialEdit?.version || initialDeploy?.version || '',
    version: '',
    // Build-from-scratch fields
    appName: initialIosFromApp?.suggestedAppName || '',
    appDescription: initialIosFromApp?.appDescription || '',
    appType: initialIosFromApp ? 'ios-swiftui' : 'web-api',
    language: initialIosFromApp ? 'swift' : 'javascript',
    // iOS (SwiftUI) build settings — only used when appType is 'ios-swiftui'.
    swiftVersion: '5.0',
    deploymentTarget: '17.0',
    features: [],
    customFeature: '',
    // Optional Jira source: build the app from an existing Epic/Story/etc.
    useJira: false,
    jiraProjectKey: '',
    jiraIssueType: 'Epic',
    jiraIssueKey: '',
    jiraIssueSummary: '',
    // Optional UI design mockup (HTML) produced by the Design Mockup tool, used
    // as a visual reference when the AI scaffolds the app's frontend.
    mockupHtml: '',
    mockupModelUsed: '',
    selectedServices: [],   // e.g. ['jira', 'confluence', 'postgresql']
    serviceConfig: {},      // { postgresql: { connectionString: '' }, 'custom-api-0': { name, baseUrl, apiKey }, ... }
  });

  const authenticatedFetch = async (url, options = {}) => {
    try {
      const token = await authService.getAccessToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      };
      return fetch(url, { ...options, headers });
    } catch (err) {
      console.error('Authentication error:', err);
      throw new Error('Failed to authenticate. Please log in again.');
    }
  };

  // Authoritative source for "what env vars does this app need": the same
  // code scan the fresh-deploy flow uses (source refs, Pydantic Settings
  // fields, require-env helpers) — never .env.example. Used both by the
  // edit-flow auto-load and its manual "detect from code" button so the two
  // never drift. Returns `null` on failure (network error or non-OK
  // response) so callers can distinguish "the analysis failed, fall back to
  // saved values" from "the analysis succeeded and legitimately found zero
  // vars" ([] must NOT trigger the saved-value fallback, or a real zero-var
  // result would resurrect stale/removed localStorage credentials).
  const fetchCodeEnvVars = async (owner, repo, branch, subdir) => {
    try {
      const apiUrl = getApiBase();
      const res = await authenticatedFetch(`${apiUrl}/api/deploy/analyze-config`, {
        method: 'POST',
        body: JSON.stringify({ owner, repo, branch, subdir: subdir || '' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return null;
      return (data.envVars || []).map(v => ({ key: v.key }));
    } catch {
      return null;
    }
  };

  const fetchRepositories = async () => {
    if (repositories.length > 0) return; // already loaded
    setLoadingRepos(true);
    try {
      const apiUrl = getApiBase();
      const res = await authenticatedFetch(`${apiUrl}/api/github/repos`);
      const data = await res.json();
      if (data.success) {
        const repos = (data.repos || data.repositories || []).map(r => ({
          ...r,
          fullName: r.fullName || r.full_name || r.name || '',
        }));
        setRepositories(repos);
      }
    } catch (err) {
      console.error('Failed to fetch repositories:', err);
    } finally {
      setLoadingRepos(false);
    }
  };

  const handleRepoSelect = (fullName) => {
    if (!fullName) return;
    const [owner, repo] = fullName.split('/');
    updateField('repoUrl', `https://github.com/${fullName}`);
    updateField('owner', owner);
    updateField('repo', repo);
    // Auto-validate the selected repo
    validateRepoByName(owner, repo);
  };

  const validateRepoByName = async (owner, repo, preferredBranch) => {
    setLoading(true);
    setError('');
    try {
      const apiUrl = getApiBase();
      const res = await authenticatedFetch(`${apiUrl}/api/deploy/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owner, repo }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Repository not accessible (${res.status})`);
      }

      const data = await res.json();
      localStorage.setItem(LAST_REPO_KEY, `https://github.com/${owner}/${repo}`);
      const branches = data.branches || ['main'];
      // When a specific branch is requested (e.g. deploying the branch an edit
      // just created), honor it and make sure it's selectable even if the repo's
      // branch listing hasn't caught up with the freshly-pushed ref yet.
      const branch = preferredBranch || data.defaultBranch || 'main';
      const branchList = preferredBranch && !branches.includes(preferredBranch)
        ? [preferredBranch, ...branches]
        : branches;
      setFormData((prev) => ({
        ...prev,
        owner,
        repo,
        repoInfo: data.repoInfo,
        branches: branchList,
        branch,
        appDisplayName: prev.appDisplayName || repo,
      }));
      setStep('validation');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  // ── Build-from-Scratch: save / resume wizard progress ───────────────────────
  // Snapshot the wizard so a user can resume later (or load a different saved
  // build). The `state` blob is opaque to the server; `summary` drives the list.
  const saveBuildProgress = async () => {
    setDraftSaving(true);
    setDraftMsg('');
    try {
      const apiUrl = getApiBase();
      // Never persist secrets (API keys, DB connection strings) into a saved
      // draft — redact serviceConfig before it's stored server-side.
      const safeFormData = { ...formData, serviceConfig: redactServiceConfig(formData.serviceConfig) };
      const body = {
        name: formData.appName?.trim() || 'Untitled build',
        summary: {
          appName: formData.appName || '',
          step,
          useJira: !!formData.useJira,
          jiraIssueKey: formData.useJira ? (formData.jiraIssueKey || '') : '',
          hasMockup: !!formData.mockupHtml,
          language: formData.language || '',
        },
        state: { step, selectedModel, formData: safeFormData },
      };
      const isUpdate = !!currentDraftId;
      const url = isUpdate
        ? `${apiUrl}/api/deploy/build-drafts/${encodeURIComponent(currentDraftId)}`
        : `${apiUrl}/api/deploy/build-drafts`;
      const res = await authenticatedFetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data?.error || 'Failed to save progress');
      if (data.draft?.id) setCurrentDraftId(data.draft.id);
      setDraftMsg('Saved ✓');
      setTimeout(() => setDraftMsg((m) => (m === 'Saved ✓' ? '' : m)), 3000);
    } catch (err) {
      setDraftMsg(`⚠️ ${err.message}`);
    } finally {
      setDraftSaving(false);
    }
  };

  const fetchBuildDrafts = async () => {
    setDraftsLoading(true);
    try {
      const apiUrl = getApiBase();
      const res = await authenticatedFetch(`${apiUrl}/api/deploy/build-drafts`);
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) setBuildDrafts(data.drafts || []);
    } catch { /* ignore */ } finally {
      setDraftsLoading(false);
    }
  };

  const openLoadDrafts = () => {
    setShowLoadDrafts(true);
    fetchBuildDrafts();
  };

  const loadBuildDraft = async (id) => {
    try {
      const apiUrl = getApiBase();
      const res = await authenticatedFetch(`${apiUrl}/api/deploy/build-drafts/${encodeURIComponent(id)}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data?.error || 'Failed to load draft');
      const st = data.draft?.state || {};
      if (st.formData && typeof st.formData === 'object') {
        // Merge onto defaults so keys added since the draft was saved keep their
        // defaults instead of becoming undefined.
        setFormData((prev) => ({ ...prev, ...st.formData }));
      }
      if (st.selectedModel) setSelectedModel(st.selectedModel);
      setCurrentDraftId(id);
      setPath('build');
      setStep(st.step && BUILD_STEPS.includes(st.step) ? st.step : 'describe');
      setShowLoadDrafts(false);
      setDraftMsg('Loaded ✓');
      setTimeout(() => setDraftMsg((m) => (m === 'Loaded ✓' ? '' : m)), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteBuildDraft = async (id) => {
    try {
      const apiUrl = getApiBase();
      const res = await authenticatedFetch(`${apiUrl}/api/deploy/build-drafts/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data?.error || 'Failed to delete draft');
      if (currentDraftId === id) setCurrentDraftId(null);
      fetchBuildDrafts();
    } catch (err) {
      setDraftMsg(`⚠️ ${err.message}`);
    }
  };

  // A compact save/resume bar shown atop each Build-from-Scratch step.
  const renderBuildSaveBar = () => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      padding: '8px 12px', marginBottom: 14, backgroundColor: '#16161f',
      border: '1px solid #333', borderRadius: 8,
    }}>
      <button
        type="button"
        onClick={saveBuildProgress}
        disabled={draftSaving}
        style={{
          padding: '6px 12px', backgroundColor: '#1e2a1e', color: '#81c784',
          border: '1px solid #3a5a3a', borderRadius: 6, fontSize: 12, fontWeight: 600,
          cursor: draftSaving ? 'not-allowed' : 'pointer',
        }}
      >
        {draftSaving ? 'Saving…' : '💾 Save progress'}
      </button>
      <button
        type="button"
        onClick={openLoadDrafts}
        style={{
          padding: '6px 12px', backgroundColor: '#16232e', color: '#7cc4ff',
          border: '1px solid #3a6a80', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
        }}
      >
        📂 Load saved build
      </button>
      {currentDraftId && <span style={{ fontSize: 11, color: '#888' }}>Editing a saved draft — “Save progress” updates it.</span>}
      {draftMsg && (
        <span style={{ fontSize: 12, color: draftMsg.startsWith('⚠️') ? '#ef9a9a' : '#81c784' }}>{draftMsg}</span>
      )}
    </div>
  );

  // A modal listing the caller's saved build drafts to resume.
  const renderLoadDraftsModal = () => (
    <div
      onClick={() => setShowLoadDrafts(false)}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(620px, 94vw)', maxHeight: '80vh', overflowY: 'auto',
          backgroundColor: '#1b1b24', border: '1px solid #3a3a4a', borderRadius: 12,
          padding: 24, boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
        }}
      >
        <h3 style={{ color: '#7cc4ff', margin: '0 0 4px' }}>📂 Resume a saved build</h3>
        <p style={{ color: '#aaa', fontSize: 12, margin: '0 0 16px' }}>
          Pick a saved draft to load its progress back into the wizard.
        </p>
        {draftsLoading ? (
          <p style={{ color: '#888', fontSize: 13 }}>Loading…</p>
        ) : buildDrafts.length === 0 ? (
          <p style={{ color: '#888', fontSize: 13 }}>No saved builds yet. Use “Save progress” to create one.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {buildDrafts.map((d) => (
              <div
                key={d.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                  border: `1px solid ${d.id === currentDraftId ? '#4fc3f7' : '#333'}`,
                  borderRadius: 8, backgroundColor: '#14141c',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.name}
                  </div>
                  <div style={{ color: '#888', fontSize: 11, marginTop: 2 }}>
                    {d.summary?.step ? `Step: ${d.summary.step}` : ''}
                    {d.summary?.language ? ` · ${d.summary.language}` : ''}
                    {d.summary?.hasMockup ? ' · 🎨 mockup' : ''}
                    {d.summary?.useJira && d.summary?.jiraIssueKey ? ` · 🔗 ${d.summary.jiraIssueKey}` : ''}
                    {d.updatedAt ? ` · ${new Date(d.updatedAt).toLocaleString()}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => loadBuildDraft(d.id)}
                  style={{ padding: '6px 12px', backgroundColor: '#16232e', color: '#7cc4ff', border: '1px solid #3a6a80', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                  Load
                </button>
                <button
                  type="button"
                  onClick={() => deleteBuildDraft(d.id)}
                  title="Delete this saved build"
                  style={{ padding: '6px 10px', backgroundColor: 'transparent', color: '#ef9a9a', border: '1px solid #5a3a3a', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={() => setShowLoadDrafts(false)} style={btnBack}>Close</button>
        </div>
      </div>
    </div>
  );


  // Apply the client-side filter to a raw [{ rel, file, size }] list and update
  // the preview state. Shared by both the modern picker and the fallback input.
  const applyPickedFiles = (rawItems, folderName) => {
    const kept = [];
    let skipped = 0;
    let bytes = 0;
    for (const item of rawItems) {
      if (!item.rel) { skipped++; continue; }
      if (localSkipReason(item.rel, item.size)) { skipped++; continue; }
      kept.push({ path: item.rel, file: item.file, size: item.size });
      bytes += item.size;
    }
    setLocalFolderName(folderName || 'app');
    setLocalRepoName((prev) => prev || slugifyRepoName(folderName));
    setLocalFiles(kept);
    setLocalSkipped(skipped);
    setLocalTotalBytes(bytes);
    if (kept.length === 0) {
      setLocalError('No deployable files found in that folder (everything was dependencies/build output).');
    }
  };

  // ── Drag-and-drop folder import (the ONLY zero-native-popup path) ───────────
  // The drop gesture is treated by the browser as implicit consent, so reading
  // the dropped folder via webkitGetAsEntry() shows NO permission dialog.
  const handleLocalDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLocalDragOver(false);
    setLocalError('');
    setLocalTokenNeeded(false);
    const items = Array.from(e.dataTransfer?.items || []);
    const entries = items
      .map((it) => (typeof it.webkitGetAsEntry === 'function' ? it.webkitGetAsEntry() : null))
      .filter(Boolean);
    if (entries.length === 0) {
      setLocalError('Could not read what was dropped. Drag a folder from Finder/Explorer.');
      return;
    }
    setLocalBusy(true);
    setLocalProgress('Reading dropped folder…');
    try {
      let folderName = 'app';
      const all = [];
      if (entries.length === 1 && entries[0].isDirectory) {
        // A single folder → repo root == that folder's contents (strip its name).
        folderName = entries[0].name;
        const children = await readAllDirEntries(entries[0]);
        for (const child of children) {
          const nested = await collectFilesFromEntry(child, '');
          for (const f of nested) all.push(f);
        }
      } else {
        // Multiple items / loose files → keep their own names at the repo root.
        for (const entry of entries) {
          const nested = await collectFilesFromEntry(entry, '');
          for (const f of nested) all.push(f);
        }
      }
      if (all.length === 0) {
        setLocalError('That drop had no files. Drag the project folder itself (not an alias).');
        return;
      }
      applyPickedFiles(all, folderName);
    } catch (err) {
      setLocalError(`Could not read the dropped folder: ${err?.message || err}`);
    } finally {
      setLocalBusy(false);
      setLocalProgress('');
    }
  };

  // Entry point for the "browse instead" button. Prefers the File System Access
  // API; falls back to the hidden <input webkitdirectory>. NOTE: unlike drop,
  // BOTH click-to-browse paths trigger a native browser security prompt.
  const handleChooseFolder = async () => {
    setLocalError('');
    setLocalTokenNeeded(false);
    if (!SUPPORTS_DIRECTORY_PICKER) {
      if (localDirInputRef.current) localDirInputRef.current.click();
      return;
    }
    let dirHandle;
    try {
      dirHandle = await window.showDirectoryPicker({ id: 'lyric-local-deploy', mode: 'read' });
    } catch (err) {
      // User dismissed the OS picker — nothing to do, and no popup needed.
      if (err && (err.name === 'AbortError' || err.name === 'NotAllowedError')) return;
      setLocalError(`Could not open the folder picker: ${err?.message || err}`);
      return;
    }
    setLocalBusy(true);
    setLocalProgress('Scanning folder…');
    try {
      const items = await collectFilesFromDirHandle(dirHandle);
      applyPickedFiles(items, dirHandle.name);
    } catch (err) {
      setLocalError(`Could not read the folder: ${err?.message || err}`);
    } finally {
      setLocalBusy(false);
      setLocalProgress('');
    }
  };

  // Fallback path: <input webkitdirectory> change handler (Safari/Firefox). This
  // route still triggers the browser's native upload confirmation — unavoidable
  // for that element — which is exactly why the modern picker above is preferred.
  const handleLocalDirPick = (e) => {
    const picked = Array.from(e.target.files || []);
    setLocalError('');
    setLocalTokenNeeded(false);
    if (picked.length === 0) return;
    // webkitRelativePath looks like "myfolder/src/index.js"; the first segment
    // is the folder the user chose — strip it so the repo root == folder root.
    const folderName = (picked[0].webkitRelativePath || picked[0].name).split('/')[0] || 'app';
    const items = picked.map((file) => ({
      rel: (file.webkitRelativePath || file.name).split('/').slice(1).join('/'),
      file,
      size: file.size,
    }));
    applyPickedFiles(items, folderName);
    // Allow re-picking the same folder (input only fires change on a new value).
    if (localDirInputRef.current) localDirInputRef.current.value = '';
  };

  // Read the kept files to base64, create the repo, push them, then hand off to
  // the normal validate → detect-stack → deploy flow.
  const handleCreateRepoFromLocal = async () => {
    const repoSlug = slugifyRepoName(localRepoName);
    if (!repoSlug) { setLocalError('Enter a valid repository name.'); return; }
    if (localFiles.length === 0) { setLocalError('Pick a folder with at least one deployable file.'); return; }
    setLocalBusy(true);
    setLocalError('');
    setLocalTokenNeeded(false);
    try {
      const apiUrl = getApiBase();
      const payloadFiles = [];
      for (let i = 0; i < localFiles.length; i++) {
        const f = localFiles[i];
        setLocalProgress(`Reading files… ${i + 1}/${localFiles.length}`);
        const buf = await f.file.arrayBuffer();
        payloadFiles.push({ path: f.path, contentBase64: arrayBufferToBase64(buf) });
      }
      setLocalProgress('Creating repository and pushing files…');
      const tokenToSend = (githubToken || '').trim();
      const headers = { 'Content-Type': 'application/json' };
      if (tokenToSend) headers['X-User-GitHub-Token'] = tokenToSend;

      const res = await authenticatedFetch(`${apiUrl}/api/deploy/create-repo-from-upload`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          repoName: repoSlug,
          description: `Imported from local folder "${localFolderName}" via Lyric`,
          branch: 'main',
          files: payloadFiles,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok && data?.requiresGitHubToken) {
        setTokenSetup(data.tokenSetup || null);
        setLocalTokenNeeded(true);
        setLocalError(data.error || 'A GitHub Personal Access Token is required to create the repository.');
        return;
      }
      if (!res.ok) {
        const error = data?.error || `Failed to create repository (${res.status})`;
        throw new Error(data?.nextStep ? `${error}\n\nWhat to do: ${data.nextStep}` : error);
      }

      // Success — the repo now exists with the code. Continue as a normal repo
      // deploy: validateRepoByName loads branches and advances to 'validation'.
      const owner = data.repo?.owner;
      const repo = data.repo?.name;
      setLocalProgress(`Created ${data.repo?.fullName} — continuing…`);
      updateField('appDisplayName', formData.appDisplayName || repo);
      await validateRepoByName(owner, repo);
    } catch (err) {
      setLocalError(err.message);
    } finally {
      setLocalBusy(false);
      setLocalProgress('');
    }
  };

  // ── Voice-to-text for the app description textarea ────────────────────────
  const [interimTranscript, setInterimTranscript] = useState('');
  const dictationBaseRef = useRef('');
  // Which field the mic is currently dictating into. Both the build "describe"
  // textarea and the edit-phase "enhance" textarea share one dictation instance,
  // so final transcript chunks must be routed to the field whose mic was clicked.
  const dictationTargetRef = useRef('appDescription');
  const handleDictation = useCallback((text, { isFinal }) => {
    if (isFinal) {
      const chunk = text.trim();
      const appendTo = (base) => {
        const b = base || '';
        const sep = b && !/\s$/.test(b) ? ' ' : '';
        return `${b}${sep}${chunk} `;
      };
      if (dictationTargetRef.current === 'enhanceDescription') {
        setEnhanceDescription((prev) => {
          const next = appendTo(prev);
          dictationBaseRef.current = next;
          return next;
        });
      } else {
        setFormData((prev) => {
          const next = appendTo(prev.appDescription);
          dictationBaseRef.current = next;
          return { ...prev, appDescription: next };
        });
      }
      setInterimTranscript('');
    } else {
      setInterimTranscript(text);
    }
  }, []);
  const dictation = useDictation({ onResult: handleDictation });
  // Snapshot current text when dictation starts so interim previews append to it
  // cleanly instead of replacing it.
  useEffect(() => {
    if (dictation.listening) {
      dictationBaseRef.current = (dictationTargetRef.current === 'enhanceDescription'
        ? enhanceDescription
        : formData.appDescription) || '';
    } else {
      setInterimTranscript('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dictation.listening]);

  // Branch-aware deploy: when the user reaches the Review step, fetch their
  // existing deployed apps for THIS repo so we can offer "replace an existing
  // app" vs "create a new app". Only the repo deploy path uses this (the edit
  // path already targets a specific app; build-from-scratch has no repo yet).
  useEffect(() => {
    if (step !== 'review' || path !== 'repo') return;
    if (!formData.owner || !formData.repo) return;
    let cancelled = false;
    (async () => {
      const apiUrl = getApiBase();
      // Reset to the pending state on every (re)fetch so the Review step blocks
      // Deploy until we KNOW whether a conflicting app exists — otherwise a slow
      // or failed list call would look like "no apps" and silently clobber an
      // existing deployment via legacy name-derivation.
      if (!cancelled) { setExistingRepoAppsLoaded(false); setExistingRepoAppsError(''); }
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/deploy/list`);
        if (!res.ok) {
          // Non-2xx (expired auth, server error, …) must NOT be treated as
          // "no apps exist" — surface it and let Review block the deploy.
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `Could not load your apps (HTTP ${res.status})`);
        }
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        // GitHub owner/repo identifiers are case-insensitive, so compare lower.
        const owner = formData.owner.toLowerCase();
        const repo = formData.repo.toLowerCase();
        const mine = (data.apps || []).filter(a =>
          a.isOwner &&
          (a.owner || '').toLowerCase() === owner &&
          (a.repo || '').toLowerCase() === repo
        );
        setExistingRepoApps(mine);
        // Default choice: if one of my apps is already on the selected branch,
        // default to replacing THAT app (a straight redeploy). Otherwise default
        // to creating a new app so we never silently clobber a different branch.
        const sameBranch = mine.find(a => a.branch === formData.branch);
        if (mine.length === 0) {
          setDeployMode('new');
          setReplaceTargetApp('');
        } else if (sameBranch) {
          setDeployMode('replace');
          setReplaceTargetApp(sameBranch.appName);
        } else {
          setDeployMode('new');
          setReplaceTargetApp(mine[0].appName);
        }
        setNewAppDisplayName(
          `${(formData.appDisplayName || formData.repo).trim()} (${formData.branch})`
        );
        setExistingRepoAppsLoaded(true);
      } catch (err) {
        if (cancelled) return;
        // Failure path: record the reason and mark "loaded" so Review can show a
        // clear error + retry instead of hanging in the pending state forever.
        setExistingRepoApps([]);
        setExistingRepoAppsError(err.message || 'Could not load your existing apps');
        setExistingRepoAppsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, path, formData.owner, formData.repo, formData.branch, existingReloadNonce]);

  // Fetch the repo's .env files whenever the user reaches the config step (or
  // changes repo/branch/subdir). Powers the environment picker next to Deploy
  // Target. Failures leave the list empty (dropdown shows the placeholder) —
  // the picker is optional and must never block a deploy.
  useEffect(() => {
    if (step !== 'config') return;
    if (!formData.owner || !formData.repo) return;
    let cancelled = false;
    (async () => {
      const apiUrl = getApiBase();
      if (!cancelled) { setEnvFilesLoading(true); setEnvFilesError(''); }
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/deploy/env-files`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            owner: formData.owner,
            repo: formData.repo,
            branch: formData.branch,
            subdir: formData.stackInfo?.subdir || '',
          }),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `Could not load .env files (HTTP ${res.status})`);
        }
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        const files = Array.isArray(data.files) ? data.files : [];
        setEnvFiles(files);
        // If the previously-selected file is no longer present (branch changed,
        // file renamed), clear the stale selection so we don't submit a ghost.
        if (formData.selectedEnvFile && !files.includes(formData.selectedEnvFile)) {
          updateField('selectedEnvFile', '');
        }
      } catch (err) {
        if (cancelled) return;
        setEnvFiles([]);
        setEnvFilesError(err.message || 'Could not load .env files');
      } finally {
        if (!cancelled) setEnvFilesLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, formData.owner, formData.repo, formData.branch, formData.stackInfo?.subdir]);

  // Load the list of existing AWS secrets so the user can reuse one instead of creating new
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSecretsLoading(true);
      setSecretsLoadError('');
      setSecretsDiagnostic(null);
      const apiUrl = getApiBase();
      const secretsUrl = process.env.REACT_APP_SECRET_SERVER || apiUrl;
      const fullUrl = `${secretsUrl}/api/app-secrets`;
      console.log('[SECRETS-FETCH] GET', fullUrl);
      try {
        const res = await authenticatedFetch(fullUrl);
        const contentType = res.headers.get('content-type') || '(none)';
        console.log('[SECRETS-FETCH] response status=', res.status, 'content-type=', contentType);
        const rawText = await res.text();
        let data = {};
        try { data = JSON.parse(rawText); } catch { /* not JSON */ }
        if (!res.ok) {
          if (!cancelled) setSecretsLoadError(data.error || `HTTP ${res.status}`);
          return;
        }
        if (!cancelled) {
          setExistingSecrets(Array.isArray(data.secrets) ? data.secrets : []);
          setSecretsDiagnostic({
            totalCount: data.totalCount,
            sampleNames: data.sampleNames || [],
            region: data.region,
            prefix: data.prefix,
            fetchedUrl: fullUrl,
            responseStatus: res.status,
            responseContentType: contentType,
            looksLikeHtml: rawText.trimStart().startsWith('<'),
          });
        }
      } catch (err) {
        if (!cancelled) setSecretsLoadError(err.message || 'Network error');
      } finally { if (!cancelled) setSecretsLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the user picks a specific existing secret (via list OR manual entry), fetch
  // the (masked) keys it contains so we can show what's there and flag missing required keys.
  // Manual entry is debounced 500ms so we don't fire on every keystroke.
  useEffect(() => {
    if (secretMode !== 'existing') {
      setExistingSecretKeys([]);
      setExistingSecretError('');
      setMissingKeyValues({});
      setEditingSecretKeys({});
      setCustomSecretVars([]);
      return;
    }
    const effective = (existingSource === 'manual' ? manualSecretName : selectedExistingSecret).trim();
    if (!effective) {
      setExistingSecretKeys([]);
      setExistingSecretError('');
      setMissingKeyValues({});
      setEditingSecretKeys({});
      setCustomSecretVars([]);
      return;
    }
    let cancelled = false;
    const delay = existingSource === 'manual' ? 500 : 0;
    const timer = setTimeout(async () => {
      if (cancelled) return;
      setExistingSecretLoading(true);
      setExistingSecretError('');
      try {
        const apiUrl = getApiBase();
        const secretsUrl = process.env.REACT_APP_SECRET_SERVER || apiUrl;
        const res = await authenticatedFetch(`${secretsUrl}/api/app-secrets/${encodeURIComponent(effective)}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (!cancelled) setExistingSecretError(data.error || `HTTP ${res.status}`);
          return;
        }
        if (!cancelled) {
          setExistingSecretKeys(Array.isArray(data.keys) ? data.keys : []);
          setMissingKeyValues({});
          setEditingSecretKeys({});
          setCustomSecretVars([]);
        }
      } catch (err) {
        if (!cancelled) setExistingSecretError(err.message || 'Network error');
      } finally {
        if (!cancelled) setExistingSecretLoading(false);
      }
    }, delay);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secretMode, existingSource, selectedExistingSecret, manualSecretName]);

  // Visual edit: receive element-selection messages posted by the overlay the
  // /apps proxy injects into the iframed live app (?__lyricEdit=1). We only act
  // on our own tagged messages, and only from the app's origin (the API origin
  // in dev, same-origin in prod).
  useEffect(() => {
    let appOrigin = null;
    const apiUrl = getApiBase();
    try { appOrigin = new URL(apiUrl).origin; } catch { /* ignore */ }
    const onMsg = (e) => {
      if (!e.data || e.data.__lyricVisualEdit !== true) return;
      if (appOrigin && e.origin !== appOrigin && e.origin !== window.location.origin) return;
      // Only trust messages posted by the visual-editor iframe itself — a matching
      // origin isn't enough, since any other same-origin window/tab could forge a
      // __lyricVisualEdit message and mutate the Visual Edit state.
      const editorWin = visualIframeRef.current && visualIframeRef.current.contentWindow;
      if (editorWin && e.source !== editorWin) return;
      const d = e.data;
      if (d.type === 'select') {
        setVisualSelected(d.anchor || null);
        setVisualInstruction('');
      } else if (d.type === 'mode') {
        setVisualMode(d.mode === 'navigate' ? 'navigate' : (d.mode === 'draw' ? 'draw' : 'edit'));
      } else if (d.type === 'sketchCount') {
        // The overlay reports how many marks are drawn but not yet staged.
        setVisualSketchCount(Number(d.count) || 0);
      } else if (d.type === 'ready') {
        // A fresh overlay (initial load or in-app navigation) always starts in
        // edit mode with a clean selection; re-sync the toggle and re-apply the
        // user's red-nav-circle preference (a new page defaults it back on).
        setVisualMode('edit');
        setVisualSelected(null);
        setVisualSketchCount(0);
        try {
          const win = visualIframeRef.current && visualIframeRef.current.contentWindow;
          if (win && visualNavLinksRef.current === false) {
            win.postMessage({ __lyriccmd: true, type: 'apply', op: { kind: 'navBadges', on: false } }, visualAppOrigin);
          }
        } catch { /* ignore */ }
      } else if (d.type === 'navigating') {
        // The overlay is loading another page (red nav circle clicked) — drop the
        // stale selection so the panel doesn't point at an element that's gone.
        setVisualSelected(null);
      } else if (d.type === 'pendingAdd') {
        // A new element is floating in the preview waiting to be positioned and
        // placed. Clear the selection panel so it doesn't show a stale element's
        // edit box while the user drags the ghost into position.
        if (d.active) { setVisualSelected(null); setVisualInstruction(''); }
      } else if (d.type === 'undo' && d.opId) {
        // The overlay reverted its last manipulation — drop the matching entry.
        setVisualChanges(prev => prev.filter(c => c.opId !== d.opId));
      } else if (d.type === 'sketchRemove' && d.sketchId) {
        // A drawn mark (and its attached rule) was deleted in the canvas.
        setVisualChanges(prev => prev.filter(c => c.opId !== d.sketchId));
      } else if (d.type === 'sketchClear') {
        // Clear only marks drawn on the currently loaded page.
        const clearedSketchIds = new Set(Array.isArray(d.sketchIds) ? d.sketchIds : []);
        setVisualChanges(prev => prev.filter(c => !(c.op && c.op.kind === 'sketch' && clearedSketchIds.has(c.op.sketchId))));
      } else if (d.type === 'op' && d.op && d.op.kind === 'sketch' && d.op.sketchId) {
        // A drawn mark is a persistent, editable instruction: upsert ONE change
        // entry per mark (keyed by sketchId) so drawing it, then typing/editing
        // its rule, all map to the same entry instead of piling up.
        const op = d.op;
        setVisualChanges(prev => {
          const entry = { id: op.sketchId, opId: op.sketchId, kind: 'manual', op, label: manualOpLabel(op) };
          const idx = prev.findIndex(c => c.opId === op.sketchId);
          if (idx >= 0) { const copy = [...prev]; copy[idx] = entry; return copy; }
          return [...prev, entry];
        });
      } else if (d.type === 'op' && d.op && d.op.kind) {
        // A manual manipulation (text/move/add/delete/duplicate/style/nudge)
        // happened in the iframe — journal it for the enhance PR.
        const op = d.op;
        // Runs of the same continuous adjustment (color/font-size picker drag,
        // repeated arrow-key nudges) collapse into a single change entry.
        const keyOf = (o) => {
          if (!o || !o.anchor) return null;
          if (o.kind === 'style') return `style|${o.prop}|${o.anchor.uid}`;
          if (o.kind === 'nudge') return `nudge|${o.anchor.uid}`;
          if (o.kind === 'reposition') return `reposition|${o.anchor.uid}`;
          return null;
        };
        setVisualChanges(prev => {
          const k = keyOf(op);
          if (k && prev.length) {
            const last = prev[prev.length - 1];
            if (last && last.kind === 'manual' && last.op && keyOf(last.op) === k) {
              // Nudge deltas ACCUMULATE so the collapsed change carries the total
              // offset from the element's natural position (matching the enhance
              // prompt wording); other collapsible ops just keep the latest value.
              const mergedOp = (op.kind === 'nudge' && last.op.kind === 'nudge')
                ? { ...op, dx: (last.op.dx || 0) + (op.dx || 0), dy: (last.op.dy || 0) + (op.dy || 0) }
                : op;
              const merged = { ...last, op: mergedOp, opId: mergedOp.opId || last.opId, label: manualOpLabel(mergedOp) };
              return [...prev.slice(0, -1), merged];
            }
          }
          return [...prev, {
            id: op.opId || `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            opId: op.opId || null,
            kind: 'manual',
            op,
            label: manualOpLabel(op),
          }];
        });
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // Editor-style keyboard while the Visual Studio is open, forwarded to the
  // overlay: Cmd/Ctrl+Z undo, and arrow keys to nudge the selected element.
  // Skipped when typing in a field so text editing still works there.
  useEffect(() => {
    if (!visualStudioOpen) return;
    const postCmd = (msg) => {
      try {
        const win = visualIframeRef.current && visualIframeRef.current.contentWindow;
        if (win) win.postMessage(msg, visualAppOrigin);
      } catch { /* ignore */ }
    };
    const onKey = (e) => {
      const t = e.target;
      const tag = t && t.tagName ? t.tagName.toUpperCase() : '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (t && t.isContentEditable)) return;
      if ((e.key === 'z' || e.key === 'Z') && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
        e.preventDefault();
        postCmd({ __lyriccmd: true, type: visualMode === 'draw' ? 'apply' : 'undo', ...(visualMode === 'draw' ? { op: { kind: 'drawUndo' } } : {}) });
        return;
      }
      if (visualSelected && (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        const step = e.shiftKey ? 10 : 4;
        let dx = 0, dy = 0;
        if (e.key === 'ArrowLeft') dx = -step; else if (e.key === 'ArrowRight') dx = step; else if (e.key === 'ArrowUp') dy = -step; else dy = step;
        e.preventDefault();
        postCmd({ __lyriccmd: true, type: 'apply', op: { kind: 'nudge', dx, dy } });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visualStudioOpen, visualSelected, visualMode]);

  // When opened from a pinned card's Edit button, auto-run stack detection
  useEffect(() => {
    if (!initialEdit) return;
    const apiUrl = getApiBase();
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/deploy/detect-stack`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ owner: initialEdit.owner, repo: initialEdit.repo, branch: initialEdit.branch }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Stack detection failed');
        setFormData(prev => ({
          ...prev,
          stackInfo: data,
          buildCommand: data.suggestedBuildCommand || '',
          startCommand: data.suggestedStartCommand || '',
        }));
        // Show only what the code actually requires (never .env.example);
        // reuse any values the user already saved for keys still present.
        try {
          const saved = localStorage.getItem(`lyric-env-${initialEdit.owner}/${initialEdit.repo}`);
          const savedValues = saved ? Object.fromEntries(JSON.parse(saved).map(e => [e.key, e.value])) : {};
          // Prefer the persisted edit subdir over `data.subdir` — this
          // detect-stack call is unscoped, so in a monorepo its own guess
          // can point at the wrong nested app.
          const codeVars = await fetchCodeEnvVars(initialEdit.owner, initialEdit.repo, initialEdit.branch, initialEdit.subdir ?? data.subdir);
          const merged = codeVars === null
            ? (saved ? JSON.parse(saved) : [])
            : codeVars.map(v => ({ key: v.key, value: savedValues[v.key] || '' }));
          if (merged.length > 0) { setEnhanceEnvVars(merged); setShowEnhanceEnvVars(true); }
        } catch { /* ignore */ }
        setStep('enhance');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll deploy status when on the deployed step
  useEffect(() => {
    if (step !== 'deployed' || !deployResult?.appName) return;
    setDeployLiveStatus('deploying');
    setDeployLiveStage('cloning');
    setDeployLiveError(null);
    setDeployElapsed(0);

    deployTimerRef.current = setInterval(() => setDeployElapsed(e => e + 1), 1000);

    const appName = deployResult.appName;
    const doPoll = async () => {
      try {
        const apiUrl = getApiBase();
        const res = await authenticatedFetch(`${apiUrl}/api/deploy/status/${encodeURIComponent(appName)}`);
        if (!res.ok) return;
        const data = await res.json();
        console.log('[DEPLOY POLL]', appName, data);
        if (data.status) setDeployLiveStatus(data.status);
        if (data.stage) setDeployLiveStage(data.stage);
        if (data.error) setDeployLiveError(data.error);
        if (data.status === 'running') {
          // Pin automatically the moment the app is live — the success screen
          // no longer carries a manual "Add to Launch Screen" button. Mirrors
          // the rich entry the button used to write (full config for one-click
          // redeploy), and is idempotent on repeated polls.
          try {
            const pins = JSON.parse(localStorage.getItem('lyric-pinned-deployments') || '[]');
            const entry = {
              appName,
              displayName: formData.appDisplayName || formData.repo,
              owner: deployResult?.config?.owner || formData.owner,
              repo: deployResult?.config?.repo || formData.repo,
              branch: deployResult?.config?.branch || formData.branch,
              url: deployResult?.url || data.url || '',
              port: deployResult?.config?.port,
              deployId: deployResult?.deployId || '',
              pinnedAt: new Date().toISOString(),
              startCommand: formData.startCommand || '',
              buildCommand: formData.buildCommand || '',
              subdir: formData.stackInfo?.subdir || '',
              envVarKeys: (configAnalysis?.envVars || []).map(v => v.key),
              savedToAws: true,
              secretName: resolveSecretName(formData.secretName || formData.repo),
            };
            localStorage.setItem('lyric-pinned-deployments', JSON.stringify([entry, ...pins.filter(p => p.appName !== entry.appName)]));
          } catch { /* ignore storage errors */ }
        }
        if (data.status === 'running' || data.status === 'error' || data.status === 'stopped') {
          clearInterval(deployPollRef.current);
          clearInterval(deployTimerRef.current);
        }
      } catch (err) {
        console.warn('[DEPLOY POLL] fetch failed:', err.message);
      }
    };

    doPoll(); // check immediately on mount
    deployPollRef.current = setInterval(doPoll, 3000);

    return () => {
      clearInterval(deployPollRef.current);
      clearInterval(deployTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, deployResult?.appName]);

  // Fetch available ports and Okta groups when entering the config step
  useEffect(() => {
    if (step !== 'config') return;
    const apiUrl = getApiBase();
    (async () => {
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/deploy/groups`);
        if (!res.ok) return;
        const data = await res.json();
        setAvailableGroups(data.groups || []);
        setGroupsDemoMode(!!data.isDemoMode);
      } catch { /* groups unavailable */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Edit flow lands on `enhance` (not `config`), so groups must be fetched
  // there too — otherwise the "Specific Groups" picker shows zero options.
  useEffect(() => {
    if (step !== 'enhance') return;
    const apiUrl = getApiBase();
    (async () => {
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/deploy/groups`);
        if (!res.ok) return;
        const data = await res.json();
        setAvailableGroups(data.groups || []);
        setGroupsDemoMode(!!data.isDemoMode);
      } catch { /* groups unavailable */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Reset the build stopwatch whenever we (re)enter the building screen.
  useEffect(() => {
    setBuildElapsed(0);
    buildPhaseKeyRef.current = null;
    buildPhaseStartRef.current = 0;
  }, [step]);

  // Tick the shared stopwatch once per second while a build OR an edit/enhance
  // is actively running. It pauses while we wait on the user for credentials
  // and stops once the operation finishes or errors out.
  useEffect(() => {
    const buildingActive = step === 'building'
      && deployPhase !== 'done'
      && deployPhase !== 'error'
      && scaffoldPhase !== 'error'
      && buildEnvPhase !== 'waiting';
    const enhancingActive = step === 'enhancing'
      && enhancePhase !== 'done'
      && enhancePhase !== 'error'
      && enhancePhase !== 'clarify';
    if (!buildingActive && !enhancingActive) return undefined;
    const id = setInterval(() => setBuildElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [step, deployPhase, scaffoldPhase, buildEnvPhase, enhancePhase]);

  const parseRepoUrl = (url) => {
    // Matches:
    //   https://github.com/owner/repo
    //   https://github.com/owner/repo/tree/<branch>
    //   https://github.com/owner/repo/tree/<branch>/<path>
    // Branch can contain slashes (e.g. feature/foo), so capture greedily up to the
    // next /tree-style segment or end-of-path indicators.
    const match = url.match(/github\.com\/([^/]+)\/([^/.\s]+)(?:\/tree\/([^?#\s]+?))?(?:\/(?:blob|commits|releases|issues|pulls)|[?#]|$)/);
    if (!match) {
      // Fall back to bare owner/repo match for URLs without trailing slashes
      const bare = url.match(/github\.com\/([^/]+)\/([^/.\s]+)/);
      return bare ? { owner: bare[1], repo: bare[2], branchHint: null } : null;
    }
    return { owner: match[1], repo: match[2], branchHint: match[3] || null };
  };

  const validateRepo = async () => {
    setLoading(true);
    setError('');
    try {
      const parsed = parseRepoUrl(formData.repoUrl);
      if (!parsed) {
        setError('Invalid GitHub URL. Expected: https://github.com/owner/repo');
        setLoading(false);
        return;
      }

      const apiUrl = getApiBase();
      const res = await authenticatedFetch(`${apiUrl}/api/deploy/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owner: parsed.owner, repo: parsed.repo }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Repository not accessible (${res.status})`);
      }

      const data = await res.json();
      localStorage.setItem(LAST_REPO_KEY, formData.repoUrl.trim());
      const branchList = data.branches || [];
      // Branch selection precedence:
      //   1. URL hint (.../tree/<branch>) when that branch actually exists.
      //   2. 'main' if present (the common case for the default branch).
      //   3. 'master' if present (older repos).
      //   4. Empty — force an explicit pick.
      const initialBranch =
        (parsed.branchHint && branchList.includes(parsed.branchHint))
          ? parsed.branchHint
          : branchList.includes('main')
            ? 'main'
            : branchList.includes('master')
              ? 'master'
              : '';
      setFormData((prev) => ({
        ...prev,
        owner: parsed.owner,
        repo: parsed.repo,
        repoInfo: data.repoInfo,
        branches: branchList,
        branch: initialBranch,
        appDisplayName: prev.appDisplayName || parsed.repo,
      }));
      setStep('validation');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Two-stage detection in monorepos:
  //   1. First call with no subdir → backend returns the shallowest app's
  //      stack PLUS a `candidateApps` list of every deployable subdir found.
  //   2. If there's more than one candidate, the UI bounces to the 'pickApp'
  //      step, the user chooses one, and detectStack is invoked again with
  //      that subdir to re-run detection scoped to that single app.
  //
  // For repos with 0–1 candidates the second call never happens and behavior
  // is identical to before.
  const detectStack = async (targetSubdir = undefined) => {
    setLoading(true);
    setError('');
    try {
      const apiUrl = getApiBase();
      const res = await authenticatedFetch(`${apiUrl}/api/deploy/detect-stack`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner: formData.owner,
          repo: formData.repo,
          branch: formData.branch,
          ...(targetSubdir !== undefined ? { subdir: targetSubdir } : {}),
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Stack detection failed');
      }

      const data = await res.json();

      // Monorepo branch: multiple apps detected and we haven't scoped yet.
      // Stash the candidate list, send the user to the picker, skip the
      // commands/port pre-fill (that'll happen once they pick).
      const isInitialPass = targetSubdir === undefined;
      const hasMultipleCandidates = Array.isArray(data.candidateApps) && data.candidateApps.length > 1;
      if (isInitialPass && hasMultipleCandidates && path !== 'edit') {
        setFormData((prev) => ({ ...prev, stackInfo: data }));
        // Do not guess for the user. A repo-root app and a nested app can be
        // separate halves of one system, so require an explicit informed choice.
        setPickedCandidate(null);
        setStep('pickApp');
        return;
      }

      setFormData((prev) => ({
        ...prev,
        stackInfo: data,
        buildCommand: data.suggestedBuildCommand || '',
        startCommand: data.suggestedStartCommand || '',
      }));
      // Default-on every compliance finding so the safe choice is "fix it"
      const findings = Array.isArray(data.complianceFindings) ? data.complianceFindings : [];
      if (findings.length > 0) {
        setApprovedFixes(Object.fromEntries(
          findings.filter(f => f.autoFixable === true).map(f => [f.id, true])
        ));
      } else {
        setApprovedFixes({});
      }
      // Force user to re-acknowledge blockers if any exist on this fresh detection
      setOverrideBlockers(false);
      // Reset the discovered-endpoint check for the newly detected app.
      setEndpointTests({});
      setEndpointResults(null);
      setEndpointMeta(null);
      setEndpointError('');
      if (path === 'edit') {
        // Show only what the code actually requires (never .env.example);
        // reuse any values the user already saved for keys still present.
        try {
          const saved = localStorage.getItem(`lyric-env-${formData.owner}/${formData.repo}`);
          const savedValues = saved ? Object.fromEntries(JSON.parse(saved).map(e => [e.key, e.value])) : {};
          // Prefer the persisted edit subdir over `data.subdir` when this was
          // an unscoped detection pass — a monorepo's own guess can point at
          // the wrong nested app.
          const codeVars = await fetchCodeEnvVars(formData.owner, formData.repo, formData.branch, initialEdit?.subdir ?? data.subdir);
          const merged = codeVars === null
            ? (saved ? JSON.parse(saved) : [])
            : codeVars.map(v => ({ key: v.key, value: savedValues[v.key] || '' }));
          if (merged.length > 0) {
            setEnhanceEnvVars(merged);
            setShowEnhanceEnvVars(true);
          }
        } catch { /* ignore */ }
        setStep('enhance');
      } else {
        setStep('stack');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Discovered-endpoint network check helpers ──────────────────────────
  // Stable id for an endpoint row (matches what we send to the server as
  // `label`, so results line back up with the discovered list).
  const endpointId = (e) => `${e.kind}:${e.value}`;

  // Lazily fetch-or-create the per-endpoint test config.
  const getEndpointTest = (e) => endpointTests[endpointId(e)] || emptyEndpointTest();

  const setEndpointTest = (e, patch) => {
    const id = endpointId(e);
    setEndpointTests((curr) => {
      const base = curr[id] || emptyEndpointTest();
      return { ...curr, [id]: { ...base, ...patch } };
    });
  };
  const setEndpointAuth = (e, patch) => {
    const id = endpointId(e);
    setEndpointTests((curr) => {
      const base = curr[id] || emptyEndpointTest();
      return { ...curr, [id]: { ...base, auth: { ...base.auth, ...patch } } };
    });
  };
  const setEndpointCustomHeader = (e, headerIdx, field, value) => {
    const id = endpointId(e);
    setEndpointTests((curr) => {
      const base = curr[id] || emptyEndpointTest();
      const custom = (base.auth.custom || []).map((h, hi) => (hi === headerIdx ? { ...h, [field]: value } : h));
      return { ...curr, [id]: { ...base, auth: { ...base.auth, custom } } };
    });
  };
  const addEndpointCustomHeader = (e) => {
    const id = endpointId(e);
    setEndpointTests((curr) => {
      const base = curr[id] || emptyEndpointTest();
      const custom = [...(base.auth.custom || []), { name: '', value: '' }];
      return { ...curr, [id]: { ...base, auth: { ...base.auth, custom } } };
    });
  };

  // Probe every selected, auto-probeable endpoint from inside Lyric's pod.
  // Reuses the shared netcheck transport via POST /api/deploy/netcheck.
  const runEndpointCheck = async () => {
    const stack = formData.stackInfo || {};
    const discovered = Array.isArray(stack.discoveredEndpoints) ? stack.discoveredEndpoints : [];

    // Build the targets list from selected rows that have a concrete probe —
    // either auto-detected (suggestedProbe) or a URL the user pasted for an
    // env-var / runtime-built endpoint whose value the scanner couldn't see.
    const targets = [];
    for (const e of discovered) {
      const t = getEndpointTest(e);
      const probe = e.suggestedProbe || parseManualProbe(t.manualUrl);
      if (!t.selected || !probe) continue;
      const headers = buildEndpointHeaders(t.auth);
      targets.push({
        host: probe.host,
        port: probe.port,
        scheme: probe.scheme,
        label: endpointId(e),
        ...(headers ? { headers } : {}),
      });
    }

    if (targets.length === 0) {
      setEndpointError('Select at least one endpoint to verify — paste a URL for env-var / runtime-built endpoints so they can be probed.');
      return;
    }

    setEndpointChecking(true);
    setEndpointError('');
    setEndpointResults(null);
    setEndpointMeta(null);
    try {
      const apiUrl = getApiBase();
      const res = await authenticatedFetch(`${apiUrl}/api/deploy/netcheck`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner: formData.owner,
          repo: formData.repo,
          targets,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Reachability check failed (HTTP ${res.status})`);
      setEndpointResults(Array.isArray(data.results) ? data.results : []);
      setEndpointMeta(data.meta || null);
    } catch (err) {
      setEndpointError(err.message);
    } finally {
      setEndpointChecking(false);
    }
  };

  // Resolve which AWS secret the deploy will read from, given the picker state.
  // For 'new' mode, fall back to the supplied default (e.g. lyric-<repo>).
  const resolveSecretName = (defaultName) => {
    if (secretMode === 'existing') {
      const fromExisting = existingSource === 'manual' ? manualSecretName : selectedExistingSecret;
      return (fromExisting || '').trim();
    }
    return (defaultName || '').trim();
  };

  // Write the supplied env vars to AWS Secrets Manager iff the user is creating a new secret.
  // Returns true on success, false on failure. 'existing' mode is a no-op (returns true).
  const writeSecretIfNew = async (secretName, vars) => {
    if (secretMode !== 'new') return true;
    const filled = (vars || []).filter(v => v.key?.trim() && (v.value ?? '').trim());
    if (filled.length === 0) return true; // nothing to save
    try {
      const apiUrl = getApiBase();
      const secretsUrl = process.env.REACT_APP_SECRET_SERVER || apiUrl;
      const res = await authenticatedFetch(`${secretsUrl}/api/app-secrets/${encodeURIComponent(secretName)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vars: filled.map(v => ({ key: v.key, value: v.value })) }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.warn('[SECRETS] AWS save failed:', data.error || res.status);
        return false;
      }
      return true;
    } catch (err) {
      console.warn('[SECRETS] AWS save error:', err.message);
      return false;
    }
  };

  // Merge new keys into an existing AWS secret. Returns true on success.
  const mergeIntoExistingSecret = async (secretName, vars) => {
    const filled = (vars || []).filter(v => v.key?.trim() && (v.value ?? '').trim());
    if (filled.length === 0) return true;
    try {
      const apiUrl = getApiBase();
      const secretsUrl = process.env.REACT_APP_SECRET_SERVER || apiUrl;
      const res = await authenticatedFetch(`${secretsUrl}/api/app-secrets/${encodeURIComponent(secretName)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vars: filled.map(v => ({ key: v.key, value: v.value })) }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.warn('[SECRETS] PATCH failed:', data.error || res.status);
        return false;
      }
      return true;
    } catch (err) {
      console.warn('[SECRETS] PATCH error:', err.message);
      return false;
    }
  };

  // Build the list of entries to merge into the selected existing secret:
  // missing/edited required keys (missingKeyValues) plus any freeform
  // key/value pairs the user added via "+ Add another variable".
  const collectMissingKeyMergeVars = () => {
    const fromExisting = Object.entries(missingKeyValues)
      .filter(([k, v]) => k && (v ?? '').trim())
      .map(([key, value]) => ({ key, value }));
    const fromCustom = customSecretVars
      .filter(v => v.key?.trim() && (v.value ?? '').trim())
      .map(v => ({ key: v.key.trim(), value: v.value }));
    return [...fromExisting, ...fromCustom];
  };

  // Renders the "Create new / Use existing" AWS secret picker.
  // - `defaultName`: suggested name for the 'new' branch
  // - `requiredVars`: array of { key, isSecret?, description? } the app needs at runtime.
  //   In 'existing' mode we check each required key against the selected secret's contents
  //   and offer inline inputs for any that are missing.
  const renderSecretPicker = ({ defaultName, requiredVars = [] }) => {
    const requiredKeys = (requiredVars || []).map(v => v.key).filter(Boolean);
    const existingKeySet = new Set(existingSecretKeys.map(k => k.key));
    const missingRequired = requiredKeys.filter(k => !existingKeySet.has(k));
    // Deliberately does NOT surface keys already in the AWS secret beyond
    // requiredKeys ("also in secret") — a secret saved before a code change
    // can retain stale keys the app no longer reads, and showing them back
    // to the user contradicts "only what the code needs".
    // The effective name being inspected — list selection or manual entry.
    const effectiveExisting = (existingSource === 'manual' ? manualSecretName : selectedExistingSecret).trim();
    // Edit flow: guarantee the app's current secret is always an option so the
    // dropdown shows it selected even before the ListSecrets fetch resolves (or
    // if the AWS principal lacks secretsmanager:ListSecrets). No-op for new deploys.
    const editSecretName = (initialEdit?.secretName || '').trim();
    const optionSecrets = editSecretName && !existingSecrets.some(s => s.appName === editSecretName)
      ? [{ name: `tools/lyric/${editSecretName}`, appName: editSecretName }, ...existingSecrets]
      : existingSecrets;

    return (
      <div style={{ marginTop: 14, marginBottom: 14, padding: 12, backgroundColor: '#0f1419', border: '1px solid #2a3a4a', borderRadius: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#9fa8da', marginBottom: 8 }}>
          🔐 AWS Secrets Manager
        </div>

        {secretsLoadError && (
          <div style={{ marginBottom: 8, padding: '6px 10px', backgroundColor: '#3a1a1a', border: '1px solid #ef5350', borderRadius: 4, fontSize: 11, color: '#ef9a9a' }}>
            Could not load secrets list — {secretsLoadError}
          </div>
        )}

        <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#aaa', cursor: 'pointer' }}>
            <input type="radio" name="secret-mode" value="new" checked={secretMode === 'new'} onChange={() => setSecretMode('new')} />
            Create new
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#aaa', cursor: 'pointer' }}>
            <input type="radio" name="secret-mode" value="existing" checked={secretMode === 'existing'} onChange={() => setSecretMode('existing')} />
            Use existing
          </label>
        </div>

        {secretMode === 'new' ? (
          <div style={{ fontSize: 11, color: '#777' }}>
            Will save form values to <code style={{ color: '#7986cb' }}>tools/lyric/{(defaultName || 'app-name').trim() || 'app-name'}</code>
          </div>
        ) : (
          <>
            {/* Sub-toggle: pick from list (needs ListSecrets IAM) or type the name (only needs GetSecretValue) */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 6, fontSize: 11, color: '#888' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="radio" name="existing-source" value="list" checked={existingSource === 'list'} onChange={() => setExistingSource('list')} />
                Pick from list
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="radio" name="existing-source" value="manual" checked={existingSource === 'manual'} onChange={() => setExistingSource('manual')} />
                Type name
              </label>
            </div>

            {existingSource === 'manual' ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, color: '#777', whiteSpace: 'nowrap' }}>tools/lyric/</span>
                  <input
                    type="text"
                    value={manualSecretName}
                    onChange={(e) => setManualSecretName(e.target.value)}
                    placeholder="e.g. LyricJiraTool"
                    style={{ flex: 1, padding: '6px 8px', backgroundColor: '#1a1f2a', color: '#ccc', border: '1px solid #2a3a4a', borderRadius: 4, fontSize: 12, fontFamily: 'monospace' }}
                  />
                </div>
                <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>
                  Useful when you know the secret name but don't have <code>secretsmanager:ListSecrets</code> IAM permission. We verify it exists via <code>GetSecretValue</code>.
                </div>
              </div>
            ) : (
              <select
                value={selectedExistingSecret}
                onChange={(e) => setSelectedExistingSecret(e.target.value)}
                style={{ width: '100%', padding: '6px 8px', backgroundColor: '#1a1f2a', color: '#ccc', border: '1px solid #2a3a4a', borderRadius: 4, fontSize: 12 }}
              >
                <option value="">
                  {secretsLoading ? 'Loading…' : optionSecrets.length === 0 ? '— No existing secrets found —' : '— Select an existing secret —'}
                </option>
                {optionSecrets.map(s => (
                  <option key={s.name} value={s.appName}>tools/lyric/{s.appName}</option>
                ))}
              </select>
            )}

            {/* Diagnostic info: when the dropdown is empty and the user picked LIST source, tell them *why* */}
            {existingSource === 'list' && !secretsLoading && existingSecrets.length === 0 && secretsDiagnostic && (
              <div style={{ marginTop: 8, padding: '8px 10px', backgroundColor: '#1a1a2a', border: '1px solid #5c6bc0', borderRadius: 4, fontSize: 11, color: '#9fa8da', lineHeight: 1.5 }}>
                <div style={{ marginBottom: 6, color: '#bbb' }}>
                  Fetched: <code style={{ color: '#ccc' }}>{secretsDiagnostic.fetchedUrl}</code> → HTTP {secretsDiagnostic.responseStatus} ({secretsDiagnostic.responseContentType})
                </div>
                {secretsDiagnostic.looksLikeHtml ? (
                  <div style={{ color: '#ef5350' }}>
                    <strong>Response was HTML, not JSON.</strong> The fetch is hitting the React dev server (port 3002) which serves <code>index.html</code> for unknown routes, instead of reaching Express on :3001. The new <code>/api/app-secrets</code> endpoint either isn't running yet (server not restarted) or your CRA proxy isn't forwarding this path. Try changing <code>REACT_APP_API_URL</code> in <code>.env</code> to <code>http://localhost:3001</code> and restart both server and client.
                  </div>
                ) : typeof secretsDiagnostic.totalCount !== 'number' || !secretsDiagnostic.region ? (
                  <div style={{ color: '#ffb74d' }}>
                    <strong>Server response missing diagnostic fields.</strong> Restart your Node server (kill PID on :3001 and re-run <code>npm run server</code>) and reload this page with cache disabled. The new <code>GET /api/app-secrets</code> response should include <code>region</code>, <code>totalCount</code>, <code>sampleNames</code>, and <code>prefix</code> fields.
                  </div>
                ) : (
                  <>
                    <div><strong>Diagnostic:</strong> AWS region <code style={{ color: '#ce93d8' }}>{secretsDiagnostic.region}</code> returned <strong>{secretsDiagnostic.totalCount}</strong> total secrets; <strong>0</strong> match prefix <code style={{ color: '#ce93d8' }}>{secretsDiagnostic.prefix}</code>.</div>
                    {secretsDiagnostic.totalCount === 0 ? (
                      <div style={{ marginTop: 4, color: '#ef9a9a' }}>The server's AWS principal can list secrets, but sees zero in this region. Likely wrong AWS account/region — check AWS_REGION and AWS_PROFILE in server's .env.</div>
                    ) : (
                      <div style={{ marginTop: 4 }}>Server sees other secrets but none start with <code>{secretsDiagnostic.prefix}</code>. Sample names returned: <code style={{ color: '#ccc' }}>{(secretsDiagnostic.sampleNames || []).join(', ') || '(none)'}</code></div>
                    )}
                  </>
                )}
              </div>
            )}

            {effectiveExisting && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 10, color: '#666', marginBottom: 4 }}>
                  Inspecting <code style={{ color: '#9fa8da' }}>tools/lyric/{effectiveExisting}</code>
                </div>
                {existingSecretLoading && (
                  <div style={{ fontSize: 11, color: '#777' }}>Loading secret contents…</div>
                )}
                {existingSecretError && (
                  <div style={{ padding: '6px 10px', backgroundColor: '#3a1a1a', border: '1px solid #ef5350', borderRadius: 4, fontSize: 11, color: '#ef9a9a' }}>
                    Could not load secret — {existingSecretError}
                  </div>
                )}
                {!existingSecretLoading && !existingSecretError && (
                  <>
                    {requiredKeys.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>App needs</div>
                        {requiredKeys.map(k => {
                          const present = existingKeySet.has(k);
                          const isEditing = !!editingSecretKeys[k];
                          return (
                            <div key={k} style={{ marginBottom: 4 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: present ? '#81c784' : '#ef9a9a' }}>
                                <span>{present ? '✓' : '✗'}</span>
                                <code>{k}</code>
                                <span style={{ color: '#666', fontSize: 10 }}>{present ? 'in secret' : 'missing'}</span>
                                {present && !isEditing && (
                                  <button
                                    type="button"
                                    onClick={() => setEditingSecretKeys(prev => ({ ...prev, [k]: true }))}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#90caf9', fontSize: 11, padding: 0 }}
                                  >
                                    ✏️ Edit
                                  </button>
                                )}
                              </div>
                              {present && isEditing && (
                                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                                  <input
                                    type={k.toLowerCase().match(/secret|token|password|key/) ? 'password' : 'text'}
                                    value={missingKeyValues[k] || ''}
                                    onChange={(e) => setMissingKeyValues(prev => ({ ...prev, [k]: e.target.value }))}
                                    placeholder="new value (leave blank to keep current)"
                                    style={{ flex: 1, padding: '4px 8px', backgroundColor: '#1a1f2a', color: '#ccc', border: '1px solid #2a3a4a', borderRadius: 3, fontSize: 11 }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingSecretKeys(prev => { const next = { ...prev }; delete next[k]; return next; });
                                      setMissingKeyValues(prev => { const next = { ...prev }; delete next[k]; return next; });
                                    }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef5350', fontSize: 11, padding: '0 4px' }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {missingRequired.length > 0 && (
                      <div style={{ marginTop: 8, padding: 8, backgroundColor: '#1a1a2a', border: '1px solid #5c6bc0', borderRadius: 4 }}>
                        <div style={{ fontSize: 11, color: '#9fa8da', marginBottom: 6 }}>
                          Add the missing keys (they'll be merged into <code>tools/lyric/{selectedExistingSecret}</code> on deploy):
                        </div>
                        {missingRequired.map(k => (
                          <div key={k} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                            <code style={{ minWidth: 140, fontSize: 11, color: '#ccc', alignSelf: 'center' }}>{k}</code>
                            <input
                              type={k.toLowerCase().match(/secret|token|password|key/) ? 'password' : 'text'}
                              value={missingKeyValues[k] || ''}
                              onChange={(e) => setMissingKeyValues(prev => ({ ...prev, [k]: e.target.value }))}
                              placeholder="value"
                              style={{ flex: 1, padding: '4px 8px', backgroundColor: '#1a1f2a', color: '#ccc', border: '1px solid #2a3a4a', borderRadius: 3, fontSize: 11 }}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {requiredKeys.length > 0 && missingRequired.length === 0 && (
                      <div style={{ marginTop: 6, fontSize: 11, color: '#81c784' }}>
                        All required keys are present.
                      </div>
                    )}

                    {/* Freeform additions — any extra credential beyond what the app declares as required. */}
                    <div style={{ marginTop: 10 }}>
                      {customSecretVars.map((v, i) => (
                        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'center' }}>
                          <input
                            type="text"
                            placeholder="KEY"
                            value={v.key}
                            onChange={(e) => setCustomSecretVars(prev => prev.map((row, j) => j === i ? { ...row, key: e.target.value } : row))}
                            style={{ flex: '0 0 40%', padding: '4px 8px', backgroundColor: '#1a1f2a', color: '#ccc', border: '1px solid #2a3a4a', borderRadius: 3, fontSize: 11, fontFamily: 'monospace' }}
                          />
                          <input
                            type={v.key.toLowerCase().match(/secret|token|password|key/) ? 'password' : 'text'}
                            placeholder="value"
                            value={v.value}
                            onChange={(e) => setCustomSecretVars(prev => prev.map((row, j) => j === i ? { ...row, value: e.target.value } : row))}
                            style={{ flex: 1, padding: '4px 8px', backgroundColor: '#1a1f2a', color: '#ccc', border: '1px solid #2a3a4a', borderRadius: 3, fontSize: 11 }}
                          />
<button
                            type="button"
                            aria-label={`Remove variable ${i + 1}`}
                            onClick={() => setCustomSecretVars(prev => prev.filter((_, j) => j !== i))}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef5350', fontSize: 14, padding: '0 4px' }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setCustomSecretVars(prev => [...prev, { key: '', value: '' }])}
                        style={{ background: 'none', border: '1px dashed #444', borderRadius: 4, cursor: 'pointer', color: '#a5d6a7', fontSize: 11, padding: '4px 10px' }}
                      >
                        + Add another variable
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const handleAnalyzeConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const apiUrl = getApiBase();
      const res = await authenticatedFetch(`${apiUrl}/api/deploy/analyze-config`, {
        method: 'POST',
        body: JSON.stringify({
          owner: formData.owner,
          repo: formData.repo,
          branch: formData.branch,
          // Scope env-var + config scan to the picked subdir for monorepo
          // deploys. Without this, analyze-config looks at the repo root and
          // returns "No secrets needed" for apps that actually need many.
          subdir: formData.stackInfo?.subdir || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Config analysis failed');

      setConfigAnalysis(data);
      setLyricEnvMatch(null);
      // Seed repoEnvVars with detected keys so the review step can render inputs
      setRepoEnvVars((data.envVars || []).map(v => ({ key: v.key, value: '' })));

      // Auto-check which env vars are already managed by AWS Secrets Manager (in Lyric's process.env)
      const allKeys = data.envVars?.map(e => e.key) || [];
      if (allKeys.length > 0) {
        setMatchingLyricEnv(true);
        try {
          const matchRes = await authenticatedFetch(`${apiUrl}/api/deploy/match-lyric-env`, {
            method: 'POST',
            body: JSON.stringify({ keys: allKeys }),
          });
          const matchData = await matchRes.json();
          if (matchRes.ok) {
            setLyricEnvMatch(matchData);
          }
        } catch (matchErr) {
          console.error('Failed to match Lyric env:', matchErr);
        } finally {
          setMatchingLyricEnv(false);
        }
      }

      setStep('requirements');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeploy = async () => {
    setLoading(true);
    setError('');
    try {
      const apiUrl = getApiBase();

      // Safety net: for the repo path, never deploy until we've confirmed the
      // user's existing apps loaded. Otherwise a slow/failed list could let us
      // fall back to legacy name-derivation and overwrite a running app. (The
      // Deploy button is already disabled in these states — this guards against
      // any programmatic call.)
      if (path === 'repo' && (!existingRepoAppsLoaded || existingRepoAppsError)) {
        throw new Error(existingRepoAppsError
          ? `Can't deploy — couldn't load your existing apps (${existingRepoAppsError}). Retry from the Review step.`
          : 'Still checking your existing apps — try again in a moment.');
      }

      // Skip AWS entirely when the form has no env vars to manage. We match the
      // UI rule exactly (renderRequirementsStep hides the picker on the same
      // condition), so deploy never tries to write a secret the user never saw.
      const filledVars = repoEnvVars.filter(e => e.key && e.value.trim());
      const skipSecret = repoEnvVars.length === 0;
      const requiredKeys = (configAnalysis?.envVars || [])
        .filter(envVar => envVar.required)
        .map(envVar => envVar.key);
      const availableKeys = new Set([
        ...filledVars.map(envVar => envVar.key),
        ...(lyricEnvMatch?.matched || []).map(envVar => envVar.key),
        ...(secretMode === 'existing' ? existingSecretKeys.map(envVar => envVar.key) : []),
        ...(secretMode === 'existing'
          ? Object.entries(missingKeyValues)
              .filter(([, value]) => String(value || '').trim())
              .map(([key]) => key)
          : []),
      ]);
      const missingRequiredKeys = requiredKeys.filter(key => !availableKeys.has(key));
      if (missingRequiredKeys.length > 0) {
        throw new Error(
          `Cannot deploy: required environment variable${missingRequiredKeys.length > 1 ? 's are' : ' is'} missing: `
          + `${missingRequiredKeys.join(', ')}. Enter ${missingRequiredKeys.length > 1 ? 'their values' : 'a value'} `
          + 'on the App Configuration step or choose an existing secret that contains them.'
        );
      }

      let secretName = '';
      if (!skipSecret) {
        // Resolve which AWS secret to use: a chosen existing one, or a new one named after the form.
        secretName = resolveSecretName(formData.secretName || formData.repo);
        if (!secretName) {
          throw new Error(secretMode === 'existing'
            ? 'Pick an existing AWS secret before deploying.'
            : 'Secret name is required.');
        }

        // 'new' mode: persist form values to AWS.
        // 'existing' mode: merge any user-entered missing keys into the chosen secret.
        if (secretMode === 'new') {
          const ok = await writeSecretIfNew(secretName, filledVars);
          if (!ok) throw new Error('Failed to save credentials to AWS Secrets Manager');
        } else {
          const merge = collectMissingKeyMergeVars();
          if (merge.length > 0) {
            const ok = await mergeIntoExistingSecret(secretName, merge);
            if (!ok) throw new Error(`Failed to update existing secret tools/lyric/${secretName}`);
          }
        }
      }

      const selectedFixes = Object.entries(approvedFixes)
        .filter(([id, on]) => on && (formData.stackInfo?.complianceFindings || [])
          .some(finding => finding.id === id && finding.autoFixable === true))
        .map(([id]) => id);
      let deployBranch = formData.branch;
      let preparedBranch = null;
      if (selectedFixes.length > 0) {
        const prepareRes = await authenticatedFetch(`${apiUrl}/api/deploy/prepare-compliance-branch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            owner: formData.owner,
            repo: formData.repo,
            branch: formData.branch,
            fixes: selectedFixes,
            subdir: formData.stackInfo?.subdir || '',
          }),
        });
        const prepareData = await prepareRes.json().catch(() => ({}));
        if (!prepareRes.ok) {
          throw new Error(prepareData.error || 'Could not create the compliance test branch');
        }
        preparedBranch = prepareData;
        deployBranch = prepareData.branch;
      }

      const res = await authenticatedFetch(`${apiUrl}/api/deploy/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner: formData.owner,
          repo: formData.repo,
          branch: deployBranch,
          // Branch-aware naming:
          //   • replace → target the existing app's exact name (redeploy in
          //     place; the new branch takes over that app's URL) and keep its
          //     display name so the card label doesn't churn.
          //   • new     → a distinct branch-suffixed appName so this branch runs
          //     alongside the others, with the user-editable display name.
          //   • no existing apps → legacy behaviour (server derives the name).
          ...(preparedBranch
            ? {
                appNameOverride: `lyric-${slugifyName(formData.repo)}-${slugifyName(deployBranch)}`,
                appDisplayName: `${formData.appDisplayName || formData.repo} (test fixes)`,
              }
            : deployMode === 'replace' && replaceTargetApp
            ? {
                appNameOverride: replaceTargetApp,
                appDisplayName:
                  (existingRepoApps.find(a => a.appName === replaceTargetApp)?.displayName)
                  || formData.appDisplayName || formData.repo,
              }
            : existingRepoApps.length > 0
            ? {
                appNameOverride: `lyric-${slugifyName(formData.repo)}-${slugifyName(formData.branch)}`,
                appDisplayName: (newAppDisplayName || `${formData.repo} (${formData.branch})`).trim(),
              }
            : { appDisplayName: formData.appDisplayName || formData.repo }),
          // Omit secretName entirely when there's no secret to manage
          ...(skipSecret ? {} : { secretName }),
          allowedGroups: formData.visibilityMode === 'specific' ? formData.allowedGroups : [],
          coAdmins: formData.coAdmins || [],
          allowedUsers: formData.allowedUsers || [],
          forwardOktaToken: !!formData.forwardOktaToken,
          ownerOnly: !!formData.ownerOnly,
          buildCommand: formData.buildCommand,
          startCommand: formData.startCommand,
          deployTarget: formData.deployTarget,
          selectedEnvFile: formData.selectedEnvFile || '',
          // Optional explicit version override. Blank → server auto-increments
          // the minor version (or starts a new app at 1.0.0).
          version: formData.version || '',
          subdir: formData.stackInfo?.subdir || '',
          envVars: skipSecret ? [] : (secretMode === 'new' ? filledVars : []),
          useLyricEnv: !skipSecret,
          lyricEnvKeys: (configAnalysis?.envVars || []).map(v => v.key),
          requiredEnvKeys: requiredKeys,
          // Approved fixes are already committed to deployBranch. The deployed
          // code must exactly match the branch a developer can review.
          applyFixes: [],
          // Carry the approved fix IDs through even though they're not being
          // re-applied here — deployStartHandler uses this (not applyFixes) to
          // know a fastapi-mount-spa fix was approved, so a frontend build
          // failure on this already-fixed branch still hard-fails instead of
          // silently shipping an API-only app.
          selectedFixIds: selectedFixes,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Deployment failed');
      }

      const data = await res.json();
      setDeployResult(preparedBranch ? { ...data, complianceBranch: preparedBranch } : data);
      setStep('deployed');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Progress bar ---
  const renderProgress = () => {
    const steps = path === 'build' ? BUILD_STEPS : path === 'edit' ? EDIT_STEPS : path === 'repo' ? REPO_STEPS : ['choose'];
    const visibleSteps = isIosBuild ? steps.filter((s) => s !== 'services') : steps;
    const currentIdx = visibleSteps.indexOf(step);
    return (
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, width: '100%' }}>
        {visibleSteps.map((s, i) => {
          const isActive = i === currentIdx;
          const isComplete = i < currentIdx;
          return (
            <div
              key={s}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '8px 4px',
                fontSize: 12,
                fontWeight: isActive ? 700 : 400,
                color: isActive ? '#4fc3f7' : isComplete ? '#81c784' : '#888',
                borderBottom: `3px solid ${isActive ? '#4fc3f7' : isComplete ? '#81c784' : '#444'}`,
                transition: 'all 0.2s',
              }}
            >
              {isComplete ? '✓ ' : ''}{STEP_LABELS[s]}
            </div>
          );
        })}
      </div>
    );
  };

  // --- Step 0: Choose your path ---
  const renderChooseStep = () => (
    <div>
      <h3 style={{ color: '#4fc3f7', marginBottom: 12 }}>How would you like to get started?</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
        <button
          onClick={() => { setPath('repo'); setStep('repo'); }}
          style={pathCardStyle}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#4fc3f7'; e.currentTarget.style.transform = 'scale(1.02)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#444'; e.currentTarget.style.transform = 'scale(1)'; }}
        >
          <div style={{ fontSize: 36, marginBottom: 12 }}>📦</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 8 }}>My Repository</div>
          <div style={{ fontSize: 13, color: '#aaa', lineHeight: 1.5 }}>
            Connect an existing GitHub repo and deploy it through Lyric
          </div>
        </button>
        <button
          onClick={() => { setPath('build'); setStep('describe'); }}
          style={pathCardStyle}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#ce93d8'; e.currentTarget.style.transform = 'scale(1.02)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#444'; e.currentTarget.style.transform = 'scale(1)'; }}
        >
          <div style={{ fontSize: 36, marginBottom: 12 }}>✨</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Build from scratch</div>
          <div style={{ fontSize: 13, color: '#aaa', lineHeight: 1.5 }}>
            Describe what you need and Lyric will generate and deploy it
          </div>
        </button>
        <button
          onClick={() => { setPath('edit'); setStep('repo'); }}
          style={pathCardStyle}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#ffb74d'; e.currentTarget.style.transform = 'scale(1.02)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#444'; e.currentTarget.style.transform = 'scale(1)'; }}
        >
          <div style={{ fontSize: 36, marginBottom: 12 }}>✏️</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Edit existing app</div>
          <div style={{ fontSize: 13, color: '#aaa', lineHeight: 1.5 }}>
            Select a Lyric-built app to enhance, debug, or fix
          </div>
        </button>
      </div>

      <div style={{
        marginTop: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '12px 16px', border: '1px dashed #3a5a3a', borderRadius: 10, backgroundColor: '#14180f',
      }}>
        <span style={{ fontSize: 13, color: '#aaa' }}>
          Have a build in progress? Pick up where you left off.
        </span>
        <button
          type="button"
          onClick={openLoadDrafts}
          style={{
            padding: '8px 16px', backgroundColor: '#1e2a1e', color: '#81c784',
            border: '1px solid #3a5a3a', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          📂 Resume a saved build
        </button>
      </div>
    </div>
  );

  // --- Step 1: Repository URL ---
  const renderRepoStep = () => {
    const filteredRepos = repositories.filter(r =>
      r.fullName.toLowerCase().includes(repoSearch.toLowerCase())
    );

    return (
      <div>
        <h3 style={{ color: '#4fc3f7', marginBottom: 8 }}>📦 Select GitHub Repository</h3>

        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 16 }}>
          <button
            onClick={() => { setRepoInputMode('search'); fetchRepositories(); }}
            style={{
              flex: 1, padding: '8px 10px', fontSize: 13, fontWeight: repoInputMode === 'search' ? 700 : 400,
              backgroundColor: repoInputMode === 'search' ? '#1e3a5f' : 'transparent',
              color: repoInputMode === 'search' ? '#4fc3f7' : '#888',
              border: '1px solid #444', borderRadius: '8px 0 0 8px', cursor: 'pointer',
            }}
          >
            🔍 Search
          </button>
          <button
            onClick={() => setRepoInputMode('url')}
            style={{
              flex: 1, padding: '8px 10px', fontSize: 13, fontWeight: repoInputMode === 'url' ? 700 : 400,
              backgroundColor: repoInputMode === 'url' ? '#1e3a5f' : 'transparent',
              color: repoInputMode === 'url' ? '#4fc3f7' : '#888',
              border: '1px solid #444', borderLeft: 'none', cursor: 'pointer',
            }}
          >
            🔗 URL
          </button>
          <button
            onClick={() => setRepoInputMode('local')}
            style={{
              flex: 1, padding: '8px 10px', fontSize: 13, fontWeight: repoInputMode === 'local' ? 700 : 400,
              backgroundColor: repoInputMode === 'local' ? '#1e3a5f' : 'transparent',
              color: repoInputMode === 'local' ? '#4fc3f7' : '#888',
              border: '1px solid #444', borderLeft: 'none', borderRadius: '0 8px 8px 0', cursor: 'pointer',
            }}
          >
            📁 Local Folder
          </button>
        </div>

        {repoInputMode === 'search' ? (
          <div>
            <input
              type="text"
              placeholder="Search repositories..."
              value={repoSearch}
              onChange={(e) => setRepoSearch(e.target.value)}
              onFocus={fetchRepositories}
              style={inputStyle}
            />
            {loadingRepos && (
              <p style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>📥 Loading repositories...</p>
            )}
            <select
              value=""
              onChange={(e) => handleRepoSelect(e.target.value)}
              disabled={loadingRepos || repositories.length === 0}
              style={{ ...inputStyle, marginBottom: 12 }}
            >
              <option value="">
                {loadingRepos ? 'Loading...' : repositories.length > 0 ? `Choose from ${filteredRepos.length} repositories...` : 'Click to load repositories'}
              </option>
              {filteredRepos.map(repo => (
                <option key={repo.id || repo.fullName} value={repo.fullName}>
                  {repo.fullName}
                </option>
              ))}
            </select>
            {loading && (
              <p style={{ fontSize: 12, color: '#4fc3f7', marginBottom: 8 }}>✅ Validating selected repository...</p>
            )}
          </div>
        ) : repoInputMode === 'url' ? (
          <div>
            <p style={{ color: '#aaa', fontSize: 13, marginBottom: 8 }}>
              Paste the full GitHub URL of the repository you want to deploy.
            </p>
            <input
              type="text"
              placeholder="https://github.com/org/repo"
              value={formData.repoUrl}
              onChange={(e) => updateField('repoUrl', e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && formData.repoUrl.trim() && validateRepo()}
              style={inputStyle}
            />
            <button
              onClick={validateRepo}
              disabled={loading || !formData.repoUrl.trim()}
              style={{ ...btnPrimary, backgroundColor: loading ? '#555' : '#4fc3f7', marginBottom: 8 }}
            >
              {loading ? 'Validating…' : 'Validate Repository →'}
            </button>
          </div>
        ) : (
          <div>
            <p style={{ color: '#aaa', fontSize: 13, marginBottom: 10 }}>
              No repo yet? <strong style={{ color: '#ccc' }}>Drag your project folder</strong> from
              Finder into the box below — Lyric creates a new{' '}
              <strong style={{ color: '#ccc' }}>private</strong> repo in{' '}
              <code style={{ color: '#4fc3f7' }}>ischemaview</code>, pushes your code, then continues the normal deploy.
            </p>

            {/* Drag-and-drop zone — the ONLY way to import a folder with no
                native browser permission popup. */}
            <div
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!localDragOver) setLocalDragOver(true); }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setLocalDragOver(false); }}
              onDrop={handleLocalDrop}
              style={{
                border: `2px dashed ${localDragOver ? '#4fc3f7' : '#3a4553'}`,
                borderRadius: 12,
                background: localDragOver ? 'rgba(79,195,247,0.08)' : 'rgba(255,255,255,0.02)',
                padding: '28px 20px',
                textAlign: 'center',
                transition: 'all 0.15s ease',
                marginBottom: 10,
                cursor: 'default',
              }}
            >
              <div style={{ fontSize: 34, lineHeight: 1, marginBottom: 8 }}>📂</div>
              <div style={{ color: localDragOver ? '#4fc3f7' : '#ccc', fontSize: 15, fontWeight: 600 }}>
                {localBusy ? (localProgress || 'Reading…') : (localDragOver ? 'Drop to import' : 'Drag your project folder here')}
              </div>
              <div style={{ color: '#777', fontSize: 12, marginTop: 6 }}>
                No upload prompt — dragging is instant. Dependencies &amp; build output are skipped automatically.
              </div>
            </div>

            {/* Click-to-browse fallback. Honest label: this path DOES show a
                native browser security prompt (unavoidable for any picker). */}
            <input
              ref={localDirInputRef}
              type="file"
              webkitdirectory=""
              directory=""
              multiple
              onChange={handleLocalDirPick}
              style={{ display: 'none' }}
            />
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              <button
                onClick={handleChooseFolder}
                disabled={localBusy}
                style={{
                  background: 'none', border: 'none', color: '#8b949e',
                  fontSize: 12, textDecoration: 'underline', cursor: localBusy ? 'default' : 'pointer', padding: 4,
                }}
                title="Opens a folder picker — the browser will ask for permission"
              >
                …or browse for a folder instead (shows a browser permission prompt)
              </button>
            </div>

            {localFiles.length > 0 && (
              <div style={cardStyle}>
                <div style={{ fontSize: 13, color: '#fff', marginBottom: 8 }}>
                  <strong>{localFolderName}</strong> — {localFiles.length} file{localFiles.length === 1 ? '' : 's'}
                  {' '}({(localTotalBytes / 1024).toFixed(0)} KB)
                  {localSkipped > 0 && (
                    <span style={{ color: '#888' }}> · {localSkipped} skipped (deps/build/.env)</span>
                  )}
                </div>
                <label style={labelStyle}>New repository name</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: '#888', fontSize: 13, whiteSpace: 'nowrap' }}>ischemaview /</span>
                  <input
                    type="text"
                    value={localRepoName}
                    onChange={(e) => setLocalRepoName(e.target.value)}
                    placeholder="my-app"
                    style={{ ...inputStyle, marginBottom: 4 }}
                  />
                </div>
                <p style={{ color: '#666', fontSize: 11, margin: '0 0 12px' }}>
                  Created as a <strong>private</strong> repo on branch <code>main</code>. Letters, numbers, <code>-</code>, <code>_</code>, <code>.</code> only.
                </p>
                <button
                  onClick={handleCreateRepoFromLocal}
                  disabled={localBusy || !slugifyRepoName(localRepoName)}
                  style={{
                    ...btnPrimary,
                    backgroundColor: (localBusy || !slugifyRepoName(localRepoName)) ? '#555' : '#81c784',
                    cursor: (localBusy || !slugifyRepoName(localRepoName)) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {localBusy ? (localProgress || 'Working…') : 'Create Repo & Continue →'}
                </button>
              </div>
            )}

            {localTokenNeeded && (
              <div style={{ marginTop: 10 }}>
                <label style={labelStyle}>GitHub Personal Access Token</label>
                <input
                  type="password"
                  value={githubToken}
                  onChange={(e) => setGithubToken(e.target.value)}
                  placeholder="ghp_…"
                  style={{ ...inputStyle, marginBottom: 4 }}
                />
                <p style={{ color: '#666', fontSize: 11, margin: '0 0 8px' }}>
                  Needs <code>repo</code> scope and membership in <code>ischemaview</code>.{' '}
                  {tokenSetup?.createTokenUrl && (
                    <a href={tokenSetup.createTokenUrl} target="_blank" rel="noreferrer" style={{ color: '#4fc3f7' }}>Create one →</a>
                  )}
                </p>
                <button
                  onClick={handleCreateRepoFromLocal}
                  disabled={localBusy || !githubToken.trim()}
                  style={{ ...btnPrimary, backgroundColor: localBusy ? '#555' : '#4fc3f7', marginBottom: 8 }}
                >
                  Save Token & Retry →
                </button>
              </div>
            )}

            {localError && (
              <p style={{ color: '#e57373', fontSize: 12, marginTop: 8 }}>⚠️ {localError}</p>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { setPath(null); setStep('choose'); }} style={btnBack}>← Back</button>
        </div>
      </div>
    );
  };

  // --- Step 2: Validation results ---
  const renderValidationStep = () => {
    const info = formData.repoInfo;
    return (
      <div>
        <h3 style={{ color: '#81c784', marginBottom: 8 }}>✅ Repository Found</h3>
        {info && (
          <div style={cardStyle}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 4 }}>
              {info.fullName}
            </div>
            {info.description && (
              <div style={{ color: '#aaa', fontSize: 13, marginBottom: 8 }}>{info.description}</div>
            )}
            <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#888' }}>
              {info.language && <span>🗂 {info.language}</span>}
              <span>⭐ {info.stars ?? 0}</span>
              <span>{info.isPrivate ? '🔒 Private' : '🌐 Public'}</span>
              <span>📏 {info.size ? `${(info.size / 1024).toFixed(1)} MB` : 'N/A'}</span>
            </div>
          </div>
        )}

        <label style={labelStyle}>App Name</label>
        <input
          type="text"
          value={formData.appDisplayName}
          onChange={(e) => updateField('appDisplayName', e.target.value)}
          placeholder={formData.repo || 'my-app'}
          style={{ ...inputStyle, marginBottom: 4 }}
        />
        <p style={{ color: '#666', fontSize: 11, margin: '0 0 16px' }}>
          Used as the display name and URL slug — defaults to the repo name.
        </p>

        <label style={labelStyle}>Branch to deploy</label>
        {formData.branches.length > 15 && (
          <input
            type="text"
            placeholder={`Filter ${formData.branches.length} branches…`}
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            style={{ ...inputStyle, marginBottom: 4 }}
          />
        )}
        {(() => {
          const f = branchFilter.trim().toLowerCase();
          const filtered = f
            ? formData.branches.filter(b => b.toLowerCase().includes(f))
            : formData.branches;
          // If the currently-selected branch was filtered out, keep it in the
          // list so the dropdown doesn't silently drop the user's choice.
          const branchesToShow = formData.branch && !filtered.includes(formData.branch)
            ? [formData.branch, ...filtered]
            : filtered;
          return (
            <select
              value={formData.branch}
              onChange={(e) => updateField('branch', e.target.value)}
              style={{ ...inputStyle, marginBottom: 4 }}
            >
              <option value="">
                — Select a branch ({branchesToShow.length}{f ? ` of ${formData.branches.length}` : ''} shown) —
              </option>
              {branchesToShow.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          );
        })()}
        <p style={{ color: '#666', fontSize: 11, margin: '0 0 16px' }}>
          Pick the branch you want to deploy. <code>main</code> is selected by default when it exists. Tip: a URL ending in <code>/tree/&lt;branch&gt;</code> overrides the default.
        </p>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => detectStack()}
            disabled={loading || !formData.branch}
            title={!formData.branch ? 'Pick a branch first' : ''}
            style={{
              ...btnPrimary,
              backgroundColor: (loading || !formData.branch) ? '#555' : '#81c784',
              cursor: (loading || !formData.branch) ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Detecting Stack…' : 'Detect Stack & Continue →'}
          </button>
          <button onClick={() => setStep('repo')} style={btnBack}>← Back</button>
        </div>
      </div>
    );
  };

  // --- Step 3a: Monorepo app picker ---
  // Only shown when the initial detect-stack call found >1 candidate apps in
  // the repo (e.g. ischemaview/rapid-platform has apps/workflow-tracker,
  // apps/dashboard, services/api, etc.). User picks one; we re-run detection
  // scoped to that subdir.
  const renderPickAppStep = () => {
    const candidates = formData.stackInfo?.candidateApps || [];
    const isSplitFrontendBackend = candidates.some(c =>
      c.kind === 'Browser interface' || /frontend \+ backend/i.test(c.kind || '')
    ) && candidates.some(c => c.kind === 'API / backend service');
    const recommendedComplete = candidates.find(c =>
      /complete application|deploy this only/i.test(c.recommendation || '')
    );
    const deploymentSequence = candidates
      .filter(c => Number.isInteger(c.deploymentOrder))
      .sort((a, b) => a.deploymentOrder - b.deploymentOrder);
    const filter = candidateFilter.trim().toLowerCase();
    const filtered = filter
      ? candidates.filter(c => c.label.toLowerCase().includes(filter)
          || (c.languages || []).some(l => l.toLowerCase().includes(filter))
          || (c.kind || '').toLowerCase().includes(filter)
          || (c.purpose || '').toLowerCase().includes(filter))
      : candidates;
    return (
      <div>
        <h3 style={{ color: '#ffb74d', marginBottom: 8 }}>🗂 Multiple Apps Detected</h3>
        <p style={{ color: '#bbb', marginTop: 0 }}>
          This repository contains {candidates.length} separately deployable applications. They may be
          different parts of one system. Review what each choice does before selecting one.
        </p>
        {formData.stackInfo?.repoSummary && (
          <div style={{ ...cardStyle, marginBottom: 12, padding: '10px 12px' }}>
            <div style={{ color: '#ffcc80', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
              What this repository is for
            </div>
            <div style={{ color: '#ddd', fontSize: 13, lineHeight: 1.45 }}>
              {formData.stackInfo.repoSummary}
            </div>
          </div>
        )}
        {isSplitFrontendBackend && (
          <div style={{ background: '#3e2723', border: '1px solid #ff7043', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
            <div style={{ color: '#ffab91', fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
              This repository is split into a browser interface and an API/backend
            </div>
            <div style={{ color: '#ddd', fontSize: 12, lineHeight: 1.5 }}>
              {recommendedComplete
                ? `Recommended: deploy "${recommendedComplete.label}" as one complete application. Lyric installs and starts the backend first, waits until it is healthy, then starts the frontend already connected to it.`
                : 'These are separate parts of one system. Deploy the backend first, verify it is healthy, then deploy the frontend and connect it to the backend.'}
            </div>
            {recommendedComplete && (
              <button
                type="button"
                onClick={() => {
                  setPickedCandidate(recommendedComplete.path);
                  detectStack(recommendedComplete.path);
                }}
                disabled={loading}
                style={{ ...btnPrimary, marginTop: 10, width: '100%', opacity: loading ? 0.55 : 1 }}
              >
                {loading ? 'Preparing complete application…' : 'Deploy complete application →'}
              </button>
            )}
          </div>
        )}
        {deploymentSequence.length > 0 && (
          <div style={{ background: '#10251a', border: '1px solid #4caf50', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
            <div style={{ color: '#81c784', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
              Recommended deployment order
            </div>
            {deploymentSequence.map(candidate => (
              <div key={candidate.path || '__root_order__'} style={{ color: '#ddd', fontSize: 12, lineHeight: 1.5 }}>
                <strong style={{ color: '#fff' }}>{candidate.deploymentOrder}. {candidate.label}</strong>
                {' — '}{candidate.recommendation}
              </div>
            ))}
            {recommendedComplete && (
              <div style={{ color: '#a5d6a7', fontSize: 12, lineHeight: 1.5, marginTop: 4 }}>
                The backend and frontend are included in one deployment. There is no manual second deployment step.
              </div>
            )}
          </div>
        )}
        {candidates.length > 8 && (
          <input
            type="text"
            placeholder="Filter (e.g. workflow, python, apps/…)"
            value={candidateFilter}
            onChange={(e) => setCandidateFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              marginBottom: 8,
              borderRadius: 4,
              border: '1px solid #555',
              background: '#1a1a1a',
              color: '#fff',
              fontSize: 13,
              boxSizing: 'border-box',
            }}
            autoFocus
          />
        )}
        <div style={{ ...cardStyle, maxHeight: 520, overflowY: 'auto' }}>
          <div style={{ display: 'grid', gap: 8 }}>
            {filtered.length === 0 && (
              <div style={{ color: '#888', fontStyle: 'italic', textAlign: 'center', padding: 16 }}>
                No candidates match "{candidateFilter}"
              </div>
            )}
            {filtered.map((c) => {
              const isPicked = pickedCandidate === c.path;
              return (
                <label
                  key={c.path || '__root__'}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '10px 12px',
                    borderRadius: 6,
                    border: `1px solid ${isPicked ? '#ffb74d' : '#444'}`,
                    background: isPicked ? '#3a2c12' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="picked-candidate"
                    value={c.path}
                    checked={isPicked}
                    onChange={() => setPickedCandidate(c.path)}
                    style={{ marginTop: 2 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ fontFamily: 'monospace', fontWeight: 600, color: '#fff' }}>
                        {c.label}
                      </div>
                      <AppScopeInfo isRepoRoot={!c.path} />
                      {c.kind && (
                        <span style={{ color: '#90caf9', background: '#102536', borderRadius: 10, padding: '2px 7px', fontSize: 11 }}>
                          {c.kind}
                        </span>
                      )}
                      {c.recommendation && (
                        <span style={{
                          color: c.deploymentOrder === 1 ? '#a5d6a7' : '#ffcc80',
                          background: c.deploymentOrder === 1 ? '#15351f' : '#3a2c12',
                          borderRadius: 10,
                          padding: '2px 7px',
                          fontSize: 11,
                          fontWeight: 700,
                        }}>
                          {c.recommendation}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>
                      {(c.languages || []).join(' + ') || 'Unknown'}
                      {c.markers && c.markers.length > 0 && (
                        <> · <code style={{ color: '#888' }}>{c.markers.join(', ')}</code></>
                      )}
                    </div>
                    {c.purpose && (
                      <div style={{ color: '#ddd', fontSize: 13, lineHeight: 1.45, marginTop: 8 }}>
                        <strong style={{ color: '#fff' }}>What it does:</strong> {c.purpose}
                      </div>
                    )}
                    {c.deploymentImpact && (
                      <div style={{ color: '#bbb', fontSize: 12, lineHeight: 1.45, marginTop: 6 }}>
                        <strong style={{ color: '#ffcc80' }}>If you select this:</strong> {c.deploymentImpact}
                      </div>
                    )}
                    {c.needs && (
                      <div style={{ color: '#ffab91', fontSize: 12, lineHeight: 1.45, marginTop: 6 }}>
                        <strong>Before deploying:</strong> {c.needs}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={() => setStep('validation')} style={btnBack}>← Back</button>
          <button
            onClick={() => detectStack(pickedCandidate)}
            disabled={loading || candidates.length === 0 || pickedCandidate === null}
            style={{
              ...btnPrimary,
              opacity: (loading || candidates.length === 0 || pickedCandidate === null) ? 0.55 : 1,
              cursor: (loading || candidates.length === 0 || pickedCandidate === null) ? 'not-allowed' : 'pointer',
            }}
          >
            {loading
              ? 'Detecting…'
              : pickedCandidate === null
                ? 'Select an application to continue'
                : `Continue with ${pickedCandidate || '(repo root)'}`}
          </button>
        </div>
      </div>
    );
  };

  // --- Step 3: Stack detection ---
  const renderStackStep = () => {
    const stack = formData.stackInfo;
    // Set by the compliance-check IIFE below; read by the bottom buttons to gate Continue.
    // null = nothing blocks; string = human-readable reason the button is disabled.
    let currentStackBlockReason = null;
    let currentStackHasHardBlock = false;
    return (
      <div>
        <h3 style={{ color: '#ffb74d', marginBottom: 8 }}>🔍 Stack Detected</h3>
        {stack && (
          <div style={cardStyle}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <InfoRow label="Language" value={stack.language || 'Unknown'} />
              <InfoRow label="Framework" value={stack.framework || 'None detected'} />
              <InfoRow label="Has CI/CD" value={stack.hasCICD ? '✅ Yes' : '❌ No'} />
              <InfoRow label="Package Manager" value={stack.packageManager || 'N/A'} />
              <InfoRow label="Deployable" value={stack.isDeployable ? '✅ Yes' : '⚠️ Needs config'} />
            </div>
            {stack.warnings && stack.warnings.length > 0 && (
              <div style={{ marginTop: 12 }}>
                {stack.warnings.map((w, i) => {
                  const subdirMatch = w.match(/^App detected in subdirectory: (.+)$/);
                  if (subdirMatch) {
                    const subdir = subdirMatch[1];
                    return (
                      <ExpandableWarning
                        key={i}
                        label={`⚠️ App detected in subdirectory: ${subdir}`}
                        what={`The deployable app lives inside the "${subdir}" folder, not the repo root. Lyric will clone the full repo and run all install/build/start commands from that subdirectory.`}
                        action={`No action needed — Lyric handles this automatically. Just make sure the app's entry point and dependencies are inside "${subdir}". If you expected the app to be at the repo root, the repo may be a monorepo.`}
                      />
                    );
                  }
                  return (
                    <div key={i} style={{ color: '#ffab91', fontSize: 12, marginBottom: 2, padding: '6px 8px', backgroundColor: '#3e2723', borderRadius: 6 }}>⚠️ {w}</div>
                  );
                })}
              </div>
            )}

            {/* Lyric Compliance Check — auto-fix recipes for apps that aren't proxy-ready.
                Findings are produced by the server during stack detection. Critical findings
                will prevent the app from running and need manual or auto-applied fixes;
                warnings are UX-only and can be deferred. Approved auto-fixes are applied
                to the deploy directory after clone; the source repo stays untouched. */}
            {Array.isArray(stack.complianceFindings) && stack.complianceFindings.length > 0 && (() => {
              // 'error' or 'critical' or blocking:true all count as run-blockers
              const isBlocker = f => f.severity === 'error' || f.severity === 'critical' || f.blocking === true;
              const criticals = stack.complianceFindings.filter(isBlocker);
              const warns = stack.complianceFindings.filter(f => !isBlocker(f));

              const renderFinding = (f, isCritical) => {
                const isInfo = f.severity === 'info' && f.blocking !== true;
                const palette = isCritical
                  ? { bg: '#2a1414', border: '#ef5350', label: '#ef9a9a', icon: '🛑' }
                  : isInfo
                    ? { bg: '#11202a', border: '#2a3a4a', label: '#4fc3f7', icon: 'ℹ️' }
                    : { bg: '#1a1f2e', border: '#5c6bc0', label: '#9fa8da', icon: '🩹' };

                return (
                  <div key={f.id} style={{ marginBottom: 10, padding: 10, backgroundColor: palette.bg, border: `1px solid ${palette.border}`, borderRadius: 4 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: palette.label }}>{palette.icon} {f.title}</div>
                    {f.description && <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>{f.description}</div>}
                    {f.file && <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>Target: <code style={{ color: '#aaa' }}>{f.file}</code></div>}

                    {f.autoFixable ? (
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!approvedFixes[f.id]}
                          onChange={(e) => setApprovedFixes(prev => ({ ...prev, [f.id]: e.target.checked }))}
                          style={{ marginTop: 2 }}
                        />
                        <span style={{ fontSize: 11, color: '#81c784' }}>
                          🩹 Create review branch and test-deploy fix: {f.fixDescription}
                        </span>
                      </label>
                    ) : isInfo ? (
                      // Informational only — no action required. Show the detail
                      // without the "manual fix required" framing.
                      f.fixDescription ? (
                        <div style={{ marginTop: 8, padding: 8, backgroundColor: '#0d1117', borderRadius: 3 }}>
                          <div style={{ fontSize: 11, color: '#bbb' }}>{f.fixDescription}</div>
                          {f.codeSample && (
                            <pre style={{ margin: '6px 0 0', padding: '6px 8px', backgroundColor: '#1a1a1a', color: '#a5d6a7', fontSize: 11, borderRadius: 3, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{f.codeSample}</pre>
                          )}
                        </div>
                      ) : null
                    ) : (
                      <div style={{ marginTop: 8, padding: 8, backgroundColor: '#0d0d0d', borderRadius: 3 }}>
                        <div style={{ fontSize: 11, color: '#ffb74d', marginBottom: 4 }}>✋ Manual fix required — Lyric can't patch this safely.</div>
                        {f.fixDescription && <div style={{ fontSize: 11, color: '#ccc' }}>{f.fixDescription}</div>}
                        {f.codeSample && (
                          <pre style={{ margin: '6px 0 0', padding: '6px 8px', backgroundColor: '#1a1a1a', color: '#a5d6a7', fontSize: 11, borderRadius: 3, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{f.codeSample}</pre>
                        )}
                      </div>
                    )}
                  </div>
                );
              };

              // A critical is "unaddressed" if it has no auto-fix (must be fixed in repo)
              // OR has an auto-fix that the user has unchecked. Either case blocks Continue —
              // unless the user has explicitly overridden the gate with "Deploy anyway".
              const unaddressedCriticals = criticals.filter(c => !c.autoFixable || !approvedFixes[c.id]);
              const hasHardBlock = unaddressedCriticals.some(c => c.hardBlock);
              const blocked = hasHardBlock || (unaddressedCriticals.length > 0 && !overrideBlockers);
              currentStackHasHardBlock = hasHardBlock;
              // Stash on the closure so the bottom button can read it without re-deriving
              currentStackBlockReason = blocked
                ? hasHardBlock
                    ? 'This selection cannot produce a working app. Choose a deploy option that includes its required backend.'
                    : unaddressedCriticals.some(c => !c.autoFixable)
                    ? 'Fix the critical issues that require a repository change, then click "Re-detect stack" (or deploy anyway at your own risk).'
                    : 'Check the auto-fix boxes on each critical issue below, then click "Analyze App Config" (or deploy anyway at your own risk).'
                : null;
              return (
                <>
                  {/* Top-of-step blocker banner — only when criticals exist */}
                  {criticals.length > 0 && (
                    <div style={{ marginTop: 14, padding: 12, backgroundColor: '#3a1414', border: '2px solid #ef5350', borderRadius: 6 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#ef9a9a', marginBottom: 4 }}>
                        🛑 {criticals.length === 1 ? '1 issue will prevent this app from running' : `${criticals.length} issues will prevent this app from running`}
                      </div>
                      <div style={{ fontSize: 11, color: '#ffab91' }}>
                        {blocked
                          ? 'Deploy is blocked until each critical item below is addressed. ' + (unaddressedCriticals.some(c => !c.autoFixable) ? 'At least one requires a change to your repository — fix it there, push, then click "Re-detect stack".' : 'Check the auto-fix box on each item to apply Lyric\'s patch at deploy time.')
                          : 'All critical items are addressed. You can continue.'}
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: 14, padding: 12, backgroundColor: '#0f1419', border: '1px solid #2a3a4a', borderRadius: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#9fa8da', marginBottom: 4 }}>
                      🩹 Lyric Compliance Check ({stack.complianceFindings.length})
                    </div>
                    <div style={{ fontSize: 11, color: '#777', marginBottom: 10 }}>
                      Issues that would break the app behind <code>/apps/&lt;name&gt;/*</code>. Approved fixes are committed to a new
                      <code> lyric/deploy-fix-*</code> branch, opened as a draft pull request, and deployed as a separate test app.
                      Your selected source branch stays untouched until a developer reviews and merges the pull request.
                    </div>
                    {criticals.length > 0 && (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#ef9a9a', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Critical — must address</div>
                        {criticals.map(f => renderFinding(f, true))}
                      </>
                    )}
                    {warns.length > 0 && (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#9fa8da', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: criticals.length > 0 ? 10 : 0, marginBottom: 6 }}>Recommendations</div>
                        {warns.map(f => renderFinding(f, false))}
                      </>
                    )}
                  </div>
                </>
              );
            })()}

            {/* Network endpoints this app talks to — discovered by statically
                scanning the deployed code (NOT a preset list). The developer can
                test reachability from inside Lyric's pod before shipping, so
                "can my app even reach X?" is answered up front instead of after
                a failed deploy. Diagnostic only — never blocks the wizard. */}
            {Array.isArray(stack.discoveredEndpoints) && stack.discoveredEndpoints.length > 0 && (() => {
              const discovered = stack.discoveredEndpoints;
              const testable = discovered.filter(e =>
                !!(e.suggestedProbe || parseManualProbe((endpointTests[`${e.kind}:${e.value}`] || {}).manualUrl)));
              const resultByLabel = {};
              if (Array.isArray(endpointResults)) {
                for (const r of endpointResults) if (r.label) resultByLabel[r.label] = r;
              }

              return (
                <details style={{ marginTop: 14, padding: 12, backgroundColor: '#0f1419', border: '1px solid #2a3a4a', borderRadius: 6 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#4fc3f7', outline: 'none', userSelect: 'none' }}>
                    🌐 Network endpoints this app uses ({discovered.length})
                  </summary>
                  <div style={{ fontSize: 11, color: '#777', marginBottom: 10 }}>
                    Found by scanning <code>{stack.endpointScan?.scannedFiles ?? 0}</code> source file(s) in your repo.
                    Tick the endpoints you want to verify, add auth if they need it, then run the check — Lyric probes them
                    from inside its own pod so you know they&apos;re reachable <em>before</em> you deploy. For endpoints whose
                    value comes from a secret or is built at runtime, paste the URL to test it.
                    {stack.endpointScan?.truncated && <span style={{ color: '#ffb74d' }}> (list truncated — scan limit reached)</span>}
                  </div>

                  {discovered.map((e) => {
                    const id = `${e.kind}:${e.value}`;
                    const meta = ENDPOINT_KIND_META[e.kind] || { icon: '•', label: e.kind, probeable: false };
                    const t = endpointTests[id] || emptyEndpointTest();
                    const r = resultByLabel[id];
                    // A row is probeable if the scanner resolved a host, OR the
                    // user pasted a URL for an endpoint whose value lives in a
                    // secret / is built at runtime (env-var-url, dynamic).
                    const manualProbe = parseManualProbe(t.manualUrl);
                    const probe = e.suggestedProbe || manualProbe;
                    const canProbe = !!probe;
                    const allowsManual = !e.suggestedProbe && (e.kind === 'env-var-url' || e.kind === 'dynamic');

                    return (
                      <div key={id} style={{ marginBottom: 8, padding: 10, backgroundColor: '#161a1e', border: '1px solid #2f3a44', borderRadius: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                          {canProbe ? (
                            <input
                              type="checkbox"
                              checked={!!t.selected}
                              onChange={(ev) => setEndpointTest(e, { selected: ev.target.checked })}
                              style={{ marginTop: 3 }}
                              title="Include this endpoint in the reachability check"
                            />
                          ) : (
                            <span style={{ width: 13, display: 'inline-block' }} />
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#cfe8ff', wordBreak: 'break-all' }}>
                              {meta.icon} {e.value}
                            </div>
                            <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>
                              {meta.label} · <code style={{ color: '#888' }}>{e.file}:{e.line}</code>
                              {canProbe && <> · probes <code style={{ color: '#888' }}>{probe.host}:{probe.port}</code></>}
                            </div>
                            {e.note && <div style={{ fontSize: 10, color: e.kind === 'cli-shellout' ? '#ffb74d' : '#777', marginTop: 3 }}>{e.note}</div>}

                            {/* Manual URL entry — for endpoints whose value the
                                scanner can't see (env var / secret, or built at
                                runtime). Paste the real URL to make the row
                                probeable; entering a valid one auto-selects it. */}
                            {allowsManual && (
                              <div style={{ marginTop: 6 }}>
                                <input
                                  type="text"
                                  value={t.manualUrl}
                                  onChange={(ev) => {
                                    const v = ev.target.value;
                                    setEndpointTest(e, { manualUrl: v, selected: !!parseManualProbe(v) });
                                  }}
                                  aria-label={`Test URL for ${e.value}`}
                                  placeholder="Paste the URL to test, e.g. https://api.example.com"
                                  style={{
                                    width: '100%', boxSizing: 'border-box', padding: '5px 8px', borderRadius: 4,
                                    border: `1px solid ${t.manualUrl && !manualProbe ? '#ef5350' : '#444'}`,
                                    background: '#161a1e', color: '#fff', fontSize: 12,
                                  }}
                                />
                                {t.manualUrl && !manualProbe && (
                                  <div style={{ fontSize: 10, color: '#ef9a9a', marginTop: 3 }}>
                                    Enter a valid URL or host (e.g. <code>https://api.example.com</code> or <code>api.example.com:8443</code>).
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Auth toggle — only for probeable rows */}
                            {canProbe && (
                              <div style={{ marginTop: 6 }}>
                                <button
                                  type="button"
                                  onClick={() => setEndpointTest(e, { _authOpen: !t._authOpen })}
                                  style={{ padding: '3px 8px', background: '#1e3a5f', color: '#cfe8ff', border: '1px solid #2a4a72', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}
                                >
                                  {t._authOpen ? '▾' : '▸'} Auth {t.auth.mode !== 'none' ? `(${t.auth.mode})` : '(none)'}
                                </button>
                                {t._authOpen && (
                                  <div style={{ marginTop: 6, padding: 8, background: '#0d1117', border: '1px solid #2f3a44', borderRadius: 4 }}>
                                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 6 }}>
                                      {['none', 'bearer', 'custom'].map((mode) => (
                                        <label key={mode} style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#ddd', fontSize: 11, cursor: 'pointer' }}>
                                          <input
                                            type="radio"
                                            name={`auth-${id}`}
                                            checked={t.auth.mode === mode}
                                            onChange={() => setEndpointAuth(e, { mode })}
                                          />
                                          {mode === 'none' ? 'No auth' : mode === 'bearer' ? 'Bearer token' : 'Custom headers'}
                                        </label>
                                      ))}
                                    </div>
                                    {t.auth.mode === 'bearer' && (
                                      <input
                                        type="password"
                                        placeholder="token (sent as Authorization: Bearer …)"
                                        value={t.auth.bearer}
                                        onChange={(ev) => setEndpointAuth(e, { bearer: ev.target.value })}
                                        style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', borderRadius: 4, border: '1px solid #444', background: '#161a1e', color: '#fff', fontSize: 12 }}
                                      />
                                    )}
                                    {t.auth.mode === 'custom' && (
                                      <div>
                                        {(t.auth.custom || []).map((h, hi) => (
                                          <div key={hi} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                                            <input
                                              placeholder="Header-Name"
                                              value={h.name}
                                              onChange={(ev) => setEndpointCustomHeader(e, hi, 'name', ev.target.value)}
                                              style={{ flex: 1, padding: '5px 8px', borderRadius: 4, border: '1px solid #444', background: '#161a1e', color: '#fff', fontSize: 12 }}
                                            />
                                            <input
                                              type="password"
                                              placeholder="value"
                                              value={h.value}
                                              onChange={(ev) => setEndpointCustomHeader(e, hi, 'value', ev.target.value)}
                                              style={{ flex: 1, padding: '5px 8px', borderRadius: 4, border: '1px solid #444', background: '#161a1e', color: '#fff', fontSize: 12 }}
                                            />
                                          </div>
                                        ))}
                                        <button
                                          type="button"
                                          onClick={() => addEndpointCustomHeader(e)}
                                          style={{ padding: '3px 8px', background: '#2f3a44', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}
                                        >
                                          + Add header
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Inline result for this row, once a check has run */}
                            {r && (
                              <div style={{ marginTop: 6, padding: '6px 8px', background: r.ok ? '#10260f' : '#2a1414', border: `1px solid ${r.ok ? '#4caf50' : '#ef5350'}`, borderRadius: 4 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: r.ok ? '#c8e6c9' : '#ffcdd2' }}>
                                  {r.ok ? '✅ Reachable' : '❌ Unreachable'} · {r.latencyMs}ms
                                </div>
                                {r.error && <div style={{ fontSize: 11, color: '#ef9a9a', marginTop: 2 }}>{r.error}</div>}
                                {r.tls && (
                                  <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>
                                    TLS {r.tls.protocol} {r.tls.authorized ? '(trusted)' : '(UNTRUSTED)'}
                                    {r.tls.cert && <> · issuer <code>{r.tls.cert.issuer}</code></>}
                                  </div>
                                )}
                                {r.http && <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>HTTP {r.http.status}{r.http.server ? ` · ${r.http.server}` : ''}</div>}
                                {Array.isArray(r.sentHeaders) && r.sentHeaders.length > 0 && (
                                  <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                                    sent: {r.sentHeaders.map(([n]) => n).join(', ')}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {endpointError && (
                    <div style={{ marginTop: 8, padding: '6px 10px', background: '#4a1a1a', border: '1px solid #ef5350', borderRadius: 4, color: '#ffcdd2', fontSize: 11 }}>
                      {endpointError}
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={runEndpointCheck}
                      disabled={endpointChecking || testable.length === 0}
                      style={{
                        padding: '7px 14px', borderRadius: 4, border: 'none', fontWeight: 600, fontSize: 12,
                        background: (endpointChecking || testable.length === 0) ? '#555' : '#0052cc',
                        color: '#fff', cursor: (endpointChecking || testable.length === 0) ? 'not-allowed' : 'pointer',
                      }}
                      title={testable.length === 0 ? 'No auto-testable endpoints were found in the code' : 'Probe the selected endpoints from inside Lyric\'s pod'}
                    >
                      {endpointChecking ? 'Checking…' : '🔌 Verify reachability'}
                    </button>
                    {endpointMeta?.podIdentity && (
                      <span style={{ fontSize: 10, color: '#666' }}>
                        probed from <code>{endpointMeta.podIdentity.hostname}</code>
                        {endpointMeta.podIdentity.region ? ` (${endpointMeta.podIdentity.region})` : ''}
                      </span>
                    )}
                  </div>
                </details>
              );
            })()}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={handleAnalyzeConfig}
            disabled={loading || !!currentStackBlockReason}
            title={currentStackBlockReason || ''}
            style={{
              ...btnPrimary,
              backgroundColor: (loading || currentStackBlockReason) ? '#555' : '#ffb74d',
              cursor: (loading || currentStackBlockReason) ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Analyzing…' : 'Analyze App Config →'}
          </button>
          {/* Re-detect button — lets users who just fixed their repo re-run the
              compliance scan without going back to the Repository step. */}
          {currentStackBlockReason && !currentStackHasHardBlock && (
            <button
              onClick={() => detectStack()}
              disabled={loading}
              style={{ ...btnPrimary, backgroundColor: '#1565c0', color: '#fff', fontSize: 13 }}
              title="Re-fetch the repo from GitHub and re-run the compliance check"
            >
              🔄 Re-detect stack
            </button>
          )}
          {/* Override — explicit escape hatch when the developer accepts that the
              app may not run as expected. Opens a Lyric confirm dialog so the
              user has to actively acknowledge the choice. */}
          {currentStackBlockReason && (
            <button
              onClick={() => setDeployAnywayOpen(true)}
              disabled={loading}
              style={{ ...btnPrimary, backgroundColor: 'transparent', color: '#ef9a9a', border: '1px solid #ef5350', fontSize: 12, padding: '8px 14px' }}
              title="Bypass the gate and continue with critical issues unfixed (not recommended)"
            >
              ⚠️ Deploy anyway
            </button>
          )}
          <LyricConfirm
            isOpen={deployAnywayOpen}
            tone="danger"
            title="Deploy anyway?"
            confirmLabel="Bypass gate & continue"
            cancelLabel="Go back"
            message={(
              <>
                Your app has critical issues that will likely prevent it from working correctly
                behind <code>/apps/&lt;name&gt;/*</code>. Lyric will still let you deploy, but features
                like the launch-screen “Open” button may go to a 404 or the wrong page.
              </>
            )}
            onConfirm={() => { setOverrideBlockers(true); setDeployAnywayOpen(false); }}
            onCancel={() => setDeployAnywayOpen(false)}
          />
          <button onClick={() => setStep('validation')} style={btnBack}>← Back</button>
          {currentStackBlockReason && (
            <div style={{ width: '100%', marginTop: 6, fontSize: 11, color: '#ef9a9a' }}>
              🛑 {currentStackBlockReason}
            </div>
          )}
        </div>
      </div>
    );
  };

  // --- Step: App Config / Requirements ---
  const renderRequirementsStep = () => {
    const analysis = configAnalysis;
    // The picker is meaningful only when there are env vars to manage. We use the
    // actual form state (repoEnvVars) rather than analyzer guesses, because the
    // analyzer can flag an authType even when no real env vars exist.
    const skipSecretPicker = repoEnvVars.length === 0;
    return (
      <div>
        <h3 style={{ color: '#4fc3f7', marginBottom: 4 }}>🔐 App Configuration</h3>
        <p style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>
          {skipSecretPicker
            ? 'No credentials or env vars were detected for this app. You can deploy as-is, or add any vars below if the app needs some.'
            : 'Lyric scanned the repository and detected the following configuration requirements. Pick an existing AWS secret or fill in values to create a new one.'}
        </p>

        {/* Auth type badge */}
        {analysis?.authType && (
          <div style={{ display: 'inline-block', backgroundColor: '#1a2e3e', border: '1px solid #4fc3f7', borderRadius: 6, padding: '4px 12px', marginBottom: 16 }}>
            <span style={{ color: '#4fc3f7', fontSize: 12 }}>🔑 Authentication detected: {analysis.authType}</span>
          </div>
        )}

        {/* Secret picker — hidden when there's truly nothing to manage (no detected auth + no user-added vars) */}
        {!skipSecretPicker && renderSecretPicker({ defaultName: formData.secretName || formData.repo, requiredVars: repoEnvVars })}

        {skipSecretPicker && (
          <div style={{ backgroundColor: '#1a2e1a', border: '1px solid #4caf50', borderRadius: 8, padding: 14, marginBottom: 16 }}>
            <div style={{ color: '#81c784', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>✅ No secrets needed</div>
            <p style={{ color: '#aaa', fontSize: 12, margin: '0 0 10px' }}>
              No <code>.env.example</code> and no auth pattern were detected. The deploy will skip AWS Secrets Manager entirely.
              If this is wrong, add the variables your app needs below.
            </p>
            <button
              onClick={() => setRepoEnvVars([{ key: '', value: '' }])}
              style={{ background: 'none', border: '1px dashed #4caf50', borderRadius: 6, color: '#81c784', fontSize: 12, padding: '4px 12px', cursor: 'pointer' }}
            >
              + Add a variable
            </button>
          </div>
        )}

        {/* Env var input form — shown in 'new' mode whenever we're managing secrets */}
        {!skipSecretPicker && secretMode === 'new' && (
          <div style={{ backgroundColor: '#1a2e1a', border: '1px solid #4caf50', borderRadius: 8, padding: 14, marginBottom: 16 }}>
            <div style={{ color: '#81c784', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>🔑 Credentials</div>
            <p style={{ color: '#aaa', fontSize: 12, marginBottom: 12 }}>
              {repoEnvVars.length > 0
                ? 'Variables detected in the repo. Enter values — they will be saved to AWS Secrets Manager on deploy.'
                : 'No variables were auto-detected. Add any credentials this app needs manually.'}
            </p>

            {repoEnvVars.map((ev, i) => {
              const def = configAnalysis?.envVars?.find(d => d.key === ev.key);
              return (
                <div key={i} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input
                      type="text"
                      value={ev.key}
                      onChange={(e) => {
                        const updated = [...repoEnvVars];
                        updated[i] = { ...updated[i], key: e.target.value };
                        setRepoEnvVars(updated);
                      }}
                      placeholder="VARIABLE_NAME"
                      style={{ ...inputStyle, flex: '0 0 220px', fontFamily: 'monospace', fontSize: 12, margin: 0 }}
                    />
                    <input
                      type={def?.isSecret ? 'password' : 'text'}
                      value={ev.value}
                      onChange={(e) => {
                        const updated = [...repoEnvVars];
                        updated[i] = { ...updated[i], value: e.target.value };
                        setRepoEnvVars(updated);
                      }}
                      placeholder={def?.defaultValue || `Enter value`}
                      style={{ ...inputStyle, flex: 1, margin: 0 }}
                    />
                    <button
                      onClick={() => setRepoEnvVars(repoEnvVars.filter((_, idx) => idx !== i))}
                      style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: 16, padding: '0 4px', flexShrink: 0 }}
                    >✕</button>
                  </div>
                  {def?.description && <div style={{ color: '#666', fontSize: 11, marginTop: 2 }}>{def.description}</div>}
                </div>
              );
            })}

            <button
              onClick={() => setRepoEnvVars([...repoEnvVars, { key: '', value: '' }])}
              style={{ background: 'none', border: '1px dashed #4caf50', borderRadius: 6, color: '#81c784', fontSize: 12, padding: '4px 12px', cursor: 'pointer', marginBottom: 12 }}
            >
              + Add variable
            </button>
          </div>
        )}

        {/* Warnings */}
        {analysis?.warnings?.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            {analysis.warnings.map((w, i) => {
              const authMatch = w.match(/^Authentication detected: (.+) — ensure credentials are configured$/);
              if (authMatch) {
                const authType = authMatch[1];
                const authExplanations = {
                  'custom-auth': { what: 'A file with "auth" in the name was found in the repo. The app likely handles authentication internally.', action: 'Review the app\'s .env.example or README to find out what credentials or secrets it needs, then add them as environment variables in the next step.' },
                  'github-oauth': { what: 'Your app uses GitHub for user login (OAuth).', action: 'You\'ll need a GitHub OAuth App — create one at GitHub → Settings → Developer settings → OAuth Apps. You\'ll get a Client ID and Client Secret to add as environment variables.' },
                  'okta': { what: 'Your app uses Okta for Single Sign-On (SSO).', action: 'Grab the Client ID, Client Secret, and Okta domain from your Okta admin console and add them as environment variables.' },
                  'auth0': { what: 'Your app uses Auth0 for authentication.', action: 'Create or locate an Auth0 application and add the Client ID, Client Secret, and your Auth0 domain as environment variables.' },
                  'token-based': { what: 'Your app uses API tokens or JWT for authentication.', action: 'Make sure any signing secrets (e.g. JWT_SECRET) or API keys are set in your environment variables in the next step.' },
                  'passport': { what: 'Your app uses Passport.js for authentication.', action: 'The required credentials depend on the Passport strategy (local, OAuth, JWT, etc.). Check the app\'s .env.example or README for what variables are needed.' },
                };
                const info = authExplanations[authType] || { what: `Authentication type "${authType}" was detected.`, action: 'Review the app\'s documentation to determine what credentials are required and add them as environment variables.' };
                return <ExpandableWarning key={i} label={`⚠️ Authentication detected: ${authType} — ensure credentials are configured`} what={info.what} action={info.action} />;
              }
              return (
                <div key={i} style={{ color: '#ffcc80', fontSize: 13, marginBottom: 4, padding: '6px 10px', backgroundColor: '#3e2723', border: '1px solid #ff9800', borderRadius: 6 }}>⚠️ {w}</div>
              );
            })}
          </div>
        )}

        {/* Seed files */}
        {analysis?.seedFiles?.length > 0 && (
          <div style={{ backgroundColor: '#1a2e1a', border: '1px solid #4caf50', borderRadius: 8, padding: 12, marginBottom: 16 }}>
            <h4 style={{ color: '#81c784', fontSize: 13, margin: '0 0 6px' }}>📄 Seed / Config Files</h4>
            {analysis.seedFiles.map((sf, i) => (
              <div key={i} style={{ color: '#aaa', fontSize: 12, marginBottom: 2 }}>
                <code style={{ color: '#81c784' }}>{sf.path}</code> — {sf.description}
              </div>
            ))}
            <p style={{ color: '#666', fontSize: 11, marginTop: 6, marginBottom: 0 }}>
              These files will be used as-is from the repo. Customize them in the repo before deploying.
            </p>
          </div>
        )}

        {/* No config needed — only show when truly nothing was detected */}
        {(!analysis?.envVars?.length && !analysis?.seedFiles?.length && !analysis?.authType) && (
          <div style={{ backgroundColor: '#1a2e1a', border: '1px solid #4caf50', borderRadius: 8, padding: 16, textAlign: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
            <p style={{ color: '#81c784', fontSize: 14, margin: 0 }}>No special configuration detected — ready to deploy!</p>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setStep('config')} style={{ ...btnPrimary, backgroundColor: '#ce93d8' }}>
            Deploy Settings →
          </button>
          <button onClick={() => setStep('stack')} style={btnBack}>← Back</button>
        </div>
      </div>
    );
  };

  // --- Step 4: Configuration ---
  const renderConfigStep = () => (
    <div>
      <h3 style={{ color: '#ce93d8', marginBottom: 8 }}>⚙️ Deployment Configuration</h3>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <FormField label="Build Command" value={formData.buildCommand} onChange={(v) => updateField('buildCommand', v)} placeholder="npm run build" />
        <FormField label="Start Command" value={formData.startCommand} onChange={(v) => updateField('startCommand', v)} placeholder="npm start" />
        <div>
          <label style={labelStyle}>Deploy Target</label>
          <select
            value={formData.deployTarget}
            onChange={(e) => updateField('deployTarget', e.target.value)}
            style={inputStyle}
          >
            <option value="local">� Local (Node.js)</option>
            <option value="github-actions">⚡ GitHub Actions Dispatch</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Environment File</label>
          {envFilesLoading ? (
            <select style={{ ...inputStyle, color: '#888' }} disabled>
              <option>loading .env files…</option>
            </select>
          ) : envFiles.length === 0 ? (
            <select style={{ ...inputStyle, color: '#888' }} disabled>
              <option>no .env files available</option>
            </select>
          ) : (
            <select
              value={formData.selectedEnvFile}
              onChange={(e) => updateField('selectedEnvFile', e.target.value)}
              style={{ ...inputStyle, color: formData.selectedEnvFile ? '#e0e0e0' : '#888' }}
            >
              <option value="">— none (use detected config) —</option>
              {envFiles.map((f) => (
                <option key={f} value={f} style={{ color: '#e0e0e0' }}>{f}</option>
              ))}
            </select>
          )}
          <p style={{ color: '#888', fontSize: 11, margin: '4px 0 0' }}>
            {envFilesError
              ? `⚠️ ${envFilesError}`
              : 'Its KEY=value pairs become the app’s environment (overrides detected values).'}
          </p>
        </div>
        <div>
          <label style={labelStyle}>App Version</label>
          <input
            type="text"
            value={formData.version}
            onChange={(e) => updateField('version', e.target.value)}
            placeholder={`auto → ${nextAutoVersion(formData.currentVersion)}`}
            style={inputStyle}
          />
          <p style={{ color: '#888', fontSize: 11, margin: '4px 0 0' }}>
            {formData.currentVersion
              ? `Current: v${formData.currentVersion}. `
              : 'New app starts at 1.0.0. '}
            Leave blank to auto-increment the minor version, or type an exact
            version (e.g. 2.0.0) to set it.
          </p>
        </div>
      </div>


      {/* Group Visibility */}
      <div style={{ marginTop: 20, padding: '16px', backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: 8 }}>
        <label style={{ ...labelStyle, marginBottom: 10, display: 'block' }}>👥 Who can see this app?</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: '#e0e0e0', fontSize: 14 }}>
            <input
              type="radio"
              name="visibilityMode"
              value="everyone"
              checked={formData.visibilityMode === 'everyone'}
              onChange={() => updateField('visibilityMode', 'everyone')}
              style={{ accentColor: '#ce93d8' }}
            />
            Everyone in my organization
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: '#e0e0e0', fontSize: 14 }}>
            <input
              type="radio"
              name="visibilityMode"
              value="specific"
              checked={formData.visibilityMode === 'specific'}
              onChange={() => updateField('visibilityMode', 'specific')}
              style={{ accentColor: '#ce93d8' }}
            />
            Specific Groups
          </label>
        </div>

        {formData.visibilityMode === 'specific' && availableGroups.length > 0 && (
          <div style={{ marginTop: 12, paddingLeft: 4 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {availableGroups.map(group => {
                const checked = formData.allowedGroups.includes(group);
                return (
                  <label
                    key={group}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                      padding: '6px 12px', borderRadius: 20, fontSize: 13, fontWeight: 500,
                      backgroundColor: checked ? '#2a1a3e' : '#222',
                      border: `1px solid ${checked ? '#ce93d8' : '#444'}`,
                      color: checked ? '#ce93d8' : '#aaa',
                      transition: 'all 0.15s',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = checked
                          ? formData.allowedGroups.filter(g => g !== group)
                          : [...formData.allowedGroups, group];
                        updateField('allowedGroups', next);
                      }}
                      style={{ display: 'none' }}
                    />
                    {checked ? '✓ ' : ''}{group}
                  </label>
                );
              })}
            </div>
            {formData.allowedGroups.length === 0 && (
              <p style={{ color: '#ef5350', fontSize: 11, margin: '8px 0 0' }}>Select at least one group.</p>
            )}
          </div>
        )}

        {groupsDemoMode && (
          <p style={{ color: '#666', fontSize: 11, margin: '10px 0 0', fontStyle: 'italic' }}>
            ⚠️ Demo mode — these are sample groups. Real Okta group names will appear here once your admin adds the groups claim to the access token.
          </p>
        )}
      </div>

      {/* Owner-only pre-release gate */}
      <div style={{ marginTop: 16, padding: '16px', backgroundColor: '#1a1a2e', border: `1px solid ${formData.ownerOnly ? '#ffb74d' : '#333'}`, borderRadius: 8 }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', color: '#e0e0e0', fontSize: 14 }}>
          <input
            type="checkbox"
            checked={!formData.ownerOnly}
            onChange={(e) => updateField('ownerOnly', !e.target.checked)}
            style={{ accentColor: '#ce93d8', marginTop: 3 }}
          />
          <span>
            🚀 Release this app to my team
            <span style={{ display: 'block', color: '#888', fontSize: 12, marginTop: 4 }}>
              On by default — everyone allowed by the visibility setting above can open the app.
              <strong style={{ color: '#ffb74d' }}> Uncheck</strong> to keep it private to you (the
              owner) for production testing before releasing it to all teams.
            </span>
          </span>
        </label>
        {formData.ownerOnly && (
          <div style={{ paddingLeft: 30 }}>
            <p style={{ color: '#ffb74d', fontSize: 12, margin: '10px 0 0' }}>
              🔒 Private — only you, your co-admins, named testers, and the allowed users below can see this app until you release it.
            </p>
            <div style={{ marginTop: 12 }}>
              <AllowedUsersEditor bare emails={formData.allowedUsers} onChange={(next) => updateField('allowedUsers', next)} />
            </div>
          </div>
        )}
      </div>

      {/* Okta token forwarding (opt-in) */}
      <div style={{ marginTop: 16, padding: '16px', backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: 8 }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', color: '#e0e0e0', fontSize: 14 }}>
          <input
            type="checkbox"
            checked={!!formData.forwardOktaToken}
            onChange={(e) => updateField('forwardOktaToken', e.target.checked)}
            style={{ accentColor: '#ce93d8', marginTop: 3 }}
          />
          <span>
            🔑 Forward my Okta sign-in to this app
            <span style={{ display: 'block', color: '#888', fontSize: 12, marginTop: 4 }}>
              Injects the signed-in user's Okta token so the app's frontend can call its own
              backend (e.g. an API Gateway) as that user. Only enable for apps you trust — they
              will be able to call Okta-protected APIs on the user's behalf. For calls, use{' '}
              <code style={{ color: '#ce93d8' }}>await window.__lyricGetToken()</code> (auto-refreshes
              so idle pages don't break) rather than caching{' '}
              <code style={{ color: '#ce93d8' }}>window.__LYRIC_OKTA_TOKEN__</code>.
            </span>
          </span>
        </label>
      </div>

      <CoAdminsEditor emails={formData.coAdmins} onChange={(next) => updateField('coAdmins', next)} />

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button
          onClick={() => setStep('review')}
          disabled={formData.visibilityMode === 'specific' && formData.allowedGroups.length === 0}
          style={{ ...btnPrimary, backgroundColor: formData.visibilityMode === 'specific' && formData.allowedGroups.length === 0 ? '#555' : '#ce93d8' }}
        >
          Review & Deploy →
        </button>
        <button onClick={() => setStep('requirements')} style={btnBack}>← Back</button>
      </div>
    </div>
  );

  // --- Step 5: Review & Deploy ---
  const renderReviewStep = () => {
    const chosenExisting = (existingSource === 'manual' ? manualSecretName : selectedExistingSecret).trim();
    const secretSummary = secretMode === 'existing'
      ? (chosenExisting ? `Use existing — tools/lyric/${chosenExisting}` : '⚠️ No secret selected')
      : `Create new — tools/lyric/${(formData.secretName || formData.repo || '').trim() || formData.repo}`;
    const hasConflict = existingRepoApps.length > 0;
    const newAppSlug = `lyric-${slugifyName(formData.repo)}-${slugifyName(formData.branch)}`;
    const replaceTarget = existingRepoApps.find(a => a.appName === replaceTargetApp);
    // The conflict list only applies to the repo deploy path. For that path we
    // must NOT let the user deploy until the list has resolved (loaded) — a
    // pending/failed list could otherwise fall back to legacy name-derivation
    // and clobber an existing app.
    const conflictCheckApplies = path === 'repo';
    const listPending = conflictCheckApplies && !existingRepoAppsLoaded;
    const listFailed = conflictCheckApplies && !!existingRepoAppsError;
    const deployDisabled =
      loading ||
      listPending ||
      listFailed ||
      (hasConflict && deployMode === 'replace' && !replaceTargetApp);
    const deployLabel = loading
      ? '🚀 Deploying…'
      : listPending
      ? '⏳ Checking existing apps…'
      : listFailed
      ? '⚠️ Can’t check existing apps'
      : hasConflict && deployMode === 'replace'
      ? '🔁 Replace App'
      : hasConflict
      ? '➕ Deploy New App'
      : '🚀 Deploy Now';
    return (
      <div>
        <h3 style={{ color: '#4fc3f7', marginBottom: 8 }}>🚀 Review & Deploy</h3>
        <div style={{ ...cardStyle, fontSize: 13, marginBottom: 16 }}>
          <ReviewRow label="Repository" value={`${formData.owner}/${formData.repo}`} />
          <ReviewRow label="Branch" value={formData.branch} />
          <ReviewRow label="Build Command" value={formData.buildCommand || '(none)'} />
          <ReviewRow label="Start Command" value={formData.startCommand || '(none)'} />
          <ReviewRow label="Deploy Target" value={formData.deployTarget === 'local' ? '🐳 Local (Node.js)' : '⚡ GitHub Actions'} />
          <ReviewRow label="Environment File" value={formData.selectedEnvFile || 'none (use detected config)'} />
          <ReviewRow label="Version" value={formData.version ? `v${formData.version} (set)` : `v${nextAutoVersion(formData.currentVersion)} (auto)`} />
          <ReviewRow label="AWS Secret" value={secretSummary} />
        </div>

        {Object.entries(approvedFixes).some(([, on]) => on) && (
          <div style={{ marginBottom: 16, padding: 14, backgroundColor: '#10251a', border: '1px solid #4caf50', borderRadius: 6 }}>
            <div style={{ color: '#81c784', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
              🌿 Safe test-branch deployment
            </div>
            <div style={{ color: '#ddd', fontSize: 12, lineHeight: 1.5 }}>
              When you deploy, Lyric will create a new <code>lyric/deploy-fix-*</code> branch from{' '}
              <code>{formData.branch}</code>, commit the approved fixes there, and open a draft pull request.
              Lyric will deploy the temporary branch as a separate test app. The source branch will not change
              unless a developer reviews and merges the pull request.
            </div>
          </div>
        )}

        {listPending && (
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#0f1419', border: '1px solid #2a3a4a', borderRadius: 6, fontSize: 12, color: '#9fa8da' }}>
            ⏳ Checking whether you already have an app deployed from <code style={{ color: '#7986cb' }}>{formData.repo}</code>…
          </div>
        )}

        {listFailed && (
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#3a1a1a', border: '1px solid #ef5350', borderRadius: 6, fontSize: 12, color: '#ef9a9a' }}>
            <div style={{ marginBottom: 8 }}>
              ⚠️ Couldn’t load your existing apps — {existingRepoAppsError}. Deploy is blocked so we don’t
              accidentally overwrite an app you already have running from this repo.
            </div>
            <button
              onClick={() => setExistingReloadNonce(n => n + 1)}
              style={{ ...btnBack, borderColor: '#ef5350', color: '#ef9a9a' }}
            >
              ↻ Retry
            </button>
          </div>
        )}

        {hasConflict && !listPending && !listFailed && (
          <div style={{ marginBottom: 16, padding: 14, backgroundColor: '#0f1419', border: '1px solid #5c6bc0', borderRadius: 6 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#9fa8da', marginBottom: 6 }}>
              🌿 You already deploy <code style={{ color: '#7986cb' }}>{formData.repo}</code>
            </div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
              {existingRepoApps.length} app{existingRepoApps.length > 1 ? 's' : ''} from this repo{' '}
              {existingRepoApps.length > 1 ? 'are' : 'is'} already deployed. Replace one with branch{' '}
              <code style={{ color: '#ccc' }}>{formData.branch}</code>, or run this branch as a new app alongside them.
            </div>

            {/* Replace an existing app */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
              <input
                type="radio"
                name="deploy-mode"
                value="replace"
                checked={deployMode === 'replace'}
                onChange={() => { setDeployMode('replace'); if (!replaceTargetApp && existingRepoApps[0]) setReplaceTargetApp(existingRepoApps[0].appName); }}
                style={{ marginTop: 3 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: '#ddd', fontWeight: 600 }}>Replace an existing app</div>
                <div style={{ fontSize: 10, color: '#777', marginBottom: 6 }}>
                  Redeploys in place — the app keeps its URL and settings, now serving <code>{formData.branch}</code>.
                </div>
                <select
                  value={replaceTargetApp}
                  disabled={deployMode !== 'replace'}
                  onChange={(e) => setReplaceTargetApp(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', backgroundColor: '#1a1f2a', color: '#ccc', border: '1px solid #2a3a4a', borderRadius: 4, fontSize: 12, opacity: deployMode === 'replace' ? 1 : 0.5 }}
                >
                  {existingRepoApps.map(a => (
                    <option key={a.appName} value={a.appName}>
                      {a.displayName || a.appName} — branch {a.branch}{a.status ? ` (${a.status})` : ''}
                    </option>
                  ))}
                </select>
                {deployMode === 'replace' && replaceTarget && replaceTarget.branch === formData.branch && (
                  <div style={{ fontSize: 10, color: '#81c784', marginTop: 4 }}>
                    This app is already on <code>{formData.branch}</code> — this is a straight redeploy.
                  </div>
                )}
              </div>
            </label>

            {/* Create a new app */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
              <input
                type="radio"
                name="deploy-mode"
                value="new"
                checked={deployMode === 'new'}
                onChange={() => setDeployMode('new')}
                style={{ marginTop: 3 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: '#ddd', fontWeight: 600 }}>Create a new app</div>
                <div style={{ fontSize: 10, color: '#777', marginBottom: 6 }}>
                  Runs alongside the others at{' '}
                  <code style={{ color: '#9fa8da' }}>/apps/{newAppSlug}/</code>
                </div>
                <input
                  type="text"
                  value={newAppDisplayName}
                  disabled={deployMode !== 'new'}
                  onChange={(e) => setNewAppDisplayName(e.target.value)}
                  placeholder={`${formData.repo} (${formData.branch})`}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', backgroundColor: '#1a1f2a', color: '#ccc', border: '1px solid #2a3a4a', borderRadius: 4, fontSize: 12, opacity: deployMode === 'new' ? 1 : 0.5 }}
                />
                <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>Display name shown on the deployment card.</div>
              </div>
            </label>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleDeploy}
            disabled={deployDisabled}
            style={{ ...btnPrimary, backgroundColor: deployDisabled ? '#555' : '#4caf50', color: '#fff', fontSize: 15, padding: '12px 32px' }}
          >
            {deployLabel}
          </button>
          <button onClick={() => setStep('config')} style={btnBack}>← Back</button>
        </div>
      </div>
    );
  };

  // =============================================
  // BUILD FROM SCRATCH STEPS
  // =============================================

  // NOTE: the per-feature picker was removed from the Architecture step for now
  // (features will be reintroduced later). `formData.features` stays as an empty
  // array so the build/scaffold API contract is unchanged until then.

  const APP_TYPES = [
    { id: 'web-api', label: '🌐 Web API / Backend Service', desc: 'REST or GraphQL API server' },
    { id: 'fullstack', label: '🖥️ Full-Stack Web App', desc: 'Frontend + backend together' },
    { id: 'cli-tool', label: '⌨️ CLI Tool', desc: 'Command-line application' },
    { id: 'data-pipeline', label: '📊 Data Pipeline', desc: 'ETL or data processing service' },
    { id: 'ai-agent', label: '🤖 AI Agent / Bot', desc: 'Autonomous AI-powered service' },
    { id: 'microservice', label: '🔧 Microservice', desc: 'Focused single-purpose service' },
    { id: 'ios-swiftui', label: '📱 iOS (SwiftUI)', desc: 'Xcode project you clone and open — not deployed' },
  ];

  // iOS projects are a source deliverable, not something Lyric hosts, so the
  // wizard skips env vars and deploy entirely when this type is selected.
  const isIosBuild = formData.appType === 'ios-swiftui';

  const SWIFT_VERSION_OPTIONS = [
    { id: '5.0', label: 'Swift 5', note: 'Recommended — most reliable' },
    { id: '6.0', label: 'Swift 6', note: 'Strict concurrency' },
  ];

  const IOS_TARGET_OPTIONS = [
    { id: '17.0', label: 'iOS 17', note: 'Recommended — widest support' },
    { id: '18.0', label: 'iOS 18', note: '' },
    { id: '26.0', label: 'iOS 26', note: 'Latest only' },
  ];

  const LANGUAGE_OPTIONS = [
    { id: 'javascript', label: 'JavaScript (Node.js)' },
    { id: 'typescript', label: 'TypeScript (Node.js)' },
    { id: 'python', label: 'Python' },
    { id: 'go', label: 'Go' },
  ];

  const SERVICE_VAR_PREFIXES = {
    jira: ['JIRA_'],
    confluence: ['CONFLUENCE_'],
    box: ['BOX_'],
    'google-docs': ['LYRIC_DOCS_'],
    slack: ['SLACK_'],
    postgresql: ['DATABASE_URL', 'DB_', 'POSTGRES'],
    mongodb: ['MONGODB', 'MONGO_'],
    redis: ['REDIS_'],
    mysql: ['DATABASE_URL', 'MYSQL_'],
    'custom-api': ['EXTERNAL_API_', 'API_KEY', 'API_BASE'],
  };

  // Catalog for the "Add Integration" picker (Connected Services step). Each
  // entry's `fields` are non-secret metadata captured up front and stored in
  // formData.serviceConfig[id] — they flow into buildScaffoldPrompt() to tell
  // the AI code-gen exactly how to wire the client. Real secrets (API tokens,
  // DB passwords, etc.) are never captured here — they're collected after
  // scaffolding via the existing secret picker, once the generated
  // .env.example names the required vars.
  const SERVICE_CATALOG = [
    {
      id: 'confluence', category: 'integration', label: 'Confluence', icon: '📘',
      description: 'Read/write Confluence pages via REST API (Basic Auth with an email + API token).',
      fields: [
        { key: 'email', label: 'Confluence account email', placeholder: 'you@ischemaview.com' },
      ],
    },
    {
      id: 'box', category: 'integration', label: 'Box', icon: '📦',
      description: 'Access Box files/folders as a shared service account (Client Credentials Grant) — no per-user login.',
      fields: [],
    },
    {
      id: 'google-docs', category: 'integration', label: 'Google Docs', icon: '📄',
      description: "Fetch a Google Doc's text through Lyric's relay (one shared, admin-configured Google service account — no per-app Google OAuth). Enable it for this app after it's deployed via Edit app → Google Docs API.",
      fields: [],
    },
    {
      id: 'slack', category: 'integration', label: 'Slack', icon: '💬',
      description: 'Post messages to a Slack channel via an Incoming Webhook.',
      fields: [
        { key: 'webhookUrl', label: 'Webhook URL (optional example)', placeholder: 'https://hooks.slack.com/services/…' },
      ],
    },
    {
      id: 'postgresql', category: 'database', label: 'PostgreSQL', icon: '🐘',
      description: 'Relational database via a standard connection string.',
      fields: [
        { key: 'connectionString', label: 'Connection string (optional example)', placeholder: 'postgres://user:pass@host:5432/db' },
      ],
    },
    {
      id: 'mysql', category: 'database', label: 'MySQL', icon: '🐬',
      description: 'Relational database via a standard connection string.',
      fields: [
        { key: 'connectionString', label: 'Connection string (optional example)', placeholder: 'mysql://user:pass@host:3306/db' },
      ],
    },
    {
      id: 'mongodb', category: 'database', label: 'MongoDB', icon: '🍃',
      description: 'Document database via a standard connection URI.',
      fields: [
        { key: 'connectionString', label: 'Connection URI (optional example)', placeholder: 'mongodb+srv://user:pass@cluster/db' },
      ],
    },
    {
      id: 'redis', category: 'database', label: 'Redis', icon: '🔴',
      description: 'Key-value cache/store via a standard connection URL.',
      fields: [
        { key: 'connectionString', label: 'Connection URL (optional example)', placeholder: 'redis://user:pass@host:6379' },
      ],
    },
    {
      id: 'custom-api', category: 'custom', label: 'Custom API', icon: '⚡',
      description: 'Any other external REST API, called with a bearer/API-key header.',
      fields: [
        { key: 'name', label: 'Service name', placeholder: 'e.g. Internal Pricing API' },
        { key: 'baseUrl', label: 'Base URL (optional example)', placeholder: 'https://api.example.com' },
      ],
    },
  ];

  const isServiceVar = (key) => {
    const upper = key.toUpperCase();
    return (formData.selectedServices || []).some(svc =>
      (SERVICE_VAR_PREFIXES[svc] || []).some(prefix => upper.startsWith(prefix.toUpperCase()))
    );
  };

  const parseEnvExample = (content) => {
    const vars = [];
    let lastComment = '';
    for (const line of content.split('\n')) {
      const trimmed = line.trim();
      if (trimmed.startsWith('#')) {
        lastComment = trimmed.slice(1).trim();
      } else if (trimmed.includes('=')) {
        const eqIdx = trimmed.indexOf('=');
        const key = trimmed.slice(0, eqIdx).trim();
        const example = trimmed.slice(eqIdx + 1).trim();
        if (key) {
          vars.push({ key, example, comment: lastComment, value: '' });
          lastComment = '';
        }
      } else {
        lastComment = '';
      }
    }
    return vars;
  };

  const handleBuildSubmit = async (overrideToken) => {
    setLoading(true);
    setError('');
    setDeployResult(null);
    setScaffoldResult(null);
    setScaffoldPhase(null);
    setDeployAppResult(null);
    setDeployPhase(null);
    setShowBuildConfirm(false);
    setStep('building');

    let repoData = null;

    try {
      const apiUrl = getApiBase();
      const overrideStr = typeof overrideToken === 'string' ? overrideToken : '';
      const tokenToSend = (overrideStr || githubToken || '').trim();
      const headers = { 'Content-Type': 'application/json' };
      if (tokenToSend) headers['X-User-GitHub-Token'] = tokenToSend;

      // ── Phase 1: create the GitHub repo ──────────────────────────────────
      const res = await authenticatedFetch(`${apiUrl}/api/deploy/build-from-scratch`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          appName: formData.appName,
          appDescription: formData.appDescription,
          appType: formData.appType,
          language: formData.language,
          features: formData.features,
          jiraProjectKey: (formData.useJira && formData.jiraProjectKey) || undefined,
          jiraIssueType: (formData.useJira && formData.jiraIssueKey) ? formData.jiraIssueType : undefined,
          jiraIssueKey: (formData.useJira && formData.jiraIssueKey) || undefined,
          jiraIssueSummary: (formData.useJira && formData.jiraIssueKey) ? formData.jiraIssueSummary : undefined,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok && data?.requiresGitHubToken) {
        setTokenSetup(data.tokenSetup || null);
        setError(data.error || 'A GitHub Personal Access Token is required.');
        setStep('github-token');
        return;
      }
      if (!res.ok) throw new Error(data?.error || 'Build request failed');

      if (tokenToSend && rememberToken) {
        try { localStorage.setItem(GITHUB_TOKEN_STORAGE_KEY, tokenToSend); } catch { /* ignore */ }
      } else if (!rememberToken) {
        try { localStorage.removeItem(GITHUB_TOKEN_STORAGE_KEY); } catch { /* ignore */ }
      }

      repoData = data;
      setTokenSetup(null);
      setDeployResult(data);
      setLoading(false);

      // ── Phase 2: AI scaffold — generate code and push to repo ────────────
      setScaffoldPhase('generating');
      const scaffoldRes = await authenticatedFetch(`${apiUrl}/api/deploy/scaffold`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          org: data.repo?.owner || 'ischemaview',
          repoName: data.repo?.name || data.config?.repoSlug,
          appName: formData.appName,
          appDescription: formData.appDescription,
          appType: formData.appType,
          language: formData.language,
          features: formData.features,
          selectedServices: isIosBuild ? [] : formData.selectedServices,
          serviceConfig: isIosBuild ? {} : formData.serviceConfig,
          mockupHtml: formData.mockupHtml || undefined,
          swiftVersion: isIosBuild ? formData.swiftVersion : undefined,
          deploymentTarget: isIosBuild ? formData.deploymentTarget : undefined,
          jiraProjectKey: (formData.useJira && formData.jiraProjectKey) || undefined,
          jiraIssueType: (formData.useJira && formData.jiraIssueKey) ? formData.jiraIssueType : undefined,
          jiraIssueKey: (formData.useJira && formData.jiraIssueKey) || undefined,
          jiraIssueSummary: (formData.useJira && formData.jiraIssueKey) ? formData.jiraIssueSummary : undefined,
          selectedModel,
        }),
      });

      const scaffoldData = await scaffoldRes.json().catch(() => ({}));
      if (!scaffoldRes.ok) throw new Error(scaffoldData?.error || 'Scaffold failed');

      setScaffoldResult(scaffoldData);
      setScaffoldPhase('done');

      // iOS builds are finished here: the deliverable is the repo itself, so
      // there are no env vars to collect and nothing for Lyric to deploy.
      if (scaffoldData.deployable === false) {
        setBuildEnvVars([]);
        setBuildEnvPhase('skipped');
        setLoading(false);
        return;
      }

      // ── Phase 3: fetch .env.example and pause for user to fill in env vars ─
      const owner3 = repoData.repo?.owner || 'ischemaview';
      const repo3 = repoData.repo?.name || repoData.config?.repoSlug;
      const branch3 = repoData.repo?.defaultBranch || 'main';
      let envVarList = [];
      try {
        const envExRes = await fetch(
          `https://api.github.com/repos/${owner3}/${repo3}/contents/.env.example?ref=${branch3}`,
          { headers: { Accept: 'application/vnd.github.v3+json', ...(tokenToSend ? { Authorization: `Bearer ${tokenToSend}` } : {}) } }
        );
        if (envExRes.ok) {
          const envExData = await envExRes.json();
          const raw = atob(envExData.content.replace(/\n/g, ''));
          envVarList = parseEnvExample(raw);
        }
      } catch { /* .env.example missing — skip */ }

      setBuildEnvVars(envVarList.filter(v => v.key !== 'PORT'));
      setBuildEnvPhase('waiting');
      // Execution pauses here — handleDeployWithEnvVars() continues Phase 4

    } catch (err) {
      setError(err.message);
      if (!repoData) {
        setStep('build-review');
      } else if (!scaffoldResult) {
        setScaffoldPhase('error');
      } else {
        setDeployPhase('error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeployWithEnvVars = async () => {
    const apiUrl = getApiBase();
    const secretsUrl = process.env.REACT_APP_SECRET_SERVER || apiUrl;
    const tokenToSend = (githubToken || '').trim();
    const headers = { 'Content-Type': 'application/json' };
    if (tokenToSend) headers['X-User-GitHub-Token'] = tokenToSend;

    const owner = deployResult?.repo?.owner || 'ischemaview';
    const repo = deployResult?.repo?.name || deployResult?.config?.repoSlug;
    const branch = deployResult?.repo?.defaultBranch || 'main';

    setBuildEnvPhase('deploying');
    setDeployPhase('deploying');
    setError('');
    try {
      // App has no credential requirements iff nothing was scaffolded into buildEnvVars
      // (no .env.example, no service config). In that case skip AWS entirely.
      const filledVars = buildEnvVars.filter(e => e.key && e.value.trim());
      const skipSecret = secretMode === 'new' && filledVars.length === 0;

      let secretName = '';
      if (!skipSecret) {
        const defaultSecret = `lyric-${(deployResult?.repo?.name || repo || '').toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
        secretName = resolveSecretName(defaultSecret);
        if (!secretName) throw new Error('Pick an existing AWS secret before deploying.');

        if (secretMode === 'new') {
          const ok = await writeSecretIfNew(secretName, filledVars);
          if (!ok) setError('Warning: Could not save to AWS Secrets Manager — deploy continuing without write.');
        } else {
          const merge = collectMissingKeyMergeVars();
          if (merge.length > 0) {
            const ok = await mergeIntoExistingSecret(secretName, merge);
            if (!ok) setError(`Warning: Could not update tools/lyric/${secretName} with new keys — deploy continuing.`);
          }
        }
      }

      const portRes = await authenticatedFetch(`${apiUrl}/api/deploy/free-port`);
      const portData = await portRes.json().catch(() => ({}));
      if (!portRes.ok) throw new Error(portData?.error || 'Could not find a free port');

      const deployRes = await authenticatedFetch(`${apiUrl}/api/deploy/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          owner, repo, branch,
          port: portData.port,
          startCommand: scaffoldResult?.startCommand || undefined,
          envVars: skipSecret ? [] : (secretMode === 'new' ? filledVars : []),
          subdir: formData.stackInfo?.subdir || '',
          useLyricEnv: !skipSecret,
          ...(skipSecret ? {} : { secretName }),
        }),
      });
      const deployData = await deployRes.json().catch(() => ({}));
      if (!deployRes.ok) throw new Error(deployData?.error || 'Deploy failed');

      setDeployAppResult(deployData);
      setDeployPhase('starting');

      const targetAppName = deployData.appName;
      const POLL_INTERVAL = 5000;
      const POLL_TIMEOUT = 180_000;
      const pollStart = Date.now();

      const runningEntry = await new Promise((resolve, reject) => {
        const tick = async () => {
          if (Date.now() - pollStart > POLL_TIMEOUT)
            return reject(new Error('App did not start within 3 minutes. Check the server logs for errors in the generated app.'));
          try {
            const listRes = await authenticatedFetch(`${apiUrl}/api/deploy/list`);
            const listData = await listRes.json().catch(() => ({ apps: [] }));
            const match = (listData.apps || []).find(a => a.appName === targetAppName);
            if (match?.status === 'running') return resolve(match);
            if (match?.status === 'error') return reject(new Error(match.error || 'The app process crashed on startup. Check server logs for details.'));
          } catch { /* keep polling */ }
          setTimeout(tick, POLL_INTERVAL);
        };
        setTimeout(tick, POLL_INTERVAL);
      });

      setDeployPhase('done');

      try {
        const pins = JSON.parse(localStorage.getItem('lyric-pinned-deployments') || '[]');
        const newPin = {
          appName: targetAppName, owner, repo, branch,
          url: runningEntry.url || deployData.url,
          port: portData.port,
          deployId: deployData.deployId,
          pinnedAt: new Date().toISOString(),
        };
        localStorage.setItem('lyric-pinned-deployments', JSON.stringify([newPin, ...pins.filter(p => p.appName !== newPin.appName)]));
      } catch { /* ignore */ }

      // Persist credentials so Edit Existing App can pre-load them
      try {
        const creds = buildEnvVars.filter(e => e.key && e.value.trim());
        if (creds.length > 0)
          localStorage.setItem(`lyric-env-${owner}/${repo}`, JSON.stringify(creds.map(e => ({ key: e.key, value: e.value }))));
      } catch { /* ignore */ }
    } catch (err) {
      setError(err.message);
      setDeployPhase('error');
      setBuildEnvPhase(null);
    }
  };

  // Retry just the deploy phase after scaffold succeeded but deploy crashed
  const handleRetryDeploy = async () => {
    setError('');
    setDeployPhase('deploying');
    setDeployAppResult(null);
    try {
      const apiUrl = getApiBase();
      const pat = (githubToken || '').trim();
      const headers = { 'Content-Type': 'application/json' };
      if (pat) headers['X-User-GitHub-Token'] = pat;

      const portRes = await authenticatedFetch(`${apiUrl}/api/deploy/free-port`);
      const portData = await portRes.json().catch(() => ({}));
      if (!portRes.ok) throw new Error(portData?.error || 'Could not find a free port');

      const owner = deployResult?.repo?.owner || 'ischemaview';
      const repo = deployResult?.repo?.name;
      const branch = deployResult?.repo?.defaultBranch || 'main';

      const deployRes = await authenticatedFetch(`${apiUrl}/api/deploy/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          owner, repo, branch,
          port: portData.port,
          startCommand: scaffoldResult?.startCommand || undefined,
          subdir: formData.stackInfo?.subdir || '',
        }),
      });
      const deployData = await deployRes.json().catch(() => ({}));
      if (!deployRes.ok) throw new Error(deployData?.error || 'Deploy failed');

      setDeployAppResult(deployData);
      setDeployPhase('starting');

      const targetAppName = deployData.appName;
      const POLL_INTERVAL = 5000;
      const POLL_TIMEOUT = 180_000;
      const pollStart = Date.now();

      const runningEntry = await new Promise((resolve, reject) => {
        const tick = async () => {
          if (Date.now() - pollStart > POLL_TIMEOUT) {
            return reject(new Error('App did not start within 3 minutes. Check server logs.'));
          }
          try {
            const listRes = await authenticatedFetch(`${apiUrl}/api/deploy/list`);
            const listData = await listRes.json().catch(() => ({ apps: [] }));
            const match = (listData.apps || []).find(a => a.appName === targetAppName);
            if (match?.status === 'running') return resolve(match);
            if (match?.status === 'error') return reject(new Error(match.error || 'App crashed on startup.'));
          } catch { /* keep polling */ }
          setTimeout(tick, POLL_INTERVAL);
        };
        setTimeout(tick, POLL_INTERVAL);
      });

      setDeployPhase('done');

      try {
        const pins = JSON.parse(localStorage.getItem('lyric-pinned-deployments') || '[]');
        const newPin = {
          appName: targetAppName,
          owner, repo, branch,
          url: runningEntry.url || deployData.url,
          port: portData.port,
          deployId: deployData.deployId,
          pinnedAt: new Date().toISOString(),
        };
        localStorage.setItem('lyric-pinned-deployments', JSON.stringify([newPin, ...pins.filter(p => p.appName !== newPin.appName)]));
      } catch { /* ignore storage errors */ }
    } catch (err) {
      setError(err.message);
      setDeployPhase('error');
    }
  };

  // Deploy existing repo code without making any AI changes
  const handleDirectRedeploy = () => {
    setEnhanceResult({ filesChanged: 0, files: [] });
    setEnhancePhase('done');
    setStep('enhancing');
    handleEnhanceRedeploy();
  };

  // --- Build Step 1: Describe your app ---
  const renderDescribeStep = () => (
    <div>
      <h3 style={{ color: '#ce93d8', marginBottom: 8 }}>✨ Describe Your App</h3>
      <p style={{ color: '#aaa', fontSize: 14, marginBottom: 16 }}>
        Tell Lyric what you want to build. Be as detailed as you like.
      </p>

      {initialIosFromApp && (
        <div style={{
          backgroundColor: '#1e1e2e', border: '1px solid #4fc3f7', borderRadius: 8,
          padding: 14, marginBottom: 16,
        }}>
          <div style={{ color: '#4fc3f7', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
            📱 Building an iOS companion app for “{initialIosFromApp.sourceApp?.displayName}”
          </div>
          <p style={{ color: '#aaa', fontSize: 12, margin: '0 0 8px' }}>
            Lyric read {initialIosFromApp.filesAnalyzed?.length || 0} file(s) from{' '}
            <code style={{ color: '#ccc' }}>
              {initialIosFromApp.sourceApp?.owner}/{initialIosFromApp.sourceApp?.repo}
            </code>{' '}
            and drafted the description below. Edit it before continuing if anything is wrong.
          </p>
          {initialIosFromApp.endpoints?.length > 0 && (
            <div style={{ color: '#888', fontSize: 11, marginBottom: 6 }}>
              Endpoints found:{' '}
              {initialIosFromApp.endpoints.slice(0, 6).map((e) => `${e.method} ${e.path}`).join(', ')}
              {initialIosFromApp.endpoints.length > 6 ? ` +${initialIosFromApp.endpoints.length - 6} more` : ''}
            </div>
          )}
          {initialIosFromApp.notes && (
            <div style={{ color: '#ffb74d', fontSize: 12 }}>⚠️ {initialIosFromApp.notes}</div>
          )}
        </div>
      )}

      {renderBuildSaveBar()}

      <FormField
        label="App Name"
        value={formData.appName}
        onChange={(v) => updateField('appName', v)}
        placeholder="e.g. ai-signal-processor"
      />

      <JiraIssuePicker
        apiUrl={getApiBase()}
        authenticatedFetch={authenticatedFetch}
        enabled={formData.useJira}
        onToggle={(checked) => setFormData((prev) => ({
          ...prev,
          useJira: checked,
          ...(checked ? {} : { jiraProjectKey: '', jiraIssueType: 'Epic', jiraIssueKey: '', jiraIssueSummary: '' }),
        }))}
        value={{
          projectKey: formData.jiraProjectKey,
          issueType: formData.jiraIssueType,
          issueKey: formData.jiraIssueKey,
          issueSummary: formData.jiraIssueSummary,
        }}
        onChange={(next) => setFormData((prev) => ({
          ...prev,
          jiraProjectKey: next.projectKey || '',
          jiraIssueType: next.issueType || 'Epic',
          jiraIssueKey: next.issueKey || '',
          jiraIssueSummary: next.issueSummary || '',
        }))}
      />

      <label style={{ ...labelStyle, marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>What should this app do?</span>
        {dictation.supported && (
          <span style={{ fontSize: 11, color: dictation.listening ? '#ef5350' : '#888', fontWeight: 400 }}>
            {dictation.listening ? '● Listening — click mic to stop' : '🎤 Click the mic to dictate'}
          </span>
        )}
      </label>
      <div style={{ position: 'relative' }}>
        <textarea
          value={dictation.listening && interimTranscript
            ? `${dictationBaseRef.current || formData.appDescription}${(dictationBaseRef.current || formData.appDescription) && !/\s$/.test(dictationBaseRef.current || formData.appDescription) ? ' ' : ''}${interimTranscript}`
            : formData.appDescription}
          onChange={(e) => updateField('appDescription', e.target.value)}
          placeholder="Describe your application in plain English. For example:&#10;&#10;'A REST API that accepts medical imaging data, runs it through an AI model for anomaly detection, and returns results with confidence scores. It should have authentication, a health check endpoint, and store results in a database.'"
          rows={6}
          style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5, marginBottom: 0, paddingRight: 52 }}
        />
        <button
          type="button"
          onClick={() => { dictationTargetRef.current = 'appDescription'; dictation.toggle(); }}
          disabled={!dictation.supported}
          title={
            !dictation.supported
              ? 'Voice input is not supported in this browser. Try Chrome or Edge.'
              : dictation.listening ? 'Stop listening' : 'Start voice dictation'
          }
          aria-label={dictation.listening ? 'Stop voice dictation' : 'Start voice dictation'}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 36,
            height: 36,
            borderRadius: '50%',
            border: 'none',
            cursor: dictation.supported ? 'pointer' : 'not-allowed',
            backgroundColor: dictation.listening ? '#ef5350' : (dictation.supported ? '#3a3a4a' : '#2a2a2a'),
            color: '#fff',
            fontSize: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.15s ease',
            animation: dictation.listening ? 'lyricMicPulse 1.5s infinite' : 'none',
          }}
        >
          {dictation.listening ? '⏹' : '🎤'}
        </button>
      </div>
      {dictation.error && (
        <div style={{ color: '#ef9a9a', fontSize: 12, marginTop: 6, marginBottom: 0 }}>
          ⚠️ {dictation.error}
        </div>
      )}
      <div style={{ height: 16 }} />

      <div style={{ ...cardStyle, marginBottom: 16, border: '1px solid #4a3a5a' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#ce93d8', marginBottom: 4 }}>
          🧩 Create Jira Epic &amp; Stories (optional)
        </div>
        <p style={{ color: '#aaa', fontSize: 12, lineHeight: 1.6, margin: '0 0 12px' }}>
          Once you've described the app, generate a Jira Epic and its Stories from that
          description — the same way the Design output tool does. You can keep building from
          those issues later, like the Edit App flow.
        </p>
        <button
          type="button"
          onClick={() => setShowCreateJira(true)}
          disabled={!formData.appDescription.trim()}
          style={{
            padding: '9px 16px',
            backgroundColor: formData.appDescription.trim() ? '#20182b' : '#2a2a2a',
            color: formData.appDescription.trim() ? '#ce93d8' : '#666',
            border: `1px solid ${formData.appDescription.trim() ? '#6b4a80' : '#444'}`,
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: formData.appDescription.trim() ? 'pointer' : 'not-allowed',
          }}
        >
          🧩 Create Epic &amp; Stories →
        </button>
        {!formData.appDescription.trim() && (
          <span style={{ fontSize: 11, color: '#888', marginLeft: 10 }}>
            Describe the app above first.
          </span>
        )}
      </div>

      <div style={{ ...cardStyle, marginBottom: 16, border: '1px solid #2d4a5a' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#7cc4ff', marginBottom: 4 }}>
          🎨 Design the UI (Mockup) (optional)
        </div>
        <p style={{ color: '#aaa', fontSize: 12, lineHeight: 1.6, margin: '0 0 12px' }}>
          Open the same Design Mockup tool used in the SDLC flow to sketch the app's UI from
          your description. When you save a mockup here, the AI uses it as the visual reference
          while building the app's frontend.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setShowMockup(true)}
            style={{
              padding: '9px 16px',
              backgroundColor: '#16232e',
              color: '#7cc4ff',
              border: '1px solid #3a6a80',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            🎨 {formData.mockupHtml ? 'Edit UI mockup' : 'Design the UI'} →
          </button>
          {formData.mockupHtml && (
            <span style={{ fontSize: 12, color: '#81c784', display: 'flex', alignItems: 'center', gap: 6 }}>
              ✓ Mockup attached
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, mockupHtml: '', mockupModelUsed: '' }))}
                style={{ background: 'none', border: 'none', color: '#ef9a9a', cursor: 'pointer', fontSize: 12 }}
              >
                Remove
              </button>
            </span>
          )}
        </div>
      </div>

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <button
          type="button"
          onClick={() => setModelOpen((o) => !o)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 700, color: '#ce93d8' }}>
            🤖 AI model for this build
          </span>
          <span style={{ fontSize: 12, color: '#aaa', display: 'flex', alignItems: 'center', gap: 6 }}>
            <code style={{ color: '#ce93d8' }}>{selectedModel}</code>
            <span>{modelOpen ? '▾' : '▸'}</span>
          </span>
        </button>
        {modelOpen && (
          <div style={{ marginTop: 12 }}>
            <p style={{ color: '#aaa', fontSize: 12, margin: '0 0 12px' }}>
              Pick a different model if you like.
            </p>
            <ModelSelector selectedModel={selectedModel} setSelectedModel={setSelectedModel} />
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => setStep('architecture')}
          disabled={!formData.appName.trim() || !formData.appDescription.trim()}
          style={{
            ...btnPrimary,
            backgroundColor: (!formData.appName.trim() || !formData.appDescription.trim()) ? '#555' : '#ce93d8',
            cursor: (!formData.appName.trim() || !formData.appDescription.trim()) ? 'not-allowed' : 'pointer',
          }}
        >
          Choose Architecture →
        </button>
        <button onClick={() => { setPath(null); setStep('choose'); }} style={btnBack}>← Back</button>
      </div>

      {showCreateJira && (
        <div
          onClick={() => setShowCreateJira(false)}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(520px, 92vw)', backgroundColor: '#1b1b24', border: '1px solid #4a3a5a',
              borderRadius: 12, padding: 24, boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
            }}
          >
            <h3 style={{ color: '#ce93d8', margin: '0 0 8px' }}>🧩 Create Jira Epic &amp; Stories</h3>
            <p style={{ color: '#ccc', fontSize: 13, lineHeight: 1.6, margin: '0 0 12px' }}>
              This will generate a Jira Epic and its Stories from your app description — the
              same paradigm as the Design output tool — so you can keep iterating on the build
              from those issues later, like the Edit App flow.
            </p>
            <p style={{ color: '#888', fontSize: 12, lineHeight: 1.6, margin: '0 0 20px' }}>
              This flow is being wired up next. For now, describe your app and continue — the
              generated code isn't blocked on Jira.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowCreateJira(false)} style={btnBack}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showMockup && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1000,
            display: 'flex', flexDirection: 'column',
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 20px', backgroundColor: '#14141c', borderBottom: '1px solid #333',
          }}>
            <span style={{ color: '#7cc4ff', fontSize: 15, fontWeight: 700 }}>
              🎨 Design the UI — {formData.appName || 'New app'}
            </span>
            <button
              type="button"
              onClick={() => setShowMockup(false)}
              style={{
                padding: '8px 16px', backgroundColor: '#2a2a2a', color: '#eee',
                border: '1px solid #555', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Done ✓
            </button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: 20, backgroundColor: '#0f0f16' }}>
            <MockupBuilder
              key={`build-scratch-mockup:${showMockup}`}
              flow={{
                id: 'build-scratch',
                name: formData.appName || 'New app',
                deliverables: formData.mockupHtml
                  ? { MOCKUP: { html: formData.mockupHtml, modelUsed: formData.mockupModelUsed || null, status: 'in_progress' } }
                  : {},
              }}
              step={{ key: 'design', kind: 'design', docType: 'MOCKUP' }}
              selectedProject={formData.useJira ? formData.jiraProjectKey : ''}
              selectedModel={selectedModel}
              initialDescribe={formData.appDescription}
              embedded
              onSave={async (_docType, payload) => {
                setFormData((prev) => ({
                  ...prev,
                  ...(payload.html !== undefined ? { mockupHtml: payload.html } : {}),
                  ...(payload.modelUsed ? { mockupModelUsed: payload.modelUsed } : {}),
                }));
              }}
            />
          </div>
        </div>
      )}
    </div>
  );

  // --- Build Step 2: Architecture ---
  const renderArchitectureStep = () => {
    const pillStyle = (active) => ({
      padding: '8px 16px',
      borderRadius: 8,
      border: active ? '2px solid #ffb74d' : '1px solid #555',
      backgroundColor: active ? '#3e2d14' : 'transparent',
      color: active ? '#ffb74d' : '#ccc',
      cursor: 'pointer',
      fontSize: 13,
      fontWeight: active ? 700 : 400,
    });

    const selectType = (id) => {
      setFormData((prev) => ({
        ...prev,
        appType: id,
        // Swift is the only language for an iOS project; restore a web default
        // when switching back so the scaffold prompt stays coherent.
        language: id === 'ios-swiftui'
          ? 'swift'
          : (prev.language === 'swift' ? 'javascript' : prev.language),
      }));
    };

    return (
    <div>
      <h3 style={{ color: '#ffb74d', marginBottom: 8 }}>🏗️ Architecture</h3>

      {renderBuildSaveBar()}

      <label style={labelStyle}>Architecture Type</label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {APP_TYPES.map((t) => (
          <button
            key={t.id}
            onClick={() => selectType(t.id)}
            title={t.desc}
            style={pillStyle(formData.appType === t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isIosBuild ? (
        <div style={{
          marginBottom: 16, padding: 12, borderRadius: 8,
          border: '1px solid #555', backgroundColor: '#1e1e1e',
        }}>
          <div style={{ color: '#4fc3f7', fontSize: 13, marginBottom: 12 }}>
            Lyric generates a ready-to-open Xcode project and pushes it to a new GitHub
            repo. iOS apps are not hosted by Lyric, so there is no deploy step — you
            clone the repo and open it in Xcode.
          </div>

          <label style={labelStyle}>Swift Language Mode</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            {SWIFT_VERSION_OPTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => updateField('swiftVersion', s.id)}
                title={s.note}
                style={pillStyle(formData.swiftVersion === s.id)}
              >
                {s.label}{s.note ? ` — ${s.note}` : ''}
              </button>
            ))}
          </div>

          <label style={labelStyle}>Minimum iOS Version</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {IOS_TARGET_OPTIONS.map((t) => (
              <button
                key={t.id}
                onClick={() => updateField('deploymentTarget', t.id)}
                title={t.note}
                style={pillStyle(formData.deploymentTarget === t.id)}
              >
                {t.label}{t.note ? ` — ${t.note}` : ''}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          <label style={labelStyle}>Language / Runtime</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {LANGUAGE_OPTIONS.map((l) => (
              <button
                key={l.id}
                onClick={() => updateField('language', l.id)}
                style={pillStyle(formData.language === l.id)}
              >
                {l.label}
              </button>
            ))}
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => setStep(isIosBuild ? 'build-review' : 'services')}
          style={{ ...btnPrimary, backgroundColor: '#ffb74d' }}
        >
          {isIosBuild ? 'Review →' : 'Connected Services →'}
        </button>
        <button onClick={() => setStep('describe')} style={btnBack}>← Back</button>
      </div>
    </div>
    );
  };

  // Shared "Connected Services" catalog picker — lets the user add
  // confluence/box/google-docs/slack/postgresql/mysql/mongodb/redis/custom-api
  // integrations. Used by both the Build-from-scratch wizard (renderServicesStep)
  // and the Edit/Enhance flow (renderEnhanceStep), so an app owner can add new
  // integrations to an already-deployed app the same way as when scaffolding one.
  const renderServiceCatalogPicker = () => {
    const categories = [
      { id: 'integration', label: '🔗 Integrations', color: '#4fc3f7', addLabel: 'integration' },
      { id: 'database', label: '🗄️ Databases', color: '#81c784', addLabel: 'database' },
      { id: 'custom', label: '⚡ Custom', color: '#ffb74d', addLabel: 'custom service' },
    ];

    const selectedServices = formData.selectedServices || [];

    const addSelectedService = () => {
      if (!pickingServiceId) return;
      const svc = SERVICE_CATALOG.find(s => s.id === pickingServiceId);
      if (!svc) return;
      setFormData(prev => ({
        ...prev,
        selectedServices: prev.selectedServices.includes(svc.id)
          ? prev.selectedServices
          : [...prev.selectedServices, svc.id],
        serviceConfig: { ...prev.serviceConfig, [svc.id]: { ...pendingServiceFields } },
      }));
      setPickingServiceId('');
      setPendingServiceFields({});
      setServiceAddNotice(null);
    };

    const removeSelectedService = (svcId) => {
      setFormData(prev => {
        const { [svcId]: _removed, ...restConfig } = prev.serviceConfig || {};
        return {
          ...prev,
          selectedServices: prev.selectedServices.filter(id => id !== svcId),
          serviceConfig: restConfig,
        };
      });
    };

    return (
      <div>
        {categories.map(cat => {
          const catServices = SERVICE_CATALOG.filter(s => s.category === cat.id);
          const addedInCat = selectedServices.filter(id => catServices.some(s => s.id === id));
          const isOpen = serviceAddNotice === cat.id;
          const activeSvc = SERVICE_CATALOG.find(s => s.id === pickingServiceId);

          return (
            <div key={cat.id} style={{ marginBottom: 20 }}>
              <div style={{ color: cat.color, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
                {cat.label}
              </div>

              {addedInCat.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                  {addedInCat.map(id => {
                    const svc = SERVICE_CATALOG.find(s => s.id === id);
                    return (
                      <span
                        key={id}
                        title={svc?.description || ''}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '6px 10px', borderRadius: 20,
                          backgroundColor: '#1e1e1e', border: `1px solid ${cat.color}`,
                          color: '#eee', fontSize: 12,
                        }}
                      >
                        {svc?.icon} {svc?.label || id}
                        <button
                          type="button"
                          onClick={() => removeSelectedService(id)}
                          title={`Remove ${svc?.label || id}`}
                          style={{
                            background: 'none', border: 'none', color: '#aaa',
                            cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1,
                          }}
                        >
                          ✕
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  setServiceAddNotice(prev => (prev === cat.id ? null : cat.id));
                  setPickingServiceId('');
                  setPendingServiceFields({});
                }}
                style={{
                  width: '100%', padding: '12px 14px',
                  display: 'flex', alignItems: 'center', gap: 8,
                  backgroundColor: 'transparent', color: cat.color,
                  border: `1px dashed ${cat.color}`, borderRadius: 10,
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}
              >
                <span style={{ fontSize: 16, lineHeight: 1 }}>＋</span> Add {cat.addLabel}
              </button>

              {isOpen && (
                <div style={{ ...cardStyle, marginTop: 10, marginBottom: 0 }}>
                  <label style={labelStyle}>Choose a {cat.addLabel}</label>
                  <select
                    value={pickingServiceId}
                    onChange={(e) => { setPickingServiceId(e.target.value); setPendingServiceFields({}); }}
                    style={inputStyle}
                  >
                    <option value="">— Select —</option>
                    {catServices.map(s => (
                      <option key={s.id} value={s.id} disabled={selectedServices.includes(s.id)}>
                        {s.icon} {s.label}{selectedServices.includes(s.id) ? ' (added)' : ''}
                      </option>
                    ))}
                  </select>

                  {activeSvc && (
                    <>
                      <p style={{ color: '#888', fontSize: 12, margin: '0 0 12px', lineHeight: 1.5 }}>
                        ℹ️ {activeSvc.description}
                      </p>
                      {activeSvc.fields.map(f => (
                        <div key={f.key}>
                          <label style={labelStyle}>{f.label}</label>
                          <input
                            type="text"
                            value={pendingServiceFields[f.key] || ''}
                            onChange={(e) => setPendingServiceFields(prev => ({ ...prev, [f.key]: e.target.value }))}
                            placeholder={f.placeholder}
                            style={inputStyle}
                          />
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addSelectedService}
                        style={{ ...btnPrimary, backgroundColor: cat.color }}
                      >
                        Add {activeSvc.label}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // --- Build Step 3: Connected Services ---
  const renderServicesStep = () => {
    return (
      <div>
        <h3 style={{ color: '#4fc3f7', marginBottom: 6 }}>🔌 Connected Services</h3>
        <p style={{ color: '#aaa', fontSize: 13, marginBottom: 20 }}>
          Add any external services your app needs. All are optional — leave them empty for a
          basic, self-contained app. Real secrets (API tokens, DB passwords, etc.) are entered
          later, once the app has been scaffolded.
        </p>

        {renderBuildSaveBar()}

        {renderServiceCatalogPicker()}

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button
            onClick={() => setStep('build-review')}
            style={{ ...btnPrimary, backgroundColor: '#4caf50' }}
          >
            Review & Build →
          </button>
          <button onClick={() => setStep('architecture')} style={btnBack}>← Back</button>
        </div>
      </div>
    );
  };

  // --- Build Step 4: Review & Build ---
  const renderBuildReviewStep = () => {
    const appTypeLabel = APP_TYPES.find((t) => t.id === formData.appType)?.label || formData.appType;
    const langLabel = isIosBuild
      ? `Swift ${formData.swiftVersion} · iOS ${formData.deploymentTarget}+`
      : (LANGUAGE_OPTIONS.find((l) => l.id === formData.language)?.label || formData.language);

    return (
      <div>
        <h3 style={{ color: '#4caf50', marginBottom: 8 }}>🚀 Review & Build</h3>
        <p style={{ color: '#aaa', fontSize: 13, marginBottom: 16 }}>
          Lyric will generate a complete, deployable application based on your requirements.
        </p>
        {renderBuildSaveBar()}
        <div style={{ ...cardStyle, fontSize: 13 }}>
          <ReviewRow label="App Name" value={formData.appName} />
          {formData.useJira && formData.jiraIssueKey && (
            <ReviewRow label="Jira Source" value={`${formData.jiraIssueKey} — ${formData.jiraIssueSummary}`} />
          )}
          <ReviewRow label="Type" value={appTypeLabel} />
          <ReviewRow label="Language" value={langLabel} />
          <div style={{ padding: '8px 0', borderBottom: '1px solid #333' }}>
            <div style={{ color: '#888', marginBottom: 4 }}>Description</div>
            <div style={{ color: '#fff', fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {formData.appDescription}
            </div>
          </div>
          <ReviewRow label="AI Model" value={selectedModel} />
          {formData.mockupHtml && <ReviewRow label="UI Mockup" value="✓ Attached — used as the frontend design reference" />}
        </div>

        {showBuildConfirm ? (
          <div style={{ backgroundColor: '#1a2a1a', border: '1px solid #4caf50', borderRadius: 10, padding: 20, marginTop: 8 }}>
            <p style={{ color: '#fff', fontWeight: 600, fontSize: 15, margin: '0 0 6px' }}>
              Create repository for "{formData.appName}"?
            </p>
            <p style={{ color: '#aaa', fontSize: 13, margin: '0 0 16px' }}>
              A new private GitHub repository will be created under the <code style={{ color: '#ce93d8' }}>ischemaview</code> organization.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => { setShowBuildConfirm(false); handleBuildSubmit(); }}
                style={{ ...btnPrimary, backgroundColor: '#4caf50', color: '#fff', fontSize: 14, padding: '10px 24px' }}
              >
                Yes, create it
              </button>
              <button onClick={() => setShowBuildConfirm(false)} style={btnBack}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setShowBuildConfirm(true)}
              disabled={loading}
              style={{ ...btnPrimary, backgroundColor: loading ? '#555' : '#4caf50', color: '#fff', fontSize: 15, padding: '12px 32px' }}
            >
              {loading ? '✨ Building…' : '✨ Build My App'}
            </button>
            <button onClick={() => setStep('architecture')} style={btnBack}>← Back</button>
          </div>
        )}
      </div>
    );
  };

  const renderGitHubTokenStep = () => {
    const setup = tokenSetup || {
      org: 'ischemaview',
      requiredScopes: ['repo', 'workflow'],
      createTokenUrl: 'https://github.com/settings/tokens/new?description=Lyric%20AI%20App%20Builder&scopes=repo,workflow',
      instructions: [
        'Open the link to create a Personal Access Token (classic) with the listed scopes.',
        'Make sure your GitHub account is a member of the target organization with permission to create repos.',
        'Paste the token below. It is stored locally in your browser only — never sent anywhere except GitHub via Lyric.',
      ],
    };
    const trimmedToken = (githubToken || '').trim();
    const tokenLooksValid = trimmedToken.length >= 20;

    return (
      <div>
        <h3 style={{ color: '#ce93d8', marginBottom: 8 }}>🔐 GitHub Access Required</h3>
        <p style={{ color: '#aaa', fontSize: 13, marginBottom: 16 }}>
          To create a new repository under <code style={{ color: '#fff' }}>{setup.org}</code>, Lyric needs a
          GitHub Personal Access Token with these scopes: {(setup.requiredScopes || []).map((s) => (
            <code key={s} style={{ color: '#fff', marginLeft: 4 }}>{s}</code>
          ))}.
        </p>

        <div style={{ ...cardStyle, marginBottom: 16, fontSize: 13, lineHeight: 1.6 }}>
          <ol style={{ margin: 0, paddingLeft: 20, color: '#ddd' }}>
            {(setup.instructions || []).map((line, i) => (
              <li key={i} style={{ marginBottom: 6 }}>{line}</li>
            ))}
          </ol>
          <div style={{ marginTop: 12 }}>
            <a
              href={setup.createTokenUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#4fc3f7', textDecoration: 'none', fontWeight: 600 }}
            >
              → Create a GitHub Personal Access Token ↗
            </a>
          </div>
          {setup.currentScopes && (
            <p style={{ color: '#ff9800', fontSize: 12, marginTop: 10, marginBottom: 0 }}>
              Current token scopes: <code>{setup.currentScopes.join(', ') || '(none)'}</code>
            </p>
          )}
          {setup.githubLogin && (
            <p style={{ color: '#888', fontSize: 12, marginTop: 6, marginBottom: 0 }}>
              Authenticated as: <code>{setup.githubLogin}</code>
            </p>
          )}
        </div>

        <label style={{ display: 'block', color: '#aaa', fontSize: 13, marginBottom: 6 }}>
          Personal Access Token
        </label>
        <input
          type="password"
          value={githubToken}
          onChange={(e) => setGithubToken(e.target.value)}
          placeholder="ghp_..."
          autoComplete="off"
          spellCheck={false}
          style={{
            width: '100%',
            padding: '10px 12px',
            backgroundColor: '#1e1e2e',
            border: '1px solid #333',
            borderRadius: 6,
            color: '#fff',
            fontSize: 14,
            fontFamily: 'monospace',
            boxSizing: 'border-box',
            marginBottom: 10,
          }}
        />

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#aaa', fontSize: 13, marginBottom: 16, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={rememberToken}
            onChange={(e) => setRememberToken(e.target.checked)}
          />
          Remember this token in my browser (localStorage only)
        </label>

        {error && (
          <div style={{ backgroundColor: '#3a1a1a', border: '1px solid #ef5350', borderRadius: 6, padding: '10px 12px', marginBottom: 16, color: '#ef9a9a', fontSize: 13 }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => handleBuildSubmit(githubToken)}
            disabled={loading || !tokenLooksValid}
            style={{
              ...btnPrimary,
              backgroundColor: loading ? '#555' : (tokenLooksValid ? '#4caf50' : '#444'),
              color: '#fff',
              fontSize: 15,
              padding: '12px 32px',
              cursor: tokenLooksValid && !loading ? 'pointer' : 'not-allowed',
            }}
          >
            {loading ? '⏳ Verifying…' : '✨ Save Token & Build'}
          </button>
          <button onClick={() => setStep('build-review')} style={btnBack}>← Back</button>
          {githubToken && (
            <button
              onClick={() => {
                setGithubToken('');
                try { localStorage.removeItem(GITHUB_TOKEN_STORAGE_KEY); } catch { /* ignore */ }
              }}
              style={{ ...btnBack, color: '#ef9a9a', borderColor: '#5a2a2a' }}
            >
              Clear Saved Token
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderDeployedStep = () => {
    const url = deployResult?.url;
    const appName = deployResult?.appName;
    const liveStatus = deployLiveStatus;
    const liveStage = deployLiveStage;
    const elapsed = deployElapsed;

    const isRunning = liveStatus === 'running';
    const isError = liveStatus === 'error' || liveStatus === 'stopped';
    const isDone = isRunning || isError;
    const liveError = deployLiveError;

    const stageLabels = {
      cloning: 'Cloning repository…',
      installing: 'Installing dependencies…',
      building: 'Running build…',
      starting: 'Starting app process…',
      running: 'App is running',
    };

    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;
    const elapsedStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

    return (
      <div>
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          {isError ? (
            <div style={{ fontSize: 48, marginBottom: 12 }}>❌</div>
          ) : isRunning ? (
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          ) : (
            <div style={{ marginBottom: 16 }}>
              <div style={{
                width: 56, height: 56, margin: '0 auto 8px',
                border: '4px solid #333',
                borderTop: '4px solid #4fc3f7',
                borderRadius: '50%',
                animation: 'lyricSpinDeploy 1s linear infinite',
              }} />
            </div>
          )}

          <h3 style={{ color: isError ? '#ef5350' : isRunning ? '#4caf50' : '#4fc3f7', margin: '0 0 8px' }}>
            {isError ? 'Deployment Failed' : isRunning ? 'App is Live!' : 'Deploying…'}
          </h3>

          {isError && liveError && (
            <ExpandableWarning
              label={`❌ ${liveStatus === 'stopped' ? 'App started but crashed' : 'Error'}: ${liveError.length > 80 ? liveError.substring(0, 80) + '…' : liveError}`}
              what={liveError}
              action={liveStatus === 'stopped'
                ? 'The app started but exited shortly after. Check the start command, make sure all required env vars are set, and look at the server terminal for the full crash output.'
                : /Missing required environment variable/i.test(liveError)
                  ? 'This app cannot start until the named environment variable is configured. Return to App Configuration and enter it or choose an AWS secret that contains it.'
                : /Python dependency|pip install|ModuleNotFoundError/i.test(liveError)
                  ? 'This is a Python dependency declaration problem, not a port or start-command problem. Return to stack detection, approve the requirements.txt fix Lyric offers, and deploy the generated temporary test branch.'
                : `Common fixes: ensure the start command is correct, the port is not already in use, and all required env vars are set.`}
            />
          )}

          {!isDone && (
            <p style={{ color: '#aaa', fontSize: 14, margin: '0 0 4px' }}>
              {stageLabels[liveStage] || 'Working…'}
            </p>
          )}
          <p style={{ color: '#666', fontSize: 12, margin: 0 }}>
            {isDone ? `Completed in ${elapsedStr}` : `Elapsed: ${elapsedStr}`}
          </p>
        </div>

        {deployResult?.deployId && (
          <>
          {deployResult.complianceBranch && (
            <div style={{ backgroundColor: '#10251a', border: '1px solid #4caf50', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ color: '#81c784', fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
                🌿 Testing fixes from a temporary branch
              </div>
              <div style={{ color: '#ddd', fontSize: 12, lineHeight: 1.55 }}>
                This app is deployed from{' '}
                <a href={deployResult.complianceBranch.branchUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9' }}>
                  {deployResult.complianceBranch.branch}
                </a>.
                {' '}The source branch <code>{deployResult.complianceBranch.baseBranch}</code> is unchanged.
                {deployResult.complianceBranch.prUrl ? (
                  <> A developer must review and merge the{' '}
                    <a href={deployResult.complianceBranch.prUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9' }}>
                      draft pull request #{deployResult.complianceBranch.prNumber}
                    </a>
                    {' '}before these fixes become permanent.</>
                ) : (
                  <> {deployResult.complianceBranch.prError}</>
                )}
              </div>
            </div>
          )}
          <div style={{ backgroundColor: '#1e1e2e', border: '1px solid #333', borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px' }}>
              <div>
                <span style={{ color: '#888', fontSize: 12 }}>Status</span>
                <p style={{ color: isError ? '#ef5350' : isRunning ? '#4caf50' : '#4fc3f7', margin: '2px 0 0', fontSize: 14 }}>
                  {isError ? '❌ error' : isRunning ? '✅ running' : `⏳ ${liveStage || 'deploying'}`}
                </p>
              </div>
              {deployResult.config?.owner && deployResult.config?.repo && (
                <div>
                  <span style={{ color: '#888', fontSize: 12 }}>Repository</span>
                  <p style={{ color: '#fff', margin: '2px 0 0', fontSize: 14 }}>{deployResult.config.owner}/{deployResult.config.repo}</p>
                </div>
              )}
              {deployResult.config?.port && (
                <div>
                  <span style={{ color: '#888', fontSize: 12 }}>Port</span>
                  <p style={{ color: '#fff', margin: '2px 0 0', fontSize: 14 }}>{deployResult.config.port}</p>
                </div>
              )}
              {appName && (
                <div>
                  <span style={{ color: '#888', fontSize: 12 }}>App Name</span>
                  <p style={{ color: '#fff', margin: '2px 0 0', fontSize: 14, fontFamily: 'monospace' }}>{appName}</p>
                </div>
              )}
            </div>
          </div>
          </>
        )}

        {url && (
          <div style={{ backgroundColor: '#1a2e1a', border: '1px solid #4caf50', borderRadius: 8, padding: '12px 16px', marginBottom: 16, textAlign: 'center' }}>
            <span style={{ color: '#888', fontSize: 12, display: 'block', marginBottom: 4 }}>Your app will be available at</span>
            <a href={url} target="_blank" rel="noopener noreferrer"
              style={{ color: '#4caf50', fontSize: 16, fontFamily: 'monospace', textDecoration: 'none' }}>
              {url} ↗
            </a>
            {isRunning && (
              <p style={{ color: '#888', fontSize: 12, margin: '8px 0 0' }}>📌 Pinned to your launch screen automatically</p>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          {url && (
            <button
              onClick={() => setViewerApp({
                appName,
                displayName: formData.appDisplayName || formData.repo || appName,
                url,
                status: 'running',
              })}
              disabled={!isRunning}
              style={{ ...btnPrimary, backgroundColor: isRunning ? '#4caf50' : '#333', color: isRunning ? '#fff' : '#666', fontSize: 15, padding: '12px 32px', cursor: isRunning ? 'pointer' : 'not-allowed' }}
            >
              🌐 Open App
            </button>
          )}
          <button
            onClick={onClose}
            disabled={!isDone}
            style={{ ...btnPrimary, backgroundColor: isDone ? '#1976d2' : '#333', color: isDone ? '#fff' : '#666', fontSize: 15, padding: '12px 32px', cursor: isDone ? 'pointer' : 'not-allowed' }}
          >
            ✅ Done
          </button>
          <button onClick={() => { setDeployResult(null); setStep('choose'); }} style={btnBack}>
            ↩ Start Over
          </button>
        </div>
      </div>
    );
  };

  const renderBuildingStep = () => {
    const repo = deployResult?.repo;
    const appUrl = deployAppResult?.url;
    // An iOS build finishes at the repo: there is no deploy phase to wait on.
    const iosDone = scaffoldResult?.deployable === false && buildEnvPhase === 'skipped';
    const allDone = deployPhase === 'done' || iosDone;
    const isStarting = deployPhase === 'starting';
    const hasError = scaffoldPhase === 'error' || deployPhase === 'error' || (!loading && !deployResult);

    const phases = [
      {
        key: 'repo',
        label: 'Creating GitHub repository',
        hint: '~5s',
        done: !!deployResult,
        active: !deployResult && !hasError,
        error: false,
      },
      {
        key: 'scaffold',
        label: isIosBuild ? 'Generating SwiftUI code with AI' : 'Generating app code with AI',
        hint: '~20–40s',
        done: scaffoldPhase === 'done' || scaffoldPhase === 'error' ? scaffoldPhase === 'done' : false,
        active: scaffoldPhase === 'generating',
        error: scaffoldPhase === 'error',
      },
      {
        key: 'push',
        label: isIosBuild ? 'Pushing Xcode project to repository' : 'Pushing files to repository',
        hint: '~5s',
        done: scaffoldPhase === 'done',
        active: scaffoldPhase === 'generating',
        error: scaffoldPhase === 'error',
      },
      ...(isIosBuild ? [] : [{
        key: 'deploy',
        label: deployPhase === 'starting'
          ? 'Installing dependencies & starting app…'
          : 'Installing dependencies & starting app',
        hint: '~30–90s',
        done: deployPhase === 'done',
        active: deployPhase === 'deploying' || deployPhase === 'starting',
        error: deployPhase === 'error',
      }]),
    ];

    // Which phase are we actually in right now? Drives the progress bar and the
    // rotating fun-status line below.
    let currentPhaseKey = 'repo';
    if (allDone) currentPhaseKey = 'done';
    else if (buildEnvPhase === 'waiting') currentPhaseKey = 'waiting';
    else if (deployPhase === 'starting') currentPhaseKey = 'starting';
    else if (deployPhase === 'deploying') currentPhaseKey = 'deploy';
    else if (scaffoldPhase === 'done') currentPhaseKey = 'deploy';
    else if (scaffoldPhase === 'generating') currentPhaseKey = 'scaffold';
    else if (deployResult) currentPhaseKey = 'scaffold';
    else currentPhaseKey = 'repo';

    // Record when this phase began (in elapsed-seconds) so the bar can creep
    // toward the phase ceiling over that phase's expected duration.
    if (buildPhaseKeyRef.current !== currentPhaseKey) {
      buildPhaseKeyRef.current = currentPhaseKey;
      buildPhaseStartRef.current = buildElapsed;
    }

    const meta = BUILD_PHASE_META[currentPhaseKey] || BUILD_PHASE_META.repo;
    const [floorPct, ceilPct] = meta.range;
    const inPhase = Math.max(0, buildElapsed - buildPhaseStartRef.current);
    const frac = currentPhaseKey === 'done' ? 1 : Math.min(1, inPhase / Math.max(1, meta.est));
    const progressPct = hasError ? floorPct : Math.round(floorPct + (ceilPct - floorPct) * frac);

    // Rotating, phase-appropriate status line (changes every ~3s).
    const funList = BUILD_FUN_MESSAGES[currentPhaseKey] || BUILD_FUN_MESSAGES.repo;
    const funMsg = funList[Math.floor(buildElapsed / 3) % funList.length];

    const mm = String(Math.floor(buildElapsed / 60));
    const ss = String(buildElapsed % 60).padStart(2, '0');
    const elapsedLabel = `${mm}:${ss}`;

    const showLiveProgress = !allDone && !hasError && buildEnvPhase !== 'waiting';

    return (
      <div>
        {/* Header banner */}
        <div style={{
          backgroundColor: allDone ? '#1a2e1a' : hasError ? '#2e1a1a' : '#1a1a2e',
          border: `2px solid ${allDone ? '#4caf50' : hasError ? '#ef5350' : '#4fc3f7'}`,
          borderRadius: 12,
          padding: '24px 20px',
          textAlign: 'center',
          marginBottom: 20,
        }}>
          <div style={{ fontSize: 52, marginBottom: 10 }}>
            {allDone ? '🎉' : hasError ? '❌' : buildEnvPhase === 'waiting' ? '🔑' : <GearIcon size={52} color="#4fc3f7" />}
          </div>
          <h3 style={{ color: allDone ? '#4caf50' : hasError ? '#ef5350' : buildEnvPhase === 'waiting' ? '#ce93d8' : '#4fc3f7', margin: '0 0 8px', fontSize: 22 }}>
            {allDone
              ? (iosDone ? `${formData.appName} is ready for Xcode!` : `${formData.appName} is live!`)
              : hasError ? 'Build Failed' : buildEnvPhase === 'waiting' ? 'Configure & Deploy' : isStarting ? 'Starting Your App…' : 'Building Your App…'}
          </h3>
          <p style={{ color: '#ccc', fontSize: 14, margin: 0 }}>
            {allDone
              ? (iosDone
                  ? `Your Xcode project was pushed to GitHub — clone it and open it in Xcode.`
                  : `Your app is running and pinned to your launch screen.`)
              : hasError
              ? (error || 'Something went wrong.')
              : buildEnvPhase === 'waiting'
              ? `Code is ready — fill in credentials below and click Deploy.`
              : `${meta.label} — hang tight, this usually takes a minute or two.`}
          </p>

          {/* Live progress: rotating status line, animated bar, elapsed timer */}
          {showLiveProgress && (
            <div style={{ marginTop: 16 }}>
              <div style={{ color: '#4fc3f7', fontSize: 15, minHeight: 22, marginBottom: 10, fontWeight: 500 }}>
                {funMsg}
              </div>
              <div style={{ height: 10, backgroundColor: '#2a2a3a', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(100, Math.max(3, progressPct))}%`,
                  background: 'linear-gradient(90deg, #4fc3f7, #7e57c2)',
                  borderRadius: 6,
                  transition: 'width 0.6s ease',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, color: '#888', fontSize: 12 }}>
                <span>⏱️ Elapsed {elapsedLabel}</span>
                <span>{progressPct}% · usually 1–2 min</span>
              </div>
            </div>
          )}
        </div>

        {/* Phase progress list */}
        <div style={{ backgroundColor: '#1e1e2e', border: '1px solid #333', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          {phases.map((p) => (
            <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #2a2a3a' }}>
              <span style={{ fontSize: 18, width: 24, textAlign: 'center', flexShrink: 0 }}>
                {p.error ? '❌' : p.done ? '✅' : p.active ? '⏳' : '○'}
              </span>
              <span style={{ color: p.done ? '#4caf50' : p.error ? '#ef5350' : p.active ? '#fff' : '#666', fontSize: 14, flex: 1 }}>
                {p.label}
                {p.active && <span style={{ color: '#888', fontSize: 12, marginLeft: 8 }}>working…</span>}
              </span>
              <span style={{ color: p.done ? '#4caf50' : '#666', fontSize: 12, flexShrink: 0 }}>
                {p.done ? 'done' : p.hint}
              </span>
            </div>
          ))}
        </div>

        {/* iOS deliverable — repo + how to open it in Xcode */}
        {iosDone && (
          <div style={{ backgroundColor: '#1e1e2e', border: '2px solid #4caf50', borderRadius: 10, padding: 20, marginBottom: 16 }}>
            <h4 style={{ color: '#4caf50', margin: '0 0 6px', fontSize: 16 }}>📱 Open in Xcode</h4>
            <p style={{ color: '#aaa', fontSize: 13, margin: '0 0 12px' }}>
              {scaffoldResult?.iosAppName} · Swift {scaffoldResult?.swiftVersion} · iOS {scaffoldResult?.deploymentTarget} or later
              {scaffoldResult?.bundleId ? ` · ${scaffoldResult.bundleId}` : ''}
            </p>
            <pre style={{
              backgroundColor: '#12121c', border: '1px solid #333', borderRadius: 6,
              padding: '10px 12px', color: '#8bc34a', fontSize: 12, overflowX: 'auto', margin: '0 0 12px',
            }}>{scaffoldResult?.cloneCommand}</pre>
            <p style={{ color: '#888', fontSize: 12, margin: '0 0 12px' }}>
              Pick an iOS Simulator in Xcode and press ⌘R. Signing is disabled, so no Apple
              account is needed to run it in the Simulator.
            </p>
            {repo?.url && (
              <a href={repo.url} target="_blank" rel="noopener noreferrer"
                style={{ color: '#4fc3f7', fontSize: 13 }}>
                View repository on GitHub →
              </a>
            )}
          </div>
        )}

        {/* Env var config form — shown after scaffold, before deploy */}
        {buildEnvPhase === 'waiting' && (
          <div style={{ backgroundColor: '#1e1e2e', border: '2px solid #ce93d8', borderRadius: 10, padding: 20, marginBottom: 16 }}>
            <h4 style={{ color: '#ce93d8', margin: '0 0 6px', fontSize: 16 }}>⚙️ Configure App Credentials</h4>
            <p style={{ color: '#aaa', fontSize: 13, margin: '0 0 16px' }}>
              {secretMode === 'existing'
                ? 'Pick the existing AWS secret to use. If any required keys are missing, you can add them inline.'
                : 'Fill in the environment variables your app needs. Leave blank to skip optional services.'}
              {secretMode === 'new' && (formData.selectedServices || []).length > 0 && (
                <span style={{ color: '#ce93d8' }}> Highlighted fields match the services you connected.</span>
              )}
            </p>

            {renderSecretPicker({ defaultName: formData.appName || formData.repo || 'app-name', requiredVars: buildEnvVars })}

            {secretMode === 'new' && (
              buildEnvVars.length === 0 ? (
                <p style={{ color: '#888', fontSize: 13 }}>No environment variables detected in .env.example.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 340, overflowY: 'auto', paddingRight: 4 }}>
                  {buildEnvVars.map((ev, i) => {
                    const highlighted = isServiceVar(ev.key);
                    return (
                      <div key={ev.key} style={{
                        backgroundColor: highlighted ? '#1a1a2e' : '#252535',
                        border: `1px solid ${highlighted ? '#7c4dff' : '#333'}`,
                        borderRadius: 6, padding: '10px 12px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <code style={{ color: highlighted ? '#ce93d8' : '#fff', fontSize: 13, fontWeight: 600 }}>{ev.key}</code>
                          {highlighted && <span style={{ fontSize: 10, color: '#7c4dff', fontWeight: 700, textTransform: 'uppercase' }}>connected service</span>}
                        </div>
                        {ev.comment && <p style={{ color: '#888', fontSize: 11, margin: '0 0 6px' }}>{ev.comment}</p>}
                        <input
                          type={ev.key.toLowerCase().includes('secret') || ev.key.toLowerCase().includes('token') || ev.key.toLowerCase().includes('password') ? 'password' : 'text'}
                          placeholder={ev.example || `Enter ${ev.key}`}
                          value={ev.value}
                          onChange={(e) => setBuildEnvVars(prev => prev.map((v, j) => j === i ? { ...v, value: e.target.value } : v))}
                          style={{ ...inputStyle, margin: 0, fontSize: 13, padding: '6px 10px' }}
                        />
                      </div>
                    );
                  })}
                </div>
              )
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                onClick={handleDeployWithEnvVars}
                style={{ ...btnPrimary, backgroundColor: '#7c4dff', color: '#fff', fontSize: 15, padding: '12px 28px' }}
              >
                🚀 Deploy App
              </button>
              <button
                onClick={handleDeployWithEnvVars}
                style={{ ...btnPrimary, backgroundColor: '#444', color: '#ccc', fontSize: 13, padding: '10px 18px' }}
              >
                Skip — deploy without credentials
              </button>
            </div>
          </div>
        )}

        {/* Live app card — shown once deployed */}
        {appUrl && (
          <div style={{ backgroundColor: '#1a2e1a', border: '2px solid #4caf50', borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <span style={{ color: '#4caf50', fontSize: 12, fontWeight: 700 }}>🟢 LIVE</span>
                <p style={{ color: '#fff', margin: '4px 0 0', fontSize: 14, fontFamily: 'monospace' }}>{appUrl}</p>
                <p style={{ color: '#888', margin: '4px 0 0', fontSize: 12 }}>Pinned to your launch screen automatically</p>
              </div>
              <button
                type="button"
                onClick={() => setViewerApp({
                  appName: deployAppResult?.appName,
                  displayName: formData.appDisplayName || formData.repo || deployAppResult?.appName,
                  url: appUrl,
                  status: 'running',
                })}
                style={{ ...btnPrimary, backgroundColor: '#4caf50', color: '#fff', whiteSpace: 'nowrap', cursor: 'pointer' }}
              >
                Open App ↗
              </button>
            </div>
          </div>
        )}

        {/* Repo card */}
        {repo && (
          <div style={{ backgroundColor: '#1e1e2e', border: '1px solid #2e7d32', borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <span style={{ color: '#888', fontSize: 12 }}>GitHub Repository</span>
                <p style={{ color: '#fff', margin: '2px 0 0', fontSize: 14, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {repo.fullName}
                </p>
                <p style={{ color: '#888', margin: '4px 0 0', fontSize: 12 }}>
                  {repo.isPrivate ? '🔒 Private' : '🌐 Public'} · branch: <code>{repo.defaultBranch}</code>
                  {scaffoldResult?.filesCreated ? ` · ${scaffoldResult.filesCreated} files` : ''}
                </p>
              </div>
              <a
                href={repo.htmlUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...btnPrimary, backgroundColor: '#2e7d32', color: '#fff', textDecoration: 'none', whiteSpace: 'nowrap' }}
              >
                View Code ↗
              </a>
            </div>
          </div>
        )}

        {/* Actions */}
        {(allDone || hasError) && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            {appUrl && allDone && (
              <button
                type="button"
                onClick={() => setViewerApp({
                  appName: deployAppResult?.appName,
                  displayName: formData.appDisplayName || formData.repo || deployAppResult?.appName,
                  url: appUrl,
                  status: 'running',
                })}
                style={{ ...btnPrimary, backgroundColor: '#4caf50', color: '#fff', fontSize: 15, padding: '12px 28px', cursor: 'pointer' }}
              >
                Open App ↗
              </button>
            )}
            {hasError && deployPhase === 'error' && scaffoldPhase === 'done' && (
              <button
                onClick={handleRetryDeploy}
                style={{ ...btnPrimary, backgroundColor: '#1565c0', color: '#fff', fontSize: 15, padding: '12px 28px' }}
              >
                🔄 Retry Deploy
              </button>
            )}
            <button onClick={onClose} style={{ ...btnPrimary, backgroundColor: '#1976d2', color: '#fff', fontSize: 15, padding: '12px 28px' }}>
              ✅ Done
            </button>
            <button onClick={() => { setDeployResult(null); setScaffoldResult(null); setScaffoldPhase(null); setDeployAppResult(null); setDeployPhase(null); setBuildEnvVars([]); setBuildEnvPhase(null); setStep('choose'); }} style={btnBack}>
              ↩ Start Over
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── Enhance: describe changes to an existing repo ────────────────────────

  // Edit flow: read the selected Jira ticket and load its summary/description
  // into the change-request box so the existing enhance pipeline applies the
  // changes described in the ticket. The injected text is tracked in a ref so
  // swapping or clearing the ticket replaces/removes just that block without
  // clobbering any notes the user typed themselves.
  const loadJiraTicketIntoEnhance = useCallback(async (issueKey) => {
    if (!issueKey) return;
    setEnhanceJiraLoading(true);
    setEnhanceJiraError('');
    try {
      const apiUrl = getApiBase();
      const res = await authenticatedFetch(`${apiUrl}/api/jira/issues/${encodeURIComponent(issueKey)}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success || !data.issue) {
        throw new Error(data.error || `Could not read ${issueKey} from Jira.`);
      }
      const issue = data.issue;
      setEnhanceJiraTicket(issue);
      const header = `Apply the changes described in Jira ${issue.key}: ${issue.summary || ''}`.trim();
      const body = (issue.description || '').trim()
        || '(The ticket has no description — use the summary above as the change request.)';
      const block = `${header}\n\n${body}`;
      setEnhanceDescription((prev) => {
        let base = prev || '';
        if (enhanceJiraBlockRef.current && base.includes(enhanceJiraBlockRef.current)) {
          base = base.replace(enhanceJiraBlockRef.current, '').trim();
        }
        enhanceJiraBlockRef.current = block;
        return base ? `${base}\n\n${block}` : block;
      });
    } catch (err) {
      setEnhanceJiraError(err.message || 'Failed to read the Jira ticket.');
      setEnhanceJiraTicket(null);
    } finally {
      setEnhanceJiraLoading(false);
    }
  }, [authenticatedFetch]);

  // Remove the ticket-derived block from the change request and reset Jira state
  // (used when the ticket is cleared or the Jira toggle is switched off).
  const clearEnhanceJiraBlock = useCallback(() => {
    setEnhanceDescription((prev) => {
      if (enhanceJiraBlockRef.current && prev.includes(enhanceJiraBlockRef.current)) {
        return prev.replace(enhanceJiraBlockRef.current, '').trim();
      }
      return prev;
    });
    enhanceJiraBlockRef.current = '';
    setEnhanceJiraTicket(null);
    setEnhanceJiraError('');
  }, []);

  // Best-effort: after a ticket-driven change is applied, post a comment back on
  // the Jira ticket noting Lyric implemented it, with a link to the PR/branch.
  // Never throws — a failed stamp must not disturb the success screen.
  const postLyricJiraStamp = useCallback(async (issueKey, result, mode) => {
    if (!issueKey) return;
    setEnhanceJiraStamp({ status: 'posting', key: issueKey });
    try {
      const apiUrl = getApiBase();
      const target = `${formData.owner}/${formData.repo}`;
      const filesChanged = result?.filesChanged;
      const fileSuffix = typeof filesChanged === 'number'
        ? ` (${filesChanged} file${filesChanged !== 1 ? 's' : ''} changed)`
        : '';
      const isPr = (result?.mode || mode) === 'pr';
      const lines = [
        '🎵 Implemented by Lyric',
        '',
        `Lyric applied the changes described in this ticket to ${target}.`,
        isPr
          ? `A pull request was opened for review${fileSuffix}.`
          : `Changes were pushed to ${formData.branch || 'main'}${fileSuffix}.`,
      ];
      const link = isPr && result?.prUrl
        ? { url: result.prUrl, label: `View PR${result.prNumber ? ` #${result.prNumber}` : ''}` }
        : null;
      const res = await authenticatedFetch(`${apiUrl}/api/jira/issues/${encodeURIComponent(issueKey)}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: lines.join('\n'), link }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not add the comment.');
      setEnhanceJiraStamp({ status: 'done', key: issueKey });
    } catch (err) {
      setEnhanceJiraStamp({ status: 'error', key: issueKey, error: err.message });
    }
  }, [authenticatedFetch, formData.owner, formData.repo, formData.branch]);

  // `overrides` lets the Visual Edit flow drive the same pipeline with a
  // composed element-anchored description and a forced PR mode, without touching
  // the free-text `enhanceDescription`/`enhanceMode` the manual form uses.
  const handleEnhanceSubmit = async (overrides = {}) => {
    const description = (typeof overrides.description === 'string' ? overrides.description : enhanceDescription).trim();
    const mode = overrides.mode || enhanceMode;
    if (!description) return;
    setEnhancePhase('working');
    setEnhanceResult(null);
    setEnhanceJiraStamp(null);
    setError('');
    setStep('enhancing');
    try {
      const apiUrl = getApiBase();
      const enhanceHeaders = { 'Content-Type': 'application/json' };
      const pat = (githubToken || '').trim();
      if (pat) enhanceHeaders['X-User-GitHub-Token'] = pat;
      const res = await authenticatedFetch(`${apiUrl}/api/deploy/enhance`, {
        method: 'POST',
        headers: enhanceHeaders,
        body: JSON.stringify({
          owner: formData.owner,
          repo: formData.repo,
          branch: formData.branch || 'main',
          appName: initialEdit?.appName,
          subdir: initialEdit?.subdir || formData.stackInfo?.subdir || '',
          changeDescription: description,
          selectedModel,
          mode,
          // New integrations picked in the "🔌 Connected Services" section —
          // lets the enhance prompt include the same env-var/relay instructions
          // as the build-from-scratch scaffold prompt for these service ids.
          ...((formData.selectedServices || []).length > 0
            ? { selectedServices: formData.selectedServices, serviceConfig: formData.serviceConfig }
            : {}),
          // When fixing an issue on an already-opened PR, keep committing to the
          // same branch and reuse that PR instead of opening a new one.
          ...(overrides.prBranch ? { prBranch: overrides.prBranch } : {}),
          ...(overrides.prNumber ? { prNumber: overrides.prNumber } : {}),
          ...(overrides.prUrl ? { prUrl: overrides.prUrl } : {}),
          // Clarifying-questions round-trip: when the user has answered Lyric's
          // follow-up questions (or chose to proceed anyway) we resubmit with the
          // answers + skipClarify so the server folds them in and does NOT ask again.
          ...(Array.isArray(overrides.clarificationAnswers) && overrides.clarificationAnswers.length > 0
            ? { clarificationAnswers: overrides.clarificationAnswers }
            : {}),
          ...(Array.isArray(overrides.clarificationAssumptions)
            ? { clarificationAssumptions: overrides.clarificationAssumptions }
            : {}),
          ...(overrides.skipClarify ? { skipClarify: true } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Enhance failed');
      // The server may ask clarifying questions BEFORE touching the repo. Surface
      // them, collect answers, then resubmit the exact same request augmented with
      // the answers (see handleClarifySubmit / handleClarifySkip).
      if (data?.status === 'needs_clarification') {
        setEnhanceClarify({
          questions: Array.isArray(data.questions) ? data.questions : [],
          assumptions: Array.isArray(data.assumptions) ? data.assumptions : [],
          answers: {},
          baseOverrides: { ...overrides, description },
        });
        setEnhancePhase('clarify');
        return;
      }
      setEnhanceResult(data);
      setEnhancePhase('done');
      // Stamp the Jira ticket only for a manual, ticket-driven Apply (not the
      // visual-edit or fix-issues re-runs, which pass an override description).
      if (enhanceUseJira && enhanceJira.issueKey && typeof overrides.description !== 'string'
          && data?.status !== 'no-changes') {
        postLyricJiraStamp(enhanceJira.issueKey, data, mode);
      }
    } catch (err) {
      setError(err.message);
      setEnhancePhase('error');
    }
  };

  // Clarifying-questions round-trip. The user answered (some of) Lyric's
  // questions; resubmit the SAME request with the Q&A + skipClarify so the server
  // folds the answers into the edit and never asks again. Unanswered questions
  // are sent blank — the server treats those as "use your best judgement".
  const handleClarifySubmit = () => {
    if (!enhanceClarify) return;
    const { questions = [], answers = {}, baseOverrides = {} } = enhanceClarify;
    const clarificationAnswers = questions.map((q) => ({
      question: q.question,
      answer: (answers[q.id] || '').trim(),
    }));
    setEnhanceClarify(null);
    handleEnhanceSubmit({ ...baseOverrides, clarificationAnswers, skipClarify: true });
  };

  // "Skip & let Lyric decide" — proceed with no answers but still skip the gate
  // so the server goes straight to generating with its stated assumptions.
  const handleClarifySkip = () => {
    if (!enhanceClarify) return;
    const { assumptions = [], baseOverrides = {} } = enhanceClarify;
    setEnhanceClarify(null);
    handleEnhanceSubmit({ ...baseOverrides, clarificationAssumptions: assumptions, skipClarify: true });
  };


  // validation warnings by committing more changes to the SAME PR, then land
  // back on the Pull Request screen to finish the flow.
  const handleFixIssues = () => {
    const warnings = enhanceResult?.validationWarnings || [];
    if (!warnings.length) return;
    const lines = warnings.map(w => `- ${w.path}: ${w.warning}`).join('\n');
    const description =
      `[FIX] A previous automated change in this pull request introduced the following issue(s) that Lyric flagged. ` +
      `Fix ONLY these problems in the affected files so the code is valid and builds cleanly. ` +
      `Do NOT revert or undo the intended feature — keep all other changes intact and just correct the problem:\n\n${lines}`;
    setFixConfirmOpen(false);
    handleEnhanceSubmit({
      description,
      mode: 'pr',
      skipClarify: true,
      prBranch: enhanceResult?.prBranch || undefined,
      prNumber: enhanceResult?.prNumber || undefined,
      prUrl: enhanceResult?.prUrl || undefined,
    });
  };

  // Shared: render an element "anchor" into precise, source-locating lines.
  const anchorLines = (anchor, heading = 'ELEMENT') => {
    const lines = [];
    if (!anchor) return lines;
    lines.push(`${heading} (as rendered in the browser):`);
    lines.push(`- Tag: <${anchor.tag}>`);
    if (anchor.id) lines.push(`- id: ${anchor.id}`);
    if (anchor.classes) lines.push(`- class: ${anchor.classes}`);
    if (anchor.text) lines.push(`- Visible text: "${anchor.text}"`);
    if (anchor.cssPath) lines.push(`- DOM path: ${anchor.cssPath}`);
    if (anchor.pagePath) lines.push(`- Page: ${anchor.pagePath}${anchor.pageTitle ? ` (title: "${anchor.pageTitle}")` : ''}`);
    if (anchor.styles) lines.push(`- Computed style: color ${anchor.styles.color}, background ${anchor.styles.background}, font-size ${anchor.styles.fontSize}, font-weight ${anchor.styles.fontWeight}`);
    if (anchor.outerHTML) { lines.push('- Rendered HTML:'); lines.push(anchor.outerHTML); }
    return lines;
  };

  // A short human-readable label for a manual op, shown in the change list.
  const clip = (s, n) => { const t = String(s || ''); return t.length > n ? `${t.slice(0, n)}…` : t; };
  const manualOpLabel = (op) => {
    const who = op.anchor ? `<${op.anchor.tag}>${op.anchor.text ? ` “${clip(op.anchor.text, 24)}”` : ''}` : 'element';
    switch (op.kind) {
      case 'setText': return `✏️ Text of ${who} → “${clip(op.newText, 36)}”`;
      case 'move': {
        const ref = op.position && op.position.ref ? `<${op.position.ref.tag}>${op.position.ref.text ? ` “${clip(op.position.ref.text, 18)}”` : ''}` : 'target';
        return `↕️ Move ${who} ${op.position ? op.position.relation : ''} ${ref}`;
      }
      case 'add': {
        const p = op.position;
        if (p && p.relation === 'absolute') return `➕ Add ${op.elementType || 'element'} at (${Math.round(p.left)}, ${Math.round(p.top)})px`;
        return `➕ Add ${op.elementType || 'element'}${p && p.ref ? ` after <${p.ref.tag}>` : ''}`;
      }
      case 'delete': return `🗑️ Delete ${who}`;
      case 'duplicate': return `⧉ Duplicate ${who}`;
      case 'nudge': return `✥ Nudge ${who} to (${Math.round(op.dx)}, ${Math.round(op.dy)})px`;
      case 'reposition': return `✥ Move ${who} to (${Math.round(op.left)}, ${Math.round(op.top)})px`;
      case 'style': return `🎨 ${who}: ${op.prop} → ${op.value}`;
      case 'sketch': {
        const it = (op.items && op.items[0]) || {};
        const type = it.type === 'arrow' ? 'Arrow' : it.type === 'box' ? 'Box' : 'Mark';
        const rule = op.note && op.note.trim() ? `: “${clip(op.note.trim(), 40)}”` : ' (add a rule)';
        return `🖌 ${type}${rule}`;
      }
      default: return `Change ${who}`;
    }
  };

  // Translate a manual op into a precise natural-language instruction for the
  // enhance AI (the "what to change" — anchors are added separately).
  const describeManualOp = (op) => {
    switch (op.kind) {
      case 'setText':
        return `Change the visible text of this element from "${op.oldText}" to "${op.newText}". Update the text in the source (template / JSX / HTML) that renders it, and do not change anything else about the element.`;
      case 'move': {
        const rel = op.position && op.position.relation === 'before' ? 'BEFORE' : 'AFTER';
        const ref = op.position && op.position.ref;
        const refDesc = ref
          ? `the <${ref.tag}>${ref.text ? ` that contains the text "${ref.text}"` : ''}${ref.id ? ` (id "${ref.id}")` : ''}`
          : 'the referenced element';
        return `Move this element in the source so it renders immediately ${rel} ${refDesc}. Reorder the actual markup (do NOT use absolute positioning or transforms); keep the element and its contents identical — only change where it appears in the layout flow.`;
      }
      case 'add': {
        const p = op.position;
        if (p && p.relation === 'absolute') {
          return `Add a new ${op.elementType || 'element'} positioned absolutely at left ${Math.round(p.left)}px, top ${Math.round(p.top)}px relative to the page (CSS position: absolute). Use markup equivalent to: ${op.html}. Match the surrounding code style and wire it in appropriately; if there's no obvious behavior, leave it as a clean static placeholder.`;
        }
        const ref = p && p.ref;
        const where = ref
          ? `immediately AFTER the <${ref.tag}>${ref.text ? ` that contains "${ref.text}"` : ''}${ref.id ? ` (id "${ref.id}")` : ''}`
          : 'at the end of the page body';
        return `Add a new ${op.elementType || 'element'} ${where}. Use markup equivalent to: ${op.html}. Match the surrounding code style and wire it in appropriately; if there's no obvious behavior, leave it as a clean static placeholder.`;
      }
      case 'delete':
        return `Remove this element entirely from the source that renders it. Delete its markup (and any now-dead directly-associated code) without affecting sibling elements.`;
      case 'duplicate':
        return `Duplicate this element in the source, placing the copy immediately AFTER the original with identical markup and content.`;
      case 'reposition': {
        const positioning = op.positioning || {};
        const mode = positioning.mode === 'fixed' ? 'fixed' : 'absolute';
        const containingBlock = positioning.containingBlock
          ? `the existing containing block <${positioning.containingBlock.tag}>${positioning.containingBlock.id ? `#${positioning.containingBlock.id}` : ''}${positioning.containingBlock.cssPath ? ` (${positioning.containingBlock.cssPath})` : ''}`
          : (mode === 'fixed' ? 'the viewport' : 'the existing containing block');
        const pageLeft = Number.isFinite(positioning.pageLeft) ? positioning.pageLeft : op.left;
        const pageTop = Number.isFinite(positioning.pageTop) ? positioning.pageTop : op.top;
        const viewportLeft = Number.isFinite(positioning.viewportLeft) ? positioning.viewportLeft : op.left;
        const viewportTop = Number.isFinite(positioning.viewportTop) ? positioning.viewportTop : op.top;
        const coordinates = `page coordinates left ${Math.round(pageLeft)}px, top ${Math.round(pageTop)}px (viewport coordinates left ${Math.round(viewportLeft)}px, top ${Math.round(viewportTop)}px)`;
        return `Keep this element's existing CSS position mode: ${mode}. Keep its existing containing block (${containingBlock}) and place it at ${coordinates}; its CSS left/top offsets in that containing block should be ${Math.round(op.left)}px and ${Math.round(op.top)}px. Apply the styling using the existing convention (class / stylesheet / inline style). Keep everything else identical.`;
      }
      case 'nudge':
        return `Offset this element from its natural position by ${Math.round(op.dx)}px horizontally and ${Math.round(op.dy)}px vertically (positive = right / down, negative = left / up). Apply it the way this codebase styles things — e.g. position: relative with left/top, a transform: translate(), or a margin adjustment — using the existing convention rather than an ad-hoc override. Keep everything else identical.`;
      case 'style':
        return `Change the ${op.prop} of this element to "${op.value}". Apply it the way this codebase already does styling (existing class / stylesheet / inline style — prefer the existing convention) rather than an ad-hoc override.`;
      case 'sketch': {
        const it = (op.items && op.items[0]) || {};
        const t = it.targets || [];
        const g = it.geo || {};
        const tgt = (tg) => {
          const a = tg && tg.anchor;
          if (!a) return 'an unidentified element';
          return `the <${a.tag}>${a.text ? ` containing "${clip(a.text, 48)}"` : ''}${a.id ? ` (id "${a.id}")` : ''}${a.cssPath ? ` [DOM path: ${a.cssPath}]` : ''}`;
        };
        let where;
        if (it.type === 'arrow') {
          const from = t.find((x) => x.role === 'from');
          const to = t.find((x) => x.role === 'to');
          where = `an ARROW drawn from ${from ? tgt(from) : `page point (${g.from ? g.from.x : '?'}, ${g.from ? g.from.y : '?'})`} pointing to ${to ? tgt(to) : `page point (${g.to ? g.to.x : '?'}, ${g.to ? g.to.y : '?'})`}`;
        } else if (it.type === 'box') {
          const ar = t[0];
          where = `a BOX around the region (left ${g.x}px, top ${g.y}px, ${g.w}×${g.h}px)${ar ? `, i.e. ${tgt(ar)}` : ''}`;
        } else {
          const ov = t[0];
          where = `a freehand mark${ov ? ` over ${tgt(ov)}` : ` near page point (${g.x}, ${g.y})`}`;
        }
        const rule = op.note && op.note.trim()
          ? `The instruction the user attached to this mark reads: "${op.note.trim()}".`
          : `No text rule was attached to this mark, so infer the intent from the drawing and the element it points at.`;
        return `The user drew ${where} directly on the running app UI as a visual instruction. ${rule} Interpret the drawing together with the instruction and implement the change in the source that renders this element (JSX/TSX, a server template, or plain HTML). Match the existing code style and keep everything else intact.`;
      }
      default:
        return `Apply the requested change to this element.`;
    }
  };

  // Build one change block (handles both AI free-text changes and manual ops).
  const buildChangeBlock = (change, index, total) => {
    const lines = [];
    lines.push(total > 1 ? `── CHANGE ${index + 1} of ${total} ──` : '── CHANGE ──');
    if (change.op) {
      lines.push(...anchorLines(change.op.anchor, 'TARGET ELEMENT'));
      lines.push(`- REQUESTED CHANGE: ${describeManualOp(change.op)}`);
      if (change.op.position && change.op.position.ref) {
        lines.push('');
        lines.push(...anchorLines(change.op.position.ref, 'REFERENCE ELEMENT (for placement)'));
      }
    } else {
      lines.push(...anchorLines(change.anchor, 'SELECTED ELEMENT'));
      lines.push(`- REQUESTED CHANGE: ${String(change.instruction).trim()}`);
    }
    return lines.join('\n');
  };

  // Combine one or more element-anchored changes into a single enhance description.
  const composeVisualEditDescription = (changes) => {
    const lines = [];
    lines.push(changes.length > 1
      ? `[VISUAL EDIT] The user made ${changes.length} changes directly on the running app UI (clicking, editing text, moving, adding, deleting, or restyling elements). Apply ALL of the following changes to the source.`
      : '[VISUAL EDIT] The user made a change directly on the running app UI. Apply it to the source.');
    lines.push('');
    changes.forEach((c, i) => {
      lines.push(buildChangeBlock(c, i, changes.length));
      lines.push('');
    });
    lines.push('Locate the source that renders each element — it may be JSX/TSX, a server template (Jinja/EJS/Handlebars/ERB), or plain HTML. Rendered class names may be hashed or transformed by the build, so match primarily on the visible text, tag, id and surrounding structure. Apply ONLY the requested changes and keep all other behavior intact. Keep template control flow balanced (never unbalance {% if %}/{% endif %}/{% block %} tags).');
    return lines.join('\n');
  };

  // The queued changes, plus the in-progress AI instruction typed but not added.
  const pendingVisualChanges = () => {
    const list = [...visualChanges];
    if (visualSelected && visualInstruction.trim()) {
      list.push({ id: 'current', anchor: visualSelected, instruction: visualInstruction.trim() });
    }
    return list;
  };

  // Stage the current selection + instruction onto the change list so the user
  // can keep touring the app and collect several edits before applying.
  const addVisualChange = () => {
    if (!visualSelected || !visualInstruction.trim()) return;
    setVisualChanges(prev => [...prev, {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      anchor: visualSelected,
      instruction: visualInstruction.trim(),
    }]);
    setVisualInstruction('');
    setVisualSelected(null);
  };

  const removeVisualChange = (id) => setVisualChanges(prev => prev.filter(c => c.id !== id));

  // Post a command into the live-app overlay (add/delete/duplicate/style/mode).
  const sendVisualCmd = (op) => {
    try {
      const win = visualIframeRef.current && visualIframeRef.current.contentWindow;
      if (win) win.postMessage({ __lyriccmd: true, type: 'apply', op }, visualAppOrigin);
    } catch { /* ignore */ }
  };
  const setVisualModeCmd = (m) => {
    setVisualMode(m);
    if (m !== 'edit') setVisualSelected(null);
    sendVisualCmd({ kind: 'setMode', mode: m });
    // Entering draw: sync the overlay's active tool/colour with the panel.
    if (m === 'draw') sendVisualCmd({ kind: 'drawTool', tool: visualDrawTool, color: visualDrawColor });
  };
  // Draw-mode controls: pick a tool/colour, undo the last mark, clear, or stage
  // the whole sketch (plus the written note) onto the change list.
  const setVisualDrawToolCmd = (tool, color) => {
    const t = tool || visualDrawTool;
    const c = color || visualDrawColor;
    setVisualDrawTool(t);
    if (color) setVisualDrawColor(c);
    sendVisualCmd({ kind: 'drawTool', tool: t, color: c });
  };
  const sendVisualDrawUndo = () => sendVisualCmd({ kind: 'drawUndo' });
  const sendVisualDrawClear = () => sendVisualCmd({ kind: 'drawClear' });
  // Toggle the red "navigate to edit" circles that mark links to other pages.
  const setVisualNavLinksCmd = (on) => {
    setVisualNavLinks(on);
    visualNavLinksRef.current = on;
    sendVisualCmd({ kind: 'navBadges', on });
  };
  // Ask the overlay to revert its most recent manipulation (editor-style undo).
  const sendVisualUndo = () => {
    try {
      const win = visualIframeRef.current && visualIframeRef.current.contentWindow;
      if (win) win.postMessage({ __lyriccmd: true, type: 'undo' }, visualAppOrigin);
    } catch { /* ignore */ }
  };

  // Apply every queued change (plus any in-progress one) as a single PR.
  const applyVisualChanges = () => {
    const list = pendingVisualChanges();
    if (list.length === 0) return;
    const description = composeVisualEditDescription(list);
    setVisualStudioOpen(false);
    setVisualChanges([]);
    setVisualInstruction('');
    setVisualSelected(null);
    setVisualAddOpen(false);
    setVisualMode('edit');
    setVisualSketchCount(0);
    // Visual edits default to a PR so nothing hits the running app until review.
    // They're already precise (anchored to specific elements), so skip the
    // clarifying-questions gate — it only helps free-text requests.
    handleEnhanceSubmit({ description, mode: 'pr', skipClarify: true });
  };

  // Full-screen "Visual Edit Studio": a large live-app iframe on the left and a
  // roomy editing panel on the right. Selecting an element in the iframe posts a
  // message (handled by the effect above) that populates `visualSelected`.
  const renderVisualStudio = () => {
    if (!visualStudioOpen || !initialEdit?.appName) return null;
    const apiUrl = getApiBase();
    const pending = pendingVisualChanges();
    const closeStudio = () => setVisualStudioOpen(false);
    const barBtn = { background: '#241f38', border: '1px solid #4a3f6e', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: '#d1c4e9', fontSize: 13 };
    const toolBtn = { background: '#241f38', border: '1px solid #4a3f6e', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: '#d1c4e9', fontSize: 13, fontWeight: 600 };
    const miniBtn = { background: '#241f38', border: '1px solid #3a3450', borderRadius: 6, padding: '5px 9px', cursor: 'pointer', color: '#d1c4e9', fontSize: 12 };
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 10001, background: '#0d0b16', display: 'flex', flexDirection: 'column' }}>
        {/* Header bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid #2a2440', background: '#141024', flexShrink: 0 }}>
          <div style={{ fontSize: 20 }}>🎨</div>
          <div style={{ color: '#e6ddff', fontSize: 15, fontWeight: 700 }}>
            Visual Edit Studio
            <span style={{ color: '#9a90b5', fontSize: 12, fontWeight: 400, marginLeft: 8 }}>{initialEdit.appName}</span>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ display: 'flex', background: '#241f38', border: '1px solid #4a3f6e', borderRadius: 8, overflow: 'hidden' }}>
              <button
                type="button"
                onClick={() => setVisualModeCmd('edit')}
                style={{ background: visualMode === 'edit' ? '#7e57c2' : 'transparent', color: visualMode === 'edit' ? '#fff' : '#b39ddb', border: 'none', padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                title="Click to select, double-click to edit text, drag to move"
              >✏️ Edit</button>
              <button
                type="button"
                onClick={() => setVisualModeCmd('navigate')}
                style={{ background: visualMode === 'navigate' ? '#7e57c2' : 'transparent', color: visualMode === 'navigate' ? '#fff' : '#b39ddb', border: 'none', padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                title="Click through the app normally to reach another page, then switch back to Edit"
              >🧭 Navigate</button>
              <button
                type="button"
                onClick={() => setVisualModeCmd('draw')}
                style={{ background: visualMode === 'draw' ? '#7e57c2' : 'transparent', color: visualMode === 'draw' ? '#fff' : '#b39ddb', border: 'none', padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                title="Draw on the app (arrows, boxes, freehand) to explain a change, then stage the sketch for the AI"
              >🖌 Draw</button>
            </div>
            <button type="button" onClick={sendVisualUndo} disabled={!visualChanges.some(c => c.kind === 'manual')} style={{ ...barBtn, opacity: visualChanges.some(c => c.kind === 'manual') ? 1 : 0.45, cursor: visualChanges.some(c => c.kind === 'manual') ? 'pointer' : 'default' }} title="Undo last direct edit (⌘Z / Ctrl+Z)">↶ Undo</button>
            <button
              type="button"
              onClick={() => setVisualNavLinksCmd(!visualNavLinks)}
              style={{ ...barBtn, borderColor: visualNavLinks ? '#ef4444' : '#4a3f6e', color: visualNavLinks ? '#ff8a80' : '#b39ddb' }}
              title="Show a red circle on every element that links to another page — click a circle to open that page for editing"
            >🔴 Nav links {visualNavLinks ? 'on' : 'off'}</button>
            <button type="button" onClick={() => setVisualIframeKey(k => k + 1)} style={barBtn}>↻ Reload</button>
            <a href={`${apiUrl}/apps/${initialEdit.appName}/`} target="_blank" rel="noreferrer" style={{ ...barBtn, textDecoration: 'none' }}>Open in new tab ↗</a>
            <button type="button" onClick={closeStudio} style={{ ...barBtn, borderColor: '#5a4a4a', color: '#ffab91' }}>✕ Close editor</button>
          </div>
        </div>

        {/* Body: iframe + editing panel */}
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Live app */}
          <div style={{ flex: 1, minWidth: 0, background: '#fff', position: 'relative' }}>
            <iframe
              ref={visualIframeRef}
              key={visualIframeKey}
              title="Visual edit preview"
              src={`${apiUrl}/apps/${initialEdit.appName}/?__lyricEdit=1`}
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            />
          </div>

          {/* Editing panel */}
          <div style={{ width: 420, flexShrink: 0, background: '#141024', borderLeft: '1px solid #2a2440', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ padding: '14px 16px', overflowY: 'auto', flex: 1 }}>
              {/* Direct-edit toolbar (mirrors the mockup builder) */}
              {visualMode === 'edit' ? (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative' }}>
                      <button type="button" onClick={() => setVisualAddOpen(o => !o)} style={toolBtn}>➕ Add element ▾</button>
                      {visualAddOpen && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: '#1c1732', border: '1px solid #4a3f6e', borderRadius: 8, zIndex: 5, minWidth: 190, maxHeight: 360, overflowY: 'auto', boxShadow: '0 6px 20px rgba(0,0,0,.5)' }}>
                          {VISUAL_ADD_GROUPS.map(([group, items]) => (
                            <div key={group}>
                              <div style={{ padding: '7px 12px 3px', fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#6b6485', position: 'sticky', top: 0, background: '#1c1732' }}>{group}</div>
                              {items.map(([k, label]) => (
                                <button key={k} type="button" onClick={() => { sendVisualCmd({ kind: 'add', elementType: k }); setVisualAddOpen(false); }} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', color: '#e0e0e0', padding: '7px 12px', cursor: 'pointer', fontSize: 13 }}>{label}</button>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <span style={{ color: '#6b6485', fontSize: 11 }}>
                      appears in the center — drag it where you want, then click Place
                    </span>
                  </div>
                  <div style={{ color: '#6b6485', fontSize: 11, marginTop: 8, lineHeight: 1.5 }}>
                    Double-click text to edit it, or drag an element to move it — a toolbar (move / duplicate / delete) pins above the selected element, and <strong>Delete</strong>/<strong>Backspace</strong> removes it. Every direct edit is staged in the change list below. <span style={{ color: '#ff8a80' }}>🔴 red circles</span> mark links to other pages; click one to open that page for editing.
                  </div>
                </div>
              ) : visualMode === 'draw' ? (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: '#9575cd', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
                    🖌 Draw to explain {visualSketchCount > 0 && <span style={{ color: '#ce93d8' }}>({visualSketchCount} mark{visualSketchCount === 1 ? '' : 's'})</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    {[['pen', '✎ Pen'], ['arrow', '↗ Arrow'], ['box', '▭ Box']].map(([t, label]) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setVisualDrawToolCmd(t)}
                        style={{ flex: 1, background: visualDrawTool === t ? '#7e57c2' : '#241f38', color: visualDrawTool === t ? '#fff' : '#b39ddb', border: '1px solid #4a3f6e', borderRadius: 8, padding: '7px 6px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                      >{label}</button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ color: '#6b6485', fontSize: 11 }}>Colour</span>
                    {['#ef4444', '#3b82f6', '#22c55e', '#f59e0b', '#a855f7'].map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setVisualDrawToolCmd(visualDrawTool, c)}
                        title={c}
                        style={{ width: 20, height: 20, borderRadius: '50%', background: c, cursor: 'pointer', border: visualDrawColor === c ? '2px solid #fff' : '2px solid transparent', boxShadow: visualDrawColor === c ? '0 0 0 1px #4a3f6e' : 'none' }}
                      />
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <button type="button" onClick={sendVisualDrawUndo} disabled={visualSketchCount === 0} style={{ ...miniBtn, opacity: visualSketchCount === 0 ? 0.45 : 1, cursor: visualSketchCount === 0 ? 'default' : 'pointer' }}>↶ Undo mark</button>
                    <button type="button" onClick={sendVisualDrawClear} disabled={visualSketchCount === 0} style={{ ...miniBtn, opacity: visualSketchCount === 0 ? 0.45 : 1, cursor: visualSketchCount === 0 ? 'default' : 'pointer' }}>✕ Clear all</button>
                  </div>
                  <div style={{ color: '#6b6485', fontSize: 11, marginTop: 4, lineHeight: 1.5 }}>
                    Drag on the app to draw an <strong>arrow</strong>, <strong>box</strong> or <strong>freehand</strong> mark. A label pops up on each mark — type the <strong>rule</strong> for it (what the AI should do). Marks and rules <strong>stay visible</strong> and are added to the change list automatically; the ✕ on a label deletes that mark.
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: 16, background: '#1c1732', border: '1px solid #4a3f6e', borderRadius: 10, padding: 12, color: '#b39ddb', fontSize: 12, lineHeight: 1.6 }}>
                  🧭 <strong>Navigate mode</strong> — click through the app to reach the page you want, then switch back to <strong>✏️ Edit</strong> to change it.
                </div>
              )}

              {/* Current selection / instruction */}
              {visualSelected ? (
                <div style={{ background: '#1c1732', border: '1px solid #4a3f6e', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: '#9575cd', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>Selected element</div>
                  <div style={{ fontSize: 14, color: '#e0e0e0', marginBottom: 4 }}>
                    <code style={{ color: '#ffb74d' }}>&lt;{visualSelected.tag}&gt;</code>
                    {visualSelected.id && <code style={{ color: '#80cbc4', marginLeft: 6 }}>#{visualSelected.id}</code>}
                  </div>
                  {visualSelected.text && (
                    <div style={{ fontSize: 12, color: '#bbb', marginBottom: 4, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }} title={visualSelected.text}>
                      “{visualSelected.text}”
                    </div>
                  )}
                  {visualSelected.classes && (
                    <div style={{ fontSize: 10, color: '#666', marginBottom: 10, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={visualSelected.classes}>
                      .{String(visualSelected.classes).split(/\s+/).filter(Boolean).join(' .')}
                    </div>
                  )}
                  <textarea
                    value={visualInstruction}
                    onChange={(e) => setVisualInstruction(e.target.value)}
                    placeholder="Describe the change for this element, e.g. 'Make this button blue and change the label to Save', or 'Add a tooltip that says Coming soon'."
                    rows={6}
                    autoFocus
                    style={{ ...inputStyle, margin: 0, resize: 'vertical', fontSize: 13, lineHeight: 1.6 }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button
                      type="button"
                      onClick={addVisualChange}
                      disabled={!visualInstruction.trim()}
                      style={{ ...btnPrimary, flex: 1, backgroundColor: visualInstruction.trim() ? '#7e57c2' : '#3a3450', color: '#fff', fontSize: 13, padding: '10px 12px' }}
                    >
                      ➕ Add to change list
                    </button>
                    <button
                      type="button"
                      onClick={() => { setVisualSelected(null); setVisualInstruction(''); }}
                      style={{ background: 'none', border: '1px solid #4a3f6e', borderRadius: 8, padding: '10px 12px', cursor: 'pointer', color: '#9a90b5', fontSize: 13 }}
                    >Clear</button>
                  </div>

                  {/* Direct manipulation quick-actions (applied instantly in the preview, staged automatically) */}
                  <div style={{ borderTop: '1px solid #2f2848', marginTop: 12, paddingTop: 10 }}>
                    <div style={{ fontSize: 10, color: '#6b6485', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Quick edits (staged automatically)</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button type="button" onClick={() => sendVisualCmd({ kind: 'editText' })} style={miniBtn} title="Edit the text inline">✎ Text</button>
                      <button type="button" onClick={() => sendVisualCmd({ kind: 'duplicate' })} style={miniBtn} title="Duplicate this element">⧉ Duplicate</button>
                      <button type="button" onClick={() => sendVisualCmd({ kind: 'delete' })} style={{ ...miniBtn, borderColor: '#5a4a4a', color: '#ffab91' }} title="Delete this element">🗑 Delete</button>
                      <button type="button" onClick={() => sendVisualCmd({ kind: 'fontSize', delta: 2 })} style={miniBtn} title="Increase font size">A+</button>
                      <button type="button" onClick={() => sendVisualCmd({ kind: 'fontSize', delta: -2 })} style={miniBtn} title="Decrease font size">A−</button>
                      <button type="button" onClick={() => sendVisualCmd({ kind: 'bold' })} style={{ ...miniBtn, fontWeight: 800 }} title="Toggle bold">B</button>
                      <button type="button" onClick={() => sendVisualCmd({ kind: 'align', value: 'left' })} style={miniBtn} title="Align left">⯇</button>
                      <button type="button" onClick={() => sendVisualCmd({ kind: 'align', value: 'center' })} style={miniBtn} title="Align center">≡</button>
                      <button type="button" onClick={() => sendVisualCmd({ kind: 'align', value: 'right' })} style={miniBtn} title="Align right">⯈</button>
                      <label style={{ ...miniBtn, display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }} title="Text color">
                        🎨<input type="color" onChange={(e) => sendVisualCmd({ kind: 'color', value: e.target.value })} style={{ width: 20, height: 20, border: 'none', background: 'none', padding: 0, cursor: 'pointer' }} />
                      </label>
                      <label style={{ ...miniBtn, display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }} title="Background color">
                        ▉<input type="color" onChange={(e) => sendVisualCmd({ kind: 'background', value: e.target.value })} style={{ width: 20, height: 20, border: 'none', background: 'none', padding: 0, cursor: 'pointer' }} />
                      </label>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 10, color: '#6b6485', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Nudge position — or arrow keys (Shift = 10px)</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 30px)', gridTemplateRows: 'repeat(3, 26px)', gap: 4, justifyContent: 'start' }}>
                        <span />
                        <button type="button" onClick={() => sendVisualCmd({ kind: 'nudge', dx: 0, dy: -4 })} style={{ ...miniBtn, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Nudge up 4px">▲</button>
                        <span />
                        <button type="button" onClick={() => sendVisualCmd({ kind: 'nudge', dx: -4, dy: 0 })} style={{ ...miniBtn, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Nudge left 4px">◀</button>
                        <span />
                        <button type="button" onClick={() => sendVisualCmd({ kind: 'nudge', dx: 4, dy: 0 })} style={{ ...miniBtn, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Nudge right 4px">▶</button>
                        <span />
                        <button type="button" onClick={() => sendVisualCmd({ kind: 'nudge', dx: 0, dy: 4 })} style={{ ...miniBtn, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Nudge down 4px">▼</button>
                        <span />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ background: '#1c1732', border: '1px dashed #4a3f6e', borderRadius: 10, padding: 20, marginBottom: 16, textAlign: 'center', color: '#9a90b5', fontSize: 13, lineHeight: 1.7 }}>
                  👆 <strong style={{ color: '#d1c4e9' }}>Click any element</strong> in the app on the left to select it, then describe the change here.
                </div>
              )}

              {/* Staged changes list */}
              <div style={{ fontSize: 11, color: '#9575cd', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
                Change list {visualChanges.length > 0 && <span style={{ color: '#ce93d8' }}>({visualChanges.length})</span>}
              </div>
              {visualChanges.length === 0 ? (
                <div style={{ color: '#6b6485', fontSize: 12, marginBottom: 8 }}>
                  No changes staged yet. Add several edits, then apply them together as one PR.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
                  {visualChanges.map((c, i) => {
                    const isManual = c.kind === 'manual';
                    const a = c.anchor || (c.op && (c.op.anchor || (c.op.position && c.op.position.ref))) || null;
                    return (
                      <div key={c.id} style={{ background: '#191428', border: '1px solid #2f2848', borderRadius: 8, padding: '8px 10px', display: 'flex', gap: 8 }}>
                        <div style={{ color: '#7e57c2', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}.</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {isManual ? (
                            <>
                              <div>
                                <span style={{ fontSize: 10, background: '#2f2848', color: '#b39ddb', borderRadius: 4, padding: '1px 6px', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                  {(c.op && c.op.kind) || 'edit'}
                                </span>
                                {a && a.tag && <code style={{ fontSize: 11, color: '#ffb74d', fontFamily: 'monospace', marginLeft: 6 }}>&lt;{a.tag}&gt;</code>}
                              </div>
                              <div style={{ fontSize: 12, color: '#ccc', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                {c.label || 'Manual edit'}
                              </div>
                            </>
                          ) : (
                            <>
                              <div style={{ fontSize: 11, color: '#ffb74d', fontFamily: 'monospace' }}>
                                &lt;{a && a.tag}&gt;{a && a.text ? ` “${String(a.text).slice(0, 24)}${String(a.text).length > 24 ? '…' : ''}”` : ''}
                              </div>
                              <div style={{ fontSize: 12, color: '#ccc', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                {c.instruction}
                              </div>
                            </>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeVisualChange(c.id)}
                          title="Remove"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8a8299', fontSize: 15, flexShrink: 0, alignSelf: 'flex-start' }}
                        >✕</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Sticky apply footer */}
            <div style={{ padding: 14, borderTop: '1px solid #2a2440', background: '#120e20', flexShrink: 0 }}>
              <button
                type="button"
                onClick={applyVisualChanges}
                disabled={pending.length === 0}
                style={{ ...btnPrimary, width: '100%', background: pending.length ? 'linear-gradient(135deg,#ab47bc,#8e24aa)' : '#3a3450', color: '#fff', fontSize: 14, padding: '13px', fontWeight: 700 }}
              >
                ✨ Apply {pending.length || ''} change{pending.length === 1 ? '' : 's'} — opens PR
              </button>
              <div style={{ color: '#6b6485', fontSize: 11, textAlign: 'center', marginTop: 6 }}>
                Nothing changes on the running app until you review &amp; merge the PR.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Re-scans the actual source code (not .env.example) for required env
  // vars, via the same /api/deploy/analyze-config the fresh-deploy flow
  // uses. Kept the old name/button slot so the edit-flow UI doesn't churn,
  // but this now returns only what the code demonstrably reads.
  const loadEnvExample = async () => {
    setLoadingEnvExample(true);
    try {
      const apiUrl = getApiBase();
      const res = await authenticatedFetch(`${apiUrl}/api/deploy/analyze-config`, {
        method: 'POST',
        body: JSON.stringify({
          owner: formData.owner,
          repo: formData.repo,
          branch: formData.branch,
          subdir: formData.stackInfo?.subdir || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Config analysis failed');
      const parsed = (data.envVars || []).map(v => ({ key: v.key, value: '' }));
      // Merge: keep values already entered, add new keys
      setEnhanceEnvVars(prev => {
        const existing = Object.fromEntries(prev.map(e => [e.key, e.value]));
        return parsed.map(v => ({ key: v.key, value: existing[v.key] ?? '' }));
      });
    } catch {
      // Detection failed — leave the manual list as-is
    } finally {
      setLoadingEnvExample(false);
    }
  };

  const handleEnhanceRedeploy = async () => {
    setEnhanceDeployPhase('deploying');
    setEnhanceDeployResult(null);
    try {
      const apiUrl = getApiBase();
      const pat = (githubToken || '').trim();
      const headers = { 'Content-Type': 'application/json' };
      if (pat) headers['X-User-GitHub-Token'] = pat;

      // App has no credential requirements iff the user didn't add any env vars on
      // the enhance step. In that case skip AWS entirely — don't create an empty secret.
      const filledVars = enhanceEnvVars.filter(e => e.key.trim() && e.value.trim());
      const skipSecret = secretMode === 'new' && filledVars.length === 0;

      let secretName = '';
      if (!skipSecret) {
        // Match the name the picker displays (renderSecretPicker is passed the
        // same value at line ~5416): in the edit flow the app's CURRENT secret,
        // else a repo-derived name. Without this, "Create new" showed
        // tools/lyric/<currentSecret> but actually saved to tools/lyric/lyric-<repo>.
        const defaultSecret = initialEdit?.secretName || `lyric-${formData.repo.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
        secretName = resolveSecretName(defaultSecret);
        if (!secretName) throw new Error('Pick an existing AWS secret before redeploying.');

        if (secretMode === 'new') {
          await writeSecretIfNew(secretName, filledVars); // failure is logged; continue regardless
        } else {
          const merge = collectMissingKeyMergeVars();
          if (merge.length > 0) await mergeIntoExistingSecret(secretName, merge);
        }
      }

      const portRes = await authenticatedFetch(`${apiUrl}/api/deploy/free-port`);
      const portData = await portRes.json().catch(() => ({}));
      if (!portRes.ok) throw new Error(portData?.error || 'Could not find a free port');

      const deployRes = await authenticatedFetch(`${apiUrl}/api/deploy/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          owner: formData.owner,
          repo: formData.repo,
          branch: formData.branch || 'main',
          port: portData.port,
          envVars: skipSecret ? [] : (secretMode === 'new' ? filledVars : []),
          // Carry forward the existing app's config so an Edit-flow redeploy
          // truly replaces the running app instead of spawning a parallel one
          // with broken defaults (wrong subdir → missing deps → "No module
          // named flask" style crash).
          appNameOverride: initialEdit?.appName || undefined,
          // Allow display-name change on Edit. If untouched it stays the same;
          // server falls back to repo if blank.
          appDisplayName: (formData.appDisplayName || '').trim() || initialEdit?.displayName || formData.repo,
          subdir: initialEdit?.subdir || formData.stackInfo?.subdir || '',
          startCommand: initialEdit?.startCommand || '',
          buildCommand: initialEdit?.buildCommand || '',
          useLyricEnv: !skipSecret,
          ...(skipSecret ? {} : { secretName }),
          // Carry forward (or update) the Okta group restriction. Empty array
          // means "everyone" — same convention as the initial deploy flow.
          allowedGroups: formData.visibilityMode === 'specific' ? formData.allowedGroups : [],
          coAdmins: formData.coAdmins || [],
          forwardOktaToken: !!formData.forwardOktaToken,
          // NOTE: ownerOnly (Team release) and allowedUsers are intentionally
          // NOT sent from the Edit flow — they're managed LIVE via the Users
          // modal (PUT /owner-only, /allowed-users). Omitting them makes the
          // redeploy carry forward the current live values (see deployStartHandler
          // defensive-carry), so an edit/redeploy never clobbers a live change.
        }),
      });
      const deployData = await deployRes.json().catch(() => ({}));
      if (!deployRes.ok) throw new Error(deployData?.error || 'Deploy failed');

      setEnhanceDeployResult(deployData);
      setEnhanceDeployPhase('starting');

      const targetAppName = deployData.appName;
      const POLL_INTERVAL = 5000;
      const POLL_TIMEOUT = 180_000;
      const pollStart = Date.now();

      const runningEntry = await new Promise((resolve, reject) => {
        const tick = async () => {
          if (Date.now() - pollStart > POLL_TIMEOUT) {
            return reject(new Error('App did not start within 3 minutes.'));
          }
          try {
            const listRes = await authenticatedFetch(`${apiUrl}/api/deploy/list`);
            const listData = await listRes.json().catch(() => ({ apps: [] }));
            const match = (listData.apps || []).find(a => a.appName === targetAppName);
            if (match?.status === 'running') return resolve(match);
            if (match?.status === 'error') return reject(new Error(match.error || 'App crashed on startup. Check server logs.'));
          } catch { /* keep polling */ }
          setTimeout(tick, POLL_INTERVAL);
        };
        setTimeout(tick, POLL_INTERVAL);
      });

      setEnhanceDeployResult(prev => ({ ...prev, url: runningEntry.url || deployData.url }));
      setEnhanceDeployPhase('done');

      try {
        const pins = JSON.parse(localStorage.getItem('lyric-pinned-deployments') || '[]');
        const newPin = {
          appName: targetAppName,
          owner: formData.owner,
          repo: formData.repo,
          branch: formData.branch || 'main',
          url: runningEntry.url || deployData.url,
          port: portData.port,
          deployId: deployData.deployId,
          pinnedAt: new Date().toISOString(),
        };
        localStorage.setItem('lyric-pinned-deployments', JSON.stringify([newPin, ...pins.filter(p => p.appName !== newPin.appName)]));
      } catch { /* ignore */ }

      // Persist any updated credentials
      try {
        const creds = enhanceEnvVars.filter(e => e.key.trim() && e.value.trim());
        if (creds.length > 0)
          localStorage.setItem(`lyric-env-${formData.owner}/${formData.repo}`, JSON.stringify(creds));
      } catch { /* ignore */ }
    } catch (err) {
      setError(err.message);
      setEnhanceDeployPhase('error');
    }
  };

  // Deploy the branch the edit just created through the NORMAL deploy flow
  // (branch → stack → config → review → build), pre-filled with this repo and the
  // new branch, so the user doesn't have to go back and re-select everything.
  // Lands on the validation step with the new branch already chosen.
  const handleDeployEnhancedBranch = () => {
    const branch = enhanceResult?.prBranch;
    const owner = formData.owner;
    const repo = formData.repo;
    if (!branch || !owner || !repo) return;
    // Leave the enhance success screen and reset its transient deploy state so the
    // normal flow renders cleanly.
    setEnhanceResult(null);
    setEnhancePhase(null);
    setEnhanceDeployPhase(null);
    setEnhanceDeployResult(null);
    setEnhanceDescription('');
    setPath('repo');
    validateRepoByName(owner, repo, branch);
  };

  const renderEnhanceStep = () => {
    const apiUrl = getApiBase();
    return (
      <div>
        <h3 style={{ color: '#ffb74d', marginBottom: 8 }}>✏️ Enhance or Debug</h3>
        <p style={{ color: '#aaa', fontSize: 13, marginBottom: 16 }}>
          Tell Lyric what to fix or change in <strong style={{ color: '#fff' }}>{formData.owner}/{formData.repo}</strong>.
          Paste an error message to debug, or describe a new feature or change.
        </p>

        <JiraIssuePicker
          apiUrl={apiUrl}
          authenticatedFetch={authenticatedFetch}
          enabled={enhanceUseJira}
          title="🔗 Base changes on a Jira ticket (optional)"
          subtitleOn="Pick a project and a ticket — Lyric reads it and loads the details into the change request below."
          subtitleOff="Off — describe the change yourself below. Check the box to pull the change from a Jira ticket."
          value={enhanceJira}
          onToggle={(checked) => {
            setEnhanceUseJira(checked);
            if (checked) {
              setEnhanceJira((prev) => ({ ...prev, issueType: prev.issueType || 'Any' }));
            } else {
              clearEnhanceJiraBlock();
              setEnhanceJira({ projectKey: '', issueType: 'Any', issueKey: '', issueSummary: '' });
            }
          }}
          onChange={(next) => {
            const merged = {
              projectKey: next.projectKey || '',
              issueType: next.issueType || 'Any',
              issueKey: next.issueKey || '',
              issueSummary: next.issueSummary || '',
            };
            setEnhanceJira((prev) => {
              if (merged.issueKey && merged.issueKey !== prev.issueKey) {
                loadJiraTicketIntoEnhance(merged.issueKey);
              } else if (!merged.issueKey && prev.issueKey) {
                clearEnhanceJiraBlock();
              }
              return merged;
            });
          }}
        />
        {enhanceUseJira && enhanceJiraLoading && (
          <div style={{ color: '#4fc3f7', fontSize: 12, margin: '0 0 12px' }}>
            ⏳ Reading {enhanceJira.issueKey} from Jira…
          </div>
        )}
        {enhanceUseJira && enhanceJiraError && (
          <div style={{ color: '#ef9a9a', fontSize: 12, margin: '0 0 12px' }}>⚠️ {enhanceJiraError}</div>
        )}
        {enhanceUseJira && enhanceJiraTicket && !enhanceJiraLoading && (
          <div style={{ border: '1px solid #4a3a1a', backgroundColor: '#241d10', borderRadius: 8, padding: 12, margin: '0 0 14px' }}>
            <div style={{ color: '#ffb74d', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
              📄 {enhanceJiraTicket.key} — {enhanceJiraTicket.summary}
            </div>
            <div style={{ color: '#888', fontSize: 11, marginBottom: enhanceJiraTicket.description ? 8 : 0 }}>
              {enhanceJiraTicket.type} · {enhanceJiraTicket.status} · loaded into the change request below — edit it before applying if needed.
            </div>
            {enhanceJiraTicket.description && (
              <div style={{ color: '#cfcfcf', fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 120, overflowY: 'auto', lineHeight: 1.5 }}>
                {enhanceJiraTicket.description.length > 500
                  ? `${enhanceJiraTicket.description.slice(0, 500)}…`
                  : enhanceJiraTicket.description}
              </div>
            )}
          </div>
        )}

        <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>What should change or what's broken?</span>
          {dictation.supported && (
            <span style={{ fontSize: 11, color: dictation.listening ? '#ef5350' : '#888', fontWeight: 400 }}>
              {dictation.listening ? '● Listening — click mic to stop' : '🎤 Click the mic to dictate'}
            </span>
          )}
        </label>
        <div style={{ position: 'relative' }}>
          <textarea
            value={dictation.listening && interimTranscript
              ? `${enhanceDescription}${enhanceDescription && !/\s$/.test(enhanceDescription) ? ' ' : ''}${interimTranscript}`
              : enhanceDescription}
            onChange={(e) => setEnhanceDescription(e.target.value)}
            placeholder="Debugging? Paste the error message and describe where it happens — e.g. 'App crashes on startup: TypeError: cannot read property x of undefined in server.js line 42'\n\nAdding a feature? Describe what to change — e.g. 'Add a /metrics endpoint that returns request counts. Add rate limiting to all routes.'"
            rows={6}
            style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5, marginBottom: 0, paddingRight: 52 }}
          />
          <button
            type="button"
            onClick={() => { dictationTargetRef.current = 'enhanceDescription'; dictation.toggle(); }}
            disabled={!dictation.supported}
            aria-label={dictation.listening ? 'Stop dictation' : 'Start dictation'}
            style={{
              position: 'absolute', top: 8, right: 8,
              width: 36, height: 36, borderRadius: '50%', border: 'none',
              cursor: dictation.supported ? 'pointer' : 'not-allowed',
              backgroundColor: dictation.listening ? '#ef5350' : (dictation.supported ? '#3a3a4a' : '#2a2a2a'),
              color: '#fff', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background-color 0.15s ease',
              animation: dictation.listening ? 'lyricMicPulse 1.5s infinite' : 'none',
            }}
          >
            {dictation.listening ? '⏹' : '🎤'}
          </button>
        </div>
        {dictation.error && (
          <div style={{ color: '#ef9a9a', fontSize: 12, marginTop: 6 }}>⚠️ {dictation.error}</div>
        )}

        {/* Visual edit (beta): entry point to a full-screen studio that iframes
            the live app in select mode. Click elements, stack up changes, and
            apply them as one PR. Only shown when editing a running deployed app. */}
        {initialEdit?.appName && (
          <div style={{ margin: '18px 0', border: '1px solid #3a2f5a', background: 'linear-gradient(135deg,#1b1630,#171522)', borderRadius: 12, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 24 }}>🎨</div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ color: '#e6ddff', fontSize: 15, fontWeight: 700 }}>Visual edit studio <span style={{ color: '#9575cd', fontSize: 11, fontWeight: 500 }}>beta</span></div>
                <div style={{ color: '#9a90b5', fontSize: 12, marginTop: 2 }}>
                  Open your running app full-screen, click any element, describe the change, and stack up
                  edits — then apply them all in one PR.
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setVisualStudioOpen(true); setVisualSelected(null); setVisualInstruction(''); }}
                style={{ ...btnPrimary, background: 'linear-gradient(135deg,#7e57c2,#5e35b1)', color: '#fff', fontSize: 14, padding: '11px 20px', boxShadow: '0 2px 12px rgba(126,87,194,0.4)' }}
              >
                🎨 Open Visual Editor
              </button>
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
              <a
                href={`${apiUrl}/apps/${initialEdit.appName}/`}
                target="_blank"
                rel="noreferrer"
                style={{ color: '#b39ddb', fontSize: 12, textDecoration: 'none' }}
              >Open app in new tab ↗</a>
              {visualChanges.length > 0 && (
                <span style={{ color: '#ce93d8', fontSize: 12 }}>
                  {visualChanges.length} pending change{visualChanges.length === 1 ? '' : 's'} staged
                </span>
              )}
            </div>
          </div>
        )}

        {/*
          Edit flow: do NOT show AWS Secrets Manager at all unless either
            (a) the app was originally deployed with a secret, OR
            (b) the user explicitly added one or more credentials/env vars.
          Otherwise show a single "+ Add credentials" entry point that opens
          the env vars editor. Once vars are added, the AWS picker appears.
        */}
        {(() => {
          const hadSecret = !!initialEdit?.secretName;
          const hasVars = enhanceEnvVars.length > 0;
          const userOptedIn = showSecretsEditor;
          const showSection = hadSecret || hasVars || userOptedIn;

          if (!showSection) {
            return (
              <div style={{ marginTop: 16 }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowSecretsEditor(true);
                    setShowEnhanceEnvVars(true);
                    if (enhanceEnvVars.length === 0) {
                      setEnhanceEnvVars([{ key: '', value: '' }]);
                    }
                  }}
                  style={{ background: 'none', border: '1px dashed #444', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', color: '#888', fontSize: 12, fontWeight: 500 }}
                >
                  + Add credentials / env vars
                </button>
                <p style={{ color: '#555', fontSize: 11, margin: '4px 0 0 4px' }}>
                  Only needed if the updated app uses new API tokens or env vars.
                </p>
              </div>
            );
          }

          return (
            <>
              <div style={{ marginTop: 16 }}>
                <button
                  type="button"
                  onClick={() => setShowSecretsEditor(v => !v)}
                  style={{ background: 'none', border: '1px solid #444', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', color: '#ce93d8', fontSize: 12, fontWeight: 600 }}
                >
                  {showSecretsEditor ? '▾' : '▸'} 🔐 AWS Secrets Manager
                  {hadSecret && (
                    <span style={{ marginLeft: 8, color: '#888', fontWeight: 400 }}>
                      (current: <code style={{ color: '#aaa' }}>{initialEdit.secretName}</code>)
                    </span>
                  )}
                </button>
                {!showSecretsEditor && hadSecret && (
                  <p style={{ color: '#666', fontSize: 11, margin: '4px 0 0 4px' }}>
                    Existing secret will be reused on redeploy. Click to add/change.
                  </p>
                )}
              </div>

              {showSecretsEditor &&
                renderSecretPicker({ defaultName: initialEdit?.secretName || `lyric-${formData.repo?.toLowerCase().replace(/[^a-z0-9]/g, '-') || 'app'}`, requiredVars: enhanceEnvVars })}
            </>
          );
        })()}

        {/* Credentials section */}
        {showSecretsEditor && secretMode === 'new' && (
        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            onClick={() => setShowEnhanceEnvVars(v => !v)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <span style={{ color: '#ffb74d', fontSize: 13, fontWeight: 600 }}>
              {showEnhanceEnvVars ? '▾' : '▸'} Credentials & Environment Variables
            </span>
            {enhanceEnvVars.filter(e => e.value.trim()).length > 0 && (
              <span style={{ backgroundColor: '#1565c0', color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 10, fontWeight: 700 }}>
                {enhanceEnvVars.filter(e => e.value.trim()).length} set
              </span>
            )}
          </button>
          <p style={{ color: '#777', fontSize: 12, margin: '4px 0 0 18px' }}>
            Add API tokens, connection strings, or any env vars needed by the updated app.
          </p>

          {showEnhanceEnvVars && (
            <div style={{ marginTop: 10, backgroundColor: '#1e1e2e', border: '1px solid #333', borderRadius: 8, padding: 14 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <button
                  type="button"
                  onClick={loadEnvExample}
                  disabled={loadingEnvExample}
                  style={{ ...btnPrimary, backgroundColor: '#2e3a4e', color: '#90caf9', fontSize: 12, padding: '6px 12px' }}
                >
                  {loadingEnvExample ? '⏳ Scanning…' : '🔎 Detect vars from code'}
                </button>
                <button
                  type="button"
                  onClick={() => setEnhanceEnvVars(prev => [...prev, { key: '', value: '' }])}
                  style={{ ...btnPrimary, backgroundColor: '#2e3a4e', color: '#a5d6a7', fontSize: 12, padding: '6px 12px' }}
                >
                  + Add variable
                </button>
              </div>

              {enhanceEnvVars.length === 0 && (
                <p style={{ color: '#666', fontSize: 12, margin: 0 }}>No variables yet. Detect from code or add manually.</p>
              )}

              {enhanceEnvVars.map((ev, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="KEY"
                    value={ev.key}
                    onChange={(e) => setEnhanceEnvVars(prev => prev.map((v, j) => j === i ? { ...v, key: e.target.value } : v))}
                    style={{ ...inputStyle, margin: 0, flex: '0 0 42%', fontSize: 12, padding: '6px 8px', fontFamily: 'monospace' }}
                  />
                  <input
                    type={ev.key.toLowerCase().includes('secret') || ev.key.toLowerCase().includes('token') || ev.key.toLowerCase().includes('password') ? 'password' : 'text'}
                    placeholder="value"
                    value={ev.value}
                    onChange={(e) => setEnhanceEnvVars(prev => prev.map((v, j) => j === i ? { ...v, value: e.target.value } : v))}
                    style={{ ...inputStyle, margin: 0, flex: 1, fontSize: 12, padding: '6px 8px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setEnhanceEnvVars(prev => prev.filter((_, j) => j !== i))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef5350', fontSize: 16, padding: '0 4px', flexShrink: 0 }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Connected Services — same "Add Integration" catalog used when building
            from scratch. Lets an app owner wire in a new confluence/box/google-docs/
            slack/database/custom-api integration on an already-deployed app; the
            selection is sent to /api/deploy/enhance so the AI gets the same
            env-var/relay integration instructions as the scaffold flow. */}
        <CollapsibleSection
          title="🔌 Connected Services"
          summary={(formData.selectedServices || []).length > 0
            ? `${formData.selectedServices.length} added`
            : 'None added'}
        >
          <p style={{ color: '#aaa', fontSize: 12, margin: '0 0 14px' }}>
            Add a new external service for Lyric to wire into this app. Describe what to do with it
            above (e.g. "let users search Confluence pages") — Lyric includes the right env vars and
            integration instructions automatically.
          </p>
          {renderServiceCatalogPicker()}
        </CollapsibleSection>

        {/* Group Visibility — same control used in the initial deploy flow.
            Inlined here so editing an app lets the owner change Okta group
            access on redeploy without going through the full deploy wizard. */}
        <CollapsibleSection
          title="👥 Who can see this app?"
          summary={formData.visibilityMode === 'everyone'
            ? 'Everyone in my org'
            : formData.allowedGroups.length > 0
              ? `${formData.allowedGroups.length} group${formData.allowedGroups.length !== 1 ? 's' : ''}`
              : '⚠ Select a group'}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: '#e0e0e0', fontSize: 14 }}>
              <input
                type="radio"
                name="enhanceVisibilityMode"
                value="everyone"
                checked={formData.visibilityMode === 'everyone'}
                onChange={() => updateField('visibilityMode', 'everyone')}
                style={{ accentColor: '#ce93d8' }}
              />
              Everyone in my organization
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: '#e0e0e0', fontSize: 14 }}>
              <input
                type="radio"
                name="enhanceVisibilityMode"
                value="specific"
                checked={formData.visibilityMode === 'specific'}
                onChange={() => updateField('visibilityMode', 'specific')}
                style={{ accentColor: '#ce93d8' }}
              />
              Specific Groups
            </label>
          </div>

          {formData.visibilityMode === 'specific' && availableGroups.length > 0 && (
            <div style={{ marginTop: 12, paddingLeft: 4, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {availableGroups.map(group => {
                const checked = formData.allowedGroups.includes(group);
                return (
                  <label key={group} style={{
                    display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                    padding: '6px 12px', borderRadius: 20, fontSize: 13, fontWeight: 500,
                    backgroundColor: checked ? '#2a1a3e' : '#222',
                    border: `1px solid ${checked ? '#ce93d8' : '#444'}`,
                    color: checked ? '#ce93d8' : '#aaa', transition: 'all 0.15s',
                  }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = checked
                          ? formData.allowedGroups.filter(g => g !== group)
                          : [...formData.allowedGroups, group];
                        updateField('allowedGroups', next);
                      }}
                      style={{ display: 'none' }}
                    />
                    {checked ? '✓ ' : ''}{group}
                  </label>
                );
              })}
            </div>
          )}
          {formData.visibilityMode === 'specific' && formData.allowedGroups.length === 0 && (
            <p style={{ color: '#ef5350', fontSize: 11, margin: '8px 0 0' }}>Select at least one group.</p>
          )}
        </CollapsibleSection>

        {/* Owner-only pre-release gate */}
        <CollapsibleSection
          title="🚀 Team release & access"
          borderColor="#333"
          summary="Managed live in 👥 Users"
        >
          <div style={{ color: '#bbb', fontSize: 13, lineHeight: 1.6 }}>
            Visibility (<strong>🚀 Release to team</strong> vs <strong style={{ color: '#ffb74d' }}>🔒 Private</strong>) and
            <strong style={{ color: '#64b5f6' }}> allowed users</strong> are now managed <strong>live</strong> from the
            <strong> 👥 Users</strong> button on the app tile — changes apply <strong style={{ color: '#66bb6a' }}>instantly, with no redeploy</strong>.
            They were moved out of this Edit form so you don’t have to rebuild the app just to change who can see it.
          </div>
        </CollapsibleSection>

        {/* Okta token forwarding (opt-in) */}
        <CollapsibleSection
          title="🔑 Forward Okta sign-in"
          summary={formData.forwardOktaToken ? 'On' : 'Off'}
        >
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', color: '#e0e0e0', fontSize: 14 }}>
            <input
              type="checkbox"
              checked={!!formData.forwardOktaToken}
              onChange={(e) => updateField('forwardOktaToken', e.target.checked)}
              style={{ accentColor: '#ce93d8', marginTop: 3 }}
            />
            <span>
              🔑 Forward my Okta sign-in to this app
              <span style={{ display: 'block', color: '#888', fontSize: 12, marginTop: 4 }}>
                Injects the signed-in user's Okta token so the app's frontend can call its own
                backend (e.g. an API Gateway) as that user. Only enable for apps you trust. For
                calls, use <code style={{ color: '#ce93d8' }}>await window.__lyricGetToken()</code>{' '}
                (auto-refreshes so idle pages don't break) instead of caching the static global.
              </span>
            </span>
          </label>
        </CollapsibleSection>

        <CollapsibleSection
          title="👥 Co-admins (optional)"
          summary={formData.coAdmins.length > 0 ? `${formData.coAdmins.length} added` : 'None'}
        >
          <CoAdminsEditor bare emails={formData.coAdmins} onChange={(next) => updateField('coAdmins', next)} />
        </CollapsibleSection>

        {/* Every AI edit opens a Pull Request now — Lyric never commits straight
            to the source branch. This is enforced server-side; the card is just
            to set expectations. */}
        <CollapsibleSection
          title="🚀 How Lyric applies the changes"
          summary="Opens a PR"
        >
          <div style={{
            display: 'grid', gridTemplateColumns: '20px 1fr', columnGap: 12, alignItems: 'start',
            padding: '14px 14px', backgroundColor: '#221b32', border: '1px solid #ce93d8',
            borderRadius: 8, boxShadow: '0 0 0 1px #ce93d8 inset',
          }}>
            <div style={{ fontSize: 16, lineHeight: 1.2, marginTop: 1 }}>🔀</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: '#e8e8e8', fontSize: 14, fontWeight: 600, lineHeight: 1.35, marginBottom: 4 }}>
                Opens a Pull Request for review
              </div>
              <div style={{ color: '#9a9aa8', fontSize: 12, lineHeight: 1.5 }}>
                Lyric creates a new branch and opens a PR against <code style={{ color: '#ce93d8', backgroundColor: '#241a2e', padding: '1px 6px', borderRadius: 4, fontSize: 12 }}>{formData.branch || 'main'}</code>.
                Changes never land on <code style={{ color: '#ce93d8', backgroundColor: '#241a2e', padding: '1px 6px', borderRadius: 4, fontSize: 12 }}>{formData.branch || 'main'}</code> until you review and merge in GitHub.
                Deploy the PR branch directly from the next screen to preview it live first.
              </div>
            </div>
          </div>
        </CollapsibleSection>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <button
            onClick={() => handleEnhanceSubmit()}
            disabled={!enhanceDescription.trim() || (formData.visibilityMode === 'specific' && formData.allowedGroups.length === 0)}
            style={{ ...btnPrimary, backgroundColor: enhanceDescription.trim() && !(formData.visibilityMode === 'specific' && formData.allowedGroups.length === 0) ? '#ffb74d' : '#555', color: '#111', fontSize: 15, padding: '12px 32px' }}
          >
            {enhanceMode === 'pr' ? '✏️ Generate Changes & Open PR' : '✏️ Apply Changes'}
          </button>
          <button
            onClick={handleDirectRedeploy}
            disabled={formData.visibilityMode === 'specific' && formData.allowedGroups.length === 0}
            style={{ ...btnPrimary, backgroundColor: !(formData.visibilityMode === 'specific' && formData.allowedGroups.length === 0) ? '#1565c0' : '#555', color: '#fff', fontSize: 15, padding: '12px 24px' }}
          >
            🚀 Deploy Current Code
          </button>
          <button onClick={() => setStep('validation')} style={btnBack}>← Back</button>
        </div>
      </div>
    );
  };

  const renderEnhancingStep = () => {
    const repoUrl = `https://github.com/${formData.owner}/${formData.repo}`;
    const done = enhancePhase === 'done';
    const hasError = enhancePhase === 'error';

    // Clarifying-questions screen: Lyric asked follow-ups before touching the
    // repo. Collect answers (all optional), then resubmit or skip. The repo is
    // untouched until the user continues.
    if (enhancePhase === 'clarify' && enhanceClarify) {
      const { questions = [], assumptions = [], answers = {} } = enhanceClarify;
      const setAnswer = (id, value) =>
        setEnhanceClarify((prev) => (prev ? { ...prev, answers: { ...prev.answers, [id]: value } } : prev));
      return (
        <div>
          <div style={{
            backgroundColor: '#1a1f2e', border: '2px solid #64b5f6', borderRadius: 12,
            padding: '22px 20px', marginBottom: 18,
          }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 46, marginBottom: 8 }}>🤔</div>
              <h3 style={{ color: '#64b5f6', margin: '0 0 6px', fontSize: 22 }}>A couple quick questions</h3>
              <p style={{ color: '#ccc', fontSize: 14, margin: 0, lineHeight: 1.5 }}>
                Lyric wants to get this right before changing your code. Answer what you can —
                or skip and let Lyric decide. Nothing is committed yet.
              </p>
            </div>

            {questions.map((q, idx) => (
              <div key={q.id || idx} style={{
                backgroundColor: '#15151f', border: '1px solid #2a2a3a', borderRadius: 8,
                padding: 14, marginBottom: 12,
              }}>
                <div style={{ color: '#e8e8e8', fontSize: 15, fontWeight: 600, lineHeight: 1.4, marginBottom: q.why ? 4 : 8 }}>
                  {idx + 1}. {q.question}
                </div>
                {q.why && (
                  <div style={{ color: '#8a8a98', fontSize: 12, lineHeight: 1.4, marginBottom: 8, fontStyle: 'italic' }}>
                    Why it matters: {q.why}
                  </div>
                )}
                {Array.isArray(q.suggestions) && q.suggestions.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    {q.suggestions.map((s, si) => {
                      const active = (answers[q.id] || '') === s;
                      return (
                        <button
                          key={si}
                          type="button"
                          onClick={() => setAnswer(q.id, s)}
                          style={{
                            backgroundColor: active ? '#1565c0' : '#222838',
                            color: active ? '#fff' : '#b7c4d8',
                            border: `1px solid ${active ? '#64b5f6' : '#39465c'}`,
                            borderRadius: 14, padding: '4px 12px', fontSize: 12, cursor: 'pointer',
                          }}
                        >{s}</button>
                      );
                    })}
                  </div>
                )}
                <input
                  type="text"
                  value={answers[q.id] || ''}
                  onChange={(e) => setAnswer(q.id, e.target.value)}
                  placeholder="Your answer (optional)…"
                  style={{
                    width: '100%', boxSizing: 'border-box', backgroundColor: '#0f0f17',
                    color: '#e8e8e8', border: '1px solid #39465c', borderRadius: 6,
                    padding: '9px 11px', fontSize: 14,
                  }}
                />
              </div>
            ))}

            {assumptions.length > 0 && (
              <div style={{
                backgroundColor: '#1e1a12', border: '1px solid #c99a3a', borderRadius: 8,
                padding: '12px 14px', marginTop: 4,
              }}>
                <div style={{ color: '#e0b357', fontWeight: 700, fontSize: 12, marginBottom: 6 }}>
                  If you skip, Lyric will assume:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, color: '#d6c9a8', fontSize: 12, lineHeight: 1.6 }}>
                  {assumptions.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button onClick={handleClarifySubmit} style={{ ...btnPrimary, backgroundColor: '#1565c0', color: '#fff', fontSize: 15, padding: '12px 24px' }}>
              ✅ Continue with these answers
            </button>
            <button onClick={handleClarifySkip} style={{ ...btnBack, borderColor: '#c99a3a', color: '#e0b357' }}>
              ⏭️ Skip & let Lyric decide
            </button>
            <button
              onClick={() => { setEnhanceClarify(null); setEnhancePhase(null); setStep('enhance'); }}
              style={btnBack}
            >← Edit request</button>
          </div>
        </div>
      );
    }

    // Live progress for the (single, long) AI editing phase.
    const showLiveProgress = !done && !hasError;
    const enhFunMsg = enhanceStatusLine(buildElapsed);
    const enhEstSec = 45;
    const enhProgressPct = Math.min(92, Math.round(5 + (buildElapsed / enhEstSec) * 87));
    const enhMm = String(Math.floor(buildElapsed / 60));
    const enhSs = String(buildElapsed % 60).padStart(2, '0');
    const enhElapsedLabel = `${enhMm}:${enhSs}`;

    return (
      <div>
        <div style={{
          backgroundColor: done ? '#1a2e1a' : hasError ? '#2e1a1a' : '#1a1f2e',
          border: `2px solid ${done ? '#4caf50' : hasError ? '#ef5350' : '#ffb74d'}`,
          borderRadius: 12, padding: '24px 20px', textAlign: 'center', marginBottom: 20,
        }}>
          <div style={{ fontSize: 52, marginBottom: 10 }}>
            {done ? '✅' : hasError ? '❌' : <GearIcon size={52} color="#ffb74d" />}
          </div>
          <h3 style={{ color: done ? (enhanceResult?.status === 'no-changes' ? '#ffb74d' : '#4caf50') : hasError ? '#ef5350' : '#ffb74d', margin: '0 0 8px', fontSize: 22 }}>
            {done
              ? (enhanceResult?.status === 'no-changes' ? 'No Code Changes Made'
                : enhanceResult?.mode === 'pr' && enhanceResult?.prUrl ? 'Pull Request Opened!'
                : 'Changes Applied!')
              : hasError ? 'Enhancement Failed'
              : 'Applying Changes…'}
          </h3>
          <p style={{ color: '#ccc', fontSize: 14, margin: 0 }}>
            {done
              ? (enhanceResult?.status === 'no-changes'
                  ? 'Lyric did not produce any file changes — see Lyric\'s notes below.'
                : enhanceResult?.mode === 'pr' && enhanceResult?.prUrl
                  ? `${enhanceResult?.filesChanged} file${enhanceResult?.filesChanged !== 1 ? 's' : ''} pushed to ${enhanceResult?.prBranch || 'a new branch'} — review and merge in GitHub.`
                  : `${enhanceResult?.filesChanged} files updated in ${formData.owner}/${formData.repo}`)
              : hasError
              ? (error || 'Something went wrong.')
              : (enhanceMode === 'pr'
                  ? 'Lyric is reviewing your code and preparing a pull request — hang tight, this usually takes about a minute.'
                  : 'Lyric is reviewing your code and applying your changes — hang tight, this usually takes about a minute.')}
          </p>

          {/* Live progress: rotating status line, animated bar, elapsed timer */}
          {showLiveProgress && (
            <div style={{ marginTop: 16 }}>
              <div style={{ color: '#ffb74d', fontSize: 15, minHeight: 22, marginBottom: 10, fontWeight: 500 }}>
                {enhFunMsg}
              </div>
              <div style={{ height: 10, backgroundColor: '#2a2a3a', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(100, Math.max(3, enhProgressPct))}%`,
                  background: 'linear-gradient(90deg, #ffb74d, #7e57c2)',
                  borderRadius: 6,
                  transition: 'width 0.6s ease',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, color: '#888', fontSize: 12 }}>
                <span>⏱️ Elapsed {enhElapsedLabel}</span>
                <span>{enhProgressPct}% · usually under 2 min</span>
              </div>
            </div>
          )}
        </div>

        {done && enhanceResult?.mode === 'pr' && enhanceResult?.prUrl && (
          <div style={{ backgroundColor: '#1e1830', border: '1px solid #ce93d8', borderRadius: 8, padding: 16, marginBottom: 12 }}>
            <div style={{ color: '#ce93d8', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
              🔗 Pull Request #{enhanceResult.prNumber}
            </div>
            <a href={enhanceResult.prUrl} target="_blank" rel="noopener noreferrer"
              style={{ color: '#e1bee7', fontSize: 14, wordBreak: 'break-all', display: 'block', marginBottom: 8 }}>
              {enhanceResult.prUrl}
            </a>
            <p style={{ color: '#bbb', fontSize: 12, margin: 0, lineHeight: 1.5 }}>
              Review the diff in GitHub, then merge into <code style={{ color: '#ce93d8' }}>{formData.branch || 'main'}</code>.
              After merging, come back and redeploy this app to pick up the new code.
            </p>
          </div>
        )}

        {done && enhanceResult?.mode === 'pr' && !enhanceResult?.prUrl && enhanceResult?.prError && (
          <div style={{ backgroundColor: '#2e1a1a', border: '1px solid #ef5350', borderRadius: 8, padding: 14, marginBottom: 12 }}>
            <div style={{ color: '#ef9a9a', fontWeight: 700, fontSize: 13, marginBottom: 4 }}>⚠️ PR could not be opened</div>
            <p style={{ color: '#ffcdd2', fontSize: 12, margin: 0, lineHeight: 1.5 }}>
              Files were pushed to <code>{enhanceResult.prBranch}</code> but opening the PR failed: {enhanceResult.prError}
            </p>
          </div>
        )}

        {done && enhanceResult?.diagnosis && (
          <div style={{ backgroundColor: '#0d1b2e', border: '1px solid #4fc3f7', borderRadius: 8, padding: 16, marginBottom: 12 }}>
            <div style={{ color: '#4fc3f7', fontWeight: 700, fontSize: 13, marginBottom: 6 }}>🔍 What Lyric Found</div>
            <p style={{ color: '#cce7ff', fontSize: 13, margin: 0, lineHeight: 1.6 }}>{enhanceResult.diagnosis}</p>
          </div>
        )}

        {done && enhanceJiraStamp && (
          <div style={{
            backgroundColor: '#161022', border: '1px solid #6b4a80', borderRadius: 8,
            padding: '10px 14px', marginBottom: 12, color: '#d7c4e6', fontSize: 12,
          }}>
            {enhanceJiraStamp.status === 'posting' && <>💬 Stamping <strong>{enhanceJiraStamp.key}</strong> in Jira…</>}
            {enhanceJiraStamp.status === 'done' && <>💬 Commented on <strong>{enhanceJiraStamp.key}</strong> in Jira — noted that Lyric implemented this change.</>}
            {enhanceJiraStamp.status === 'error' && (
              <span style={{ color: '#ef9a9a' }}>
                ⚠️ Couldn't comment on {enhanceJiraStamp.key} in Jira{enhanceJiraStamp.error ? `: ${enhanceJiraStamp.error}` : ''}. The code change still succeeded.
              </span>
            )}
          </div>
        )}

        {done && enhanceResult?.validationWarnings?.length > 0 && (
          <div style={{ backgroundColor: '#2e2600', border: '1px solid #ffca28', borderRadius: 8, padding: 16, marginBottom: 12 }}>
            <div style={{ color: '#ffca28', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>⚠️ Heads up — Lyric spotted possible issues</div>
            <div style={{ color: '#fff3cd', fontSize: 12, marginBottom: 8, lineHeight: 1.5 }}>These didn't block the change, but review them before you rely on it:</div>
            {enhanceResult.validationWarnings.map((w, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
                <span style={{ color: '#ffca28', fontSize: 13, flexShrink: 0 }}>•</span>
                <span style={{ color: '#fff3cd', fontSize: 12, lineHeight: 1.5 }}>
                  <span style={{ fontFamily: 'monospace', color: '#ffe082' }}>{w.path}</span>: {w.warning}
                </span>
              </div>
            ))}
            <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setFixConfirmOpen(true)}
                style={{ backgroundColor: '#ffca28', color: '#3a2e00', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >🔧 Fix {enhanceResult.validationWarnings.length > 1 ? 'these issues' : 'this issue'}</button>
              <span style={{ color: '#bfae6a', fontSize: 11 }}>
                Lyric will make more changes to {enhanceResult?.prNumber ? `PR #${enhanceResult.prNumber}` : 'this pull request'} to correct {enhanceResult.validationWarnings.length > 1 ? 'them' : 'it'}.
              </span>
            </div>
          </div>
        )}

        {done && enhanceResult?.userActionRequired?.length > 0 && (
          <div style={{ backgroundColor: '#2e2000', border: '1px solid #ff9800', borderRadius: 8, padding: 16, marginBottom: 12 }}>
            <div style={{ color: '#ff9800', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>⚠️ Action Required — You Need To Do This</div>
            {enhanceResult.userActionRequired.map((action, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
                <span style={{ color: '#ff9800', fontSize: 13, flexShrink: 0 }}>→</span>
                <span style={{ color: '#ffe0b2', fontSize: 13, lineHeight: 1.5 }}>{action}</span>
              </div>
            ))}
          </div>
        )}

        {done && enhanceResult?.files?.length > 0 && (
          <div style={{ backgroundColor: '#1e1e2e', border: '1px solid #333', borderRadius: 8, padding: 16, marginBottom: 12, maxHeight: 220, overflowY: 'auto' }}>
            <div style={{ color: '#888', fontSize: 12, marginBottom: 8 }}>{enhanceResult.filesChanged} file{enhanceResult.filesChanged !== 1 ? 's' : ''} changed</div>
            {enhanceResult.files.map((f) => (
              <div key={f} style={{ marginBottom: enhanceResult.fileReasons?.[f] ? 8 : 2 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#ffb74d' }}>✓ {f}</div>
                {enhanceResult.fileReasons?.[f] && (
                  <div style={{ fontSize: 11, color: '#888', marginLeft: 14, marginTop: 2 }}>{enhanceResult.fileReasons[f]}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {done && enhanceResult?.nextSteps && (
          <div style={{ backgroundColor: '#1a2e1a', border: '1px solid #4caf50', borderRadius: 8, padding: 14, marginBottom: 12 }}>
            <div style={{ color: '#81c784', fontWeight: 700, fontSize: 13, marginBottom: 4 }}>✅ Next Steps</div>
            <p style={{ color: '#c8e6c9', fontSize: 13, margin: 0, lineHeight: 1.6 }}>{enhanceResult.nextSteps}</p>
          </div>
        )}

        {done && enhanceDeployPhase && (
          <div style={{
            backgroundColor: enhanceDeployPhase === 'done' ? '#1a2e1a' : enhanceDeployPhase === 'error' ? '#2e1a1a' : '#1a1f2e',
            border: `1px solid ${enhanceDeployPhase === 'done' ? '#4caf50' : enhanceDeployPhase === 'error' ? '#ef5350' : '#ffb74d'}`,
            borderRadius: 8, padding: '14px 16px', marginBottom: 16, textAlign: 'center',
          }}>
            {enhanceDeployPhase === 'done' ? (
              <>
                <div style={{ color: '#4caf50', fontWeight: 600, marginBottom: 6 }}>🟢 App is live!</div>
                <a href={enhanceDeployResult?.url} target="_blank" rel="noopener noreferrer"
                  style={{ color: '#81c784', fontSize: 13, wordBreak: 'break-all' }}>
                  {enhanceDeployResult?.url}
                </a>
              </>
            ) : enhanceDeployPhase === 'error' ? (
              <div style={{ color: '#ef5350', fontSize: 13 }}>{error || 'Redeploy failed — check server logs.'}</div>
            ) : (
              <div style={{ color: '#ffb74d', fontSize: 13 }}>
                {enhanceDeployPhase === 'starting' ? '⏳ Waiting for app to start…' : '🚀 Deploying updated app…'}
              </div>
            )}
          </div>
        )}

        {(done || hasError) && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            {done && (
              <a href={repoUrl} target="_blank" rel="noopener noreferrer"
                style={{ ...btnPrimary, backgroundColor: '#2e7d32', color: '#fff', textDecoration: 'none', fontSize: 15, padding: '12px 28px' }}>
                View on GitHub ↗
              </a>
            )}
            {done && enhanceResult?.mode !== 'pr' && enhanceDeployPhase !== 'deploying' && enhanceDeployPhase !== 'starting' && enhanceDeployPhase !== 'done' && (
              <button
                onClick={handleEnhanceRedeploy}
                style={{ ...btnPrimary, backgroundColor: '#1565c0', color: '#fff', fontSize: 15, padding: '12px 28px' }}>
                🚀 Redeploy App
              </button>
            )}
            {done && enhanceResult?.mode === 'pr' && enhanceResult?.prUrl && (
              <a href={enhanceResult.prUrl} target="_blank" rel="noopener noreferrer"
                style={{ ...btnPrimary, backgroundColor: '#7b1fa2', color: '#fff', textDecoration: 'none', fontSize: 15, padding: '12px 28px' }}>
                🔗 Open Pull Request ↗
              </a>
            )}
            {done && enhanceResult?.mode === 'pr' && enhanceResult?.prBranch && (
              <button
                onClick={handleDeployEnhancedBranch}
                title={`Deploy ${enhanceResult.prBranch} through the normal deploy flow`}
                style={{ ...btnPrimary, backgroundColor: '#00897b', color: '#fff', fontSize: 15, padding: '12px 28px' }}>
                🚀 Deploy This Branch
              </button>
            )}
            {done && enhanceDeployPhase === 'done' && enhanceDeployResult?.url && (
              <button
                type="button"
                onClick={() => setViewerApp({
                  appName: enhanceDeployResult?.appName || initialEdit?.appName,
                  displayName: initialEdit?.displayName || enhanceDeployResult?.appName || initialEdit?.appName,
                  url: enhanceDeployResult.url,
                  status: 'running',
                })}
                style={{ ...btnPrimary, backgroundColor: '#1565c0', color: '#fff', fontSize: 15, padding: '12px 28px', cursor: 'pointer' }}>
                🌐 Open App ↗
              </button>
            )}
            {done && (
              <button
                onClick={() => { setEnhanceDescription(''); setEnhanceResult(null); setEnhancePhase(null); setEnhanceDeployPhase(null); setEnhanceDeployResult(null); setStep('enhance'); }}
                style={{ ...btnPrimary, backgroundColor: '#ffb74d', color: '#111', fontSize: 15, padding: '12px 28px' }}>
                ✏️ Make More Changes
              </button>
            )}
            <button onClick={onClose} style={{ ...btnPrimary, backgroundColor: '#555', color: '#fff', fontSize: 15, padding: '12px 28px' }}>
              ✅ Done
            </button>
          </div>
        )}

        {fixConfirmOpen && (
          <div
            onClick={() => setFixConfirmOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ background: '#1e1830', border: '1px solid #7e57c2', borderRadius: 12, padding: 24, maxWidth: 460, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,.55)' }}
            >
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔧</div>
              <h3 style={{ color: '#e1bee7', margin: '0 0 8px', fontSize: 18 }}>
                Fix {enhanceResult?.validationWarnings?.length > 1 ? 'these issues' : 'this issue'}?
              </h3>
              <p style={{ color: '#cbbfe0', fontSize: 13, lineHeight: 1.6, margin: '0 0 10px' }}>
                Lyric will make additional changes to {enhanceResult?.prNumber ? <strong>PR #{enhanceResult.prNumber}</strong> : 'the current pull request'} to correct what it flagged, then bring you back to the Pull Request screen to finish up.
              </p>
              {enhanceResult?.validationWarnings?.length > 0 && (
                <div style={{ background: '#161122', border: '1px solid #3a3450', borderRadius: 8, padding: '10px 12px', marginBottom: 14, maxHeight: 140, overflowY: 'auto' }}>
                  {enhanceResult.validationWarnings.map((w, i) => (
                    <div key={i} style={{ color: '#fff3cd', fontSize: 12, lineHeight: 1.5, marginBottom: 4 }}>
                      <span style={{ fontFamily: 'monospace', color: '#ffe082' }}>{w.path}</span>: {w.warning}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setFixConfirmOpen(false)}
                  style={{ background: 'none', border: '1px solid #4a3f6e', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', color: '#b39ddb', fontSize: 14 }}
                >Cancel</button>
                <button
                  type="button"
                  onClick={handleFixIssues}
                  style={{ ...btnPrimary, background: 'linear-gradient(135deg,#ab47bc,#8e24aa)', color: '#fff', border: 'none', fontSize: 14, padding: '10px 20px', fontWeight: 700 }}
                >Yes, fix it</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const stepRenderers = {
    choose: renderChooseStep,
    repo: renderRepoStep,
    validation: renderValidationStep,
    pickApp: renderPickAppStep,
    stack: renderStackStep,
    requirements: renderRequirementsStep,
    config: renderConfigStep,
    review: renderReviewStep,
    deployed: renderDeployedStep,
    describe: renderDescribeStep,
    architecture: renderArchitectureStep,
    services: renderServicesStep,
    'build-review': renderBuildReviewStep,
    'github-token': renderGitHubTokenStep,
    building: renderBuildingStep,
    enhance: renderEnhanceStep,
    enhancing: renderEnhancingStep,
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <button onClick={onClose} style={closeBtnStyle}>✕</button>

        <h2 style={{ color: '#fff', marginBottom: 4, fontSize: 20 }}>🚀 Deploy / Create AI App</h2>
        <p style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>
          Deploy an existing repository or let Lyric build something new for you
        </p>

        {renderProgress()}

        {error && (
          <div style={{ backgroundColor: '#4e1a1a', border: '1px solid #ef5350', borderRadius: 8, padding: '10px 14px', color: '#ef9a9a', fontSize: 13, marginBottom: 16 }}>
            ❌ {error}
          </div>
        )}

        {stepRenderers[step]()}

        {showLoadDrafts && renderLoadDraftsModal()}
      </div>

      {renderVisualStudio()}

      {viewerApp && (
        <AppViewerOverlay app={viewerApp} onClose={() => setViewerApp(null)} />
      )}
    </div>
  );
}

// --- Helper components ---

function ExpandableWarning({
  label, what, action,
  accentColor = '#ffab91',
  bgColor = '#3e2723',
  expandBgColor = '#2a1a0e',
  borderColor = '#ff9800',
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ backgroundColor: bgColor, border: `1px solid ${borderColor}`, borderRadius: 8, marginBottom: 6, overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(v => !v)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', cursor: 'pointer' }}
      >
        <span style={{ color: accentColor, fontSize: 13 }}>{label}</span>
        <span style={{ color: accentColor, fontSize: 12, whiteSpace: 'nowrap', marginLeft: 12 }}>{open ? '▲ Hide' : '▼ Learn more'}</span>
      </div>
      {open && (
        <div style={{ borderTop: `1px solid ${borderColor}40`, padding: '10px 14px', backgroundColor: expandBgColor }}>
          <p style={{ color: '#ccc', fontSize: 13, margin: '0 0 8px' }}>{what}</p>
          <p style={{ color: '#81c784', fontSize: 13, margin: 0 }}>✅ <strong>What to do:</strong> {action}</p>
        </div>
      )}
    </div>
  );
}

function AppScopeInfo({ isRepoRoot }) {
  const [open, setOpen] = useState(false);
  const title = isRepoRoot ? 'Repository root' : 'App subdirectory';
  const explanation = isRepoRoot
    ? 'This app is located at the top level of the repository. Lyric will install dependencies and run the app from the repository root.'
    : 'This app is inside a folder within the repository. Lyric will use the full repository, but install dependencies and run the app from this folder.';

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        aria-label={`Explain ${title}`}
        title={`Explain ${title}`}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen(value => !value);
        }}
        style={{
          width: 18,
          height: 18,
          padding: 0,
          borderRadius: '50%',
          border: '1px solid #888',
          background: 'transparent',
          color: '#bbb',
          fontSize: 12,
          fontWeight: 700,
          lineHeight: '16px',
          cursor: 'pointer',
        }}
      >
        i
      </button>
      {open && (
        <span
          role="tooltip"
          style={{
            position: 'absolute',
            zIndex: 2,
            top: 24,
            left: 0,
            width: 280,
            padding: '10px 12px',
            border: '1px solid #555',
            borderRadius: 6,
            background: '#202020',
            color: '#ddd',
            fontSize: 12,
            lineHeight: 1.45,
            fontFamily: 'inherit',
            fontWeight: 400,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
          }}
        >
          <strong style={{ color: '#fff' }}>{title}</strong>
          <br />
          {explanation}
        </span>
      )}
    </span>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div style={{ color: '#888', fontSize: 11, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

function FormField({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ ...inputStyle, marginBottom: 0 }}
      />
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #333' }}>
      <span style={{ color: '#888' }}>{label}</span>
      <span style={{ color: '#fff', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

// --- Shared styles ---

const overlayStyle = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.85)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 9999,
};

const modalStyle = {
  backgroundColor: '#2a2a2a',
  borderRadius: 16,
  padding: 32,
  width: '90%',
  maxWidth: 640,
  maxHeight: '85vh',
  overflowY: 'auto',
  position: 'relative',
  boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
};

const closeBtnStyle = {
  position: 'absolute',
  top: 12, right: 16,
  background: 'none',
  border: 'none',
  color: '#888',
  fontSize: 22,
  cursor: 'pointer',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #555',
  backgroundColor: '#1e1e1e',
  color: '#fff',
  fontSize: 14,
  marginBottom: 12,
  boxSizing: 'border-box',
};

const labelStyle = {
  color: '#ccc',
  fontSize: 13,
  display: 'block',
  marginBottom: 4,
};

const cardStyle = {
  backgroundColor: '#1e1e1e',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
  border: '1px solid #333',
};

const btnPrimary = {
  padding: '10px 24px',
  borderRadius: 8,
  border: 'none',
  color: '#000',
  fontWeight: 700,
  cursor: 'pointer',
  fontSize: 14,
};

const btnBack = {
  padding: '10px 16px',
  borderRadius: 8,
  border: '1px solid #555',
  backgroundColor: 'transparent',
  color: '#aaa',
  cursor: 'pointer',
  fontSize: 14,
};

const pathCardStyle = {
  padding: 24,
  borderRadius: 12,
  border: '2px solid #444',
  backgroundColor: '#1e1e1e',
  cursor: 'pointer',
  textAlign: 'center',
  transition: 'all 0.2s',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

export default DeployIntakeForm;
